import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  submitTeacherApplication, 
  saveTeacherApplicationDraft, 
  getTeacherApplicationDraft,
  getCourses,
  getPublicCourses
} from '../../services/adminService';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import CountryPhoneInput from '../../components/CountryPhoneInput';
import { countries } from '../../utils/countries';

import {
  User,
  Briefcase,
  GraduationCap,
  BookOpen,
  Calendar,
  Clock,
  Link,
  Users,
  ShieldCheck,
  ClipboardCheck,
  Lock,
  ChevronDown,
  ChevronUp,
  Plus,
  Check,
  Trash2,
  Edit2,
  Info,
  CheckCircle,
  AlertTriangle,
  Mail,
  Phone,
  MapPin,
  Clock3,
  BriefcaseBusiness,
  Timer,
  Link2,
  PlayCircle,
  Globe2,
  CalendarDays,
  BadgeCheck,
  UserRound,
  BookOpenCheck,
  Award,
  Search,
  X,
  RotateCcw,
  Sparkles,
  MessageSquare
} from 'lucide-react';

// React Error Boundary for graceful fallback
class TeacherApplyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("TeacherApply render error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '24px', margin: '24px 0', background: '#FEF2F2', border: '1.5px solid #FCA5A5', borderRadius: '12px', color: '#991B1B' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 700 }}>This section could not be displayed.</h4>
          <p style={{ margin: '0 0 12px 0', fontSize: '13.5px', lineHeight: 1.5 }}>An unexpected rendering error occurred. Please refresh the page or try again.</p>
          <button
            type="button"
            className="apply-btn apply-btn-secondary"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const TeacherApply = () => {
  const navigate = useNavigate();
  const formScrollRef = useRef(null);
  const isScrollingRef = useRef(false);

  // 12 sections definitions with unique, stable string IDs
  const sections = [
    { id: 'basic-information', num: 1, label: 'Basic Information', icon: User },
    { id: 'professional-profile', num: 2, label: 'Professional Profile', icon: Briefcase },
    { id: 'qualifications-certificates', num: 3, label: 'Qualifications & Certificates', icon: GraduationCap },
    { id: 'teaching-expertise', num: 4, label: 'Teaching Subjects & Specializations', icon: BookOpen },
    { id: 'courses-programs', num: 5, label: 'Academy Programs You Are Qualified to Teach', icon: BookOpenCheck },
    { id: 'experience-background', num: 6, label: 'Experience & Background', icon: Calendar },
    { id: 'availability', num: 7, label: 'Availability', icon: Clock },
    { id: 'videos-links', num: 8, label: 'Videos & Links', icon: Link },
    { id: 'school-affiliation', num: 9, label: 'Yoga School Affiliation', icon: Award },
    { id: 'references', num: 10, label: 'Reference / How Did You Hear About Us?', icon: MessageSquare },
    { id: 'motivation-strengths', num: 11, label: 'About the Teacher', icon: Sparkles },
    { id: 'verification', num: 12, label: 'Verification', icon: ShieldCheck }
  ];

  // Helper mappings between stable IDs and numeric positions
  const getSectionId = (target) => {
    if (typeof target === 'string') return target;
    const found = sections.find(s => s.num === target);
    return found ? found.id : 'basic-information';
  };

  const getSectionNum = (target) => {
    if (typeof target === 'number') return target;
    const found = sections.find(s => s.id === target);
    return found ? found.num : 1;
  };

  // Active section & accordion state (Section 1 and 2 open by default, others closed)
  const [activeSection, setActiveSection] = useState('basic-information');
  const [expanded, setExpanded] = useState({
    'basic-information': true,
    'professional-profile': true,
    'qualifications-certificates': false,
    'teaching-expertise': false,
    'experience-background': false,
    'courses-programs': false,
    'availability': false,
    'videos-links': false,
    'school-affiliation': false,
    'references': false,
    'motivation-strengths': false,
    'verification': false
  });

  const toggleSection = (idTarget) => {
    const sId = getSectionId(idTarget);
    setExpanded(prev => ({
      ...prev,
      [sId]: !prev[sId]
    }));
  };

  // Country code selector states
  const [mobileCountryCode, setMobileCountryCode] = useState('+91');
  const [ref1CountryCode, setRef1CountryCode] = useState('+91');
  const [ref2CountryCode, setRef2CountryCode] = useState('+91');

  // Input text storage states
  const [formData, setFormData] = useState({
    // 1. Basic Information
    full_name: '',
    email: '',
    mobile: '',
    whatsapp: '',
    country: '',
    state: '',
    city: '',
    current_location: '',
    languages: '',
    time_zone: '',
    designation: '',

    // 2. Professional Profile
    yearsOfTeachingExperience: '',
    total_experience_years: '',
    total_teaching_hours: '',
    one_to_one_teaching_hours: '',
    students_trained: '',
    teaching_style: '',
    about: '',

    // 3. Qualifications & Certificates
    certifications: [],
    certs_confirmed: false,

    // 4. Teaching Expertise
    expertise: [],
    other_expertise: '',
    courses_programs_teach: '',
    types_students_taught: '',
    custom_specialization: '',
    subjects_supported_by_qualifications: '',

    // 5. Experience & Background
    experience_timeline: [],
    class_duration: '60 minutes',
    max_online_group_size: '',
    max_in_person_group_size: '',
    comfortable_one_to_one: 'Yes',
    comfortable_assessments: 'Yes',
    comfortable_assignments: 'Yes',
    comfortable_feedback: 'Yes',
    comfortable_recording: 'Yes',

    // 6. Availability
    weekly_schedule: {
      Monday: { available: false, slots: [] },
      Tuesday: { available: false, slots: [] },
      Wednesday: { available: false, slots: [] },
      Thursday: { available: false, slots: [] },
      Friday: { available: false, slots: [] },
      Saturday: { available: false, slots: [] },
      Sunday: { available: false, slots: [] }
    },
    demo_available: 'Yes',
    min_weekly_commitment: '',
    avail_morning: 'Yes',
    avail_evening: 'Yes',
    avail_weekend: 'No',
    avail_international: 'Yes',
    earliest_joining_date: '',

    // 7. Videos & Links
    intro_video_link: '',
    demo_video_link: '',
    class_sample_link: '',
    resume_drive_link: '',
    setup_photo_link: '',
    website: '',
    instagram: '',
    facebook: '',
    linkedin: '',
    youtube_channel: '',
    yoga_alliance_link: '',

    // 8. References / How Did You Hear About Us
    references: [],
    hear_about_us: '',
    hear_about_us_other: '',
    ref_name: '',
    ref_org: '',
    ref_designation: '',
    ref_email: '',
    ref_mobile: '',

    // 11. About the Teacher
    teaching_style_approach: '',
    professional_summary: '',
    why_join_pranaflow: '',
    what_makes_me_exceptional: '',
    // Legacy aliases preserved for backward compat
    motivation_statement: '',
    teaching_strengths: '',

    // 10. Verification
    government_id_drive_link: '',
    cert_suspended: 'No',
    cert_suspended_explanation: '',
    professional_complaint: 'No',
    professional_complaint_explanation: '',
    restrictions_affecting_ability: 'No',
    restrictions_explanation: '',
    decl_boundaries: false,
    decl_privacy: false,
    decl_recording: false,
    decl_medical: false,
    decl_policies: false,
    decl_accuracy: false,

    // International Teaching Experience (Section 5 conditional)
    intl_has_experience: '',
    intl_taught_outside_home: '',
    intl_taught_online_intl: '',
    intl_teaching_mode: '',
    intl_student_count: '',
    intl_teaching_hours: '',
    intl_description: '',
    intl_evidence_url: '',
    intl_countries_taught: [],
    intl_student_countries: [],
    intl_courses: [],
    intl_organisations: [],

    // Domestic Teaching Experience – India (Section 5 conditional)
    domestic_has_experience: '',
    domestic_teaching_mode: '',
    domestic_courses_taught: [],
    domestic_taught_online: '',
    domestic_online_student_count: '',
    domestic_taught_in_person: '',
    domestic_in_person_student_count: '',
    domestic_total_student_count: '',
    domestic_teaching_hours: '',
    domestic_organisations: [],
    domestic_description: '',

    // Courses & Programs You Can Teach (Section 6)
    selected_course_ids: [],
    custom_courses: [],
    teaching_formats: [],
    previously_taught_choice: '',
    previous_course_experiences: [
      {
        localId: 'init-1',
        isEditing: true,
        courseName: '',
        teachingLocation: '',
        experienceYears: '',
        experienceMonths: '',
        studentsTaught: ''
      }
    ],
    previously_taught_course_ids: [],
    prev_batches_taught: '',
    prev_students_taught: '',
    prev_recent_year: '',
    prev_teaching_desc: '',
    teaching_journey: [],

    // Yoga School Affiliation (Section 9)
    owns_or_represents_school: 'No',
    partner_school_name: '',
    yoga_alliance_school_link: '',
    yoga_alliance_school_id: '',
    school_website_link: '',
    school_address: '',
    can_issue_certificates: '',
    certificate_types_can_issue: '',
    authorized_person_name: '',
    authorized_person_contact: ''
  });

  // International & Domestic Teaching Experience multi-select states
  const intlCountriesRef = useRef(null);
  const intlStudentCountriesRef = useRef(null);
  const intlCoursesRef = useRef(null);
  const domesticCoursesRef = useRef(null);
  const [intlCountriesSearch, setIntlCountriesSearch] = useState('');
  const [showIntlCountriesDropdown, setShowIntlCountriesDropdown] = useState(false);
  const [intlStudentCountriesSearch, setIntlStudentCountriesSearch] = useState('');
  const [showIntlStudentCountriesDropdown, setShowIntlStudentCountriesDropdown] = useState(false);
  const [intlCoursesSearch, setIntlCoursesSearch] = useState('');
  const [showIntlCoursesDropdown, setShowIntlCoursesDropdown] = useState(false);
  const [intlOrgInput, setIntlOrgInput] = useState('');

  const [domesticCoursesSearch, setDomesticCoursesSearch] = useState('');
  const [showDomesticCoursesDropdown, setShowDomesticCoursesDropdown] = useState(false);
  const [domesticOrgInput, setDomesticOrgInput] = useState('');

  const intlCourseOptions = [
    '200 Hour Yoga Teacher Training', '300 Hour Yoga Teacher Training',
    '500 Hour Yoga Teacher Training', 'Hatha Yoga', 'Ashtanga Yoga',
    'Vinyasa Flow', 'Pranayama', 'Meditation', 'Yoga Philosophy',
    'Yoga Anatomy', 'Yin Yoga', 'Therapeutic Yoga', 'Kids Yoga',
    'Prenatal Yoga', 'Restorative Yoga', 'Workshops', 'Retreats'
  ];

  const domesticCourseOptions = [
    '200 Hour Yoga Teacher Training', '300 Hour Yoga Teacher Training',
    '500 Hour Yoga Teacher Training', 'Hatha Yoga', 'Ashtanga Yoga',
    'Vinyasa Flow', 'Pranayama & Meditation', 'Yoga Therapy',
    'Prenatal & Postnatal Yoga', 'Children / Kids Yoga', 'Shatkarma & Kriya',
    'Yoga Philosophy', 'Anatomy & Physiology', 'Ayurveda & Lifestyle'
  ];

  // Admin Courses Checklist states (Section 6)
  const [adminCourses, setAdminCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState('');
  const [courseSearchQuery, setCourseSearchQuery] = useState('');

  // Custom Course Builder states (Section 6)
  const [customName, setCustomName] = useState('');
  const [customCategory, setCustomCategory] = useState('Yoga Specialization');
  const [customCategoryOther, setCustomCategoryOther] = useState('');
  const [customCategoryOtherError, setCustomCategoryOtherError] = useState('');
  const [customLevel, setCustomLevel] = useState('All Levels');
  const [customDuration, setCustomDuration] = useState('');
  const [customSubjects, setCustomSubjects] = useState([]);
  const [customSubjectInput, setCustomSubjectInput] = useState('');
  const [customQualification, setCustomQualification] = useState('');
  const [customDetails, setCustomDetails] = useState('');
  const [editingCustomIdx, setEditingCustomIdx] = useState(null);

  // Repeatable Inline Builders States
  // Section 3: Certifications Builder
  const [certName, setCertName] = useState('');
  const [certIssuer, setCertIssuer] = useState('');
  const [certYear, setCertYear] = useState('');
  const [certId, setCertId] = useState('');
  const [certSchoolLink, setCertSchoolLink] = useState('');
  const [certDriveLink, setCertDriveLink] = useState('');
  const [editingCertIdx, setEditingCertIdx] = useState(null);

  // Section 5: Experience Roles Builder
  const [roleYears, setRoleYears] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [roleOrg, setRoleOrg] = useState('');
  const [roleLoc, setRoleLoc] = useState('');
  const [roleMode, setRoleMode] = useState('Online');
  const [roleDesc, setRoleDesc] = useState('');
  const [roleSubjects, setRoleSubjects] = useState('');
  const [editingRoleIdx, setEditingRoleIdx] = useState(null);

  // Section 8: References Builder
  const [refName, setRefName] = useState('');
  const [refOrg, setRefOrg] = useState('');
  const [refDesig, setRefDesig] = useState('');
  const [refRel, setRefRel] = useState('');
  const [refEmail, setRefEmail] = useState('');
  const [refMobile, setRefMobile] = useState('');
  const [refYears, setRefYears] = useState('');
  const [refContactPermitted, setRefContactPermitted] = useState('Yes');
  const [editingRefIdx, setEditingRefIdx] = useState(null);

  // Status States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [completedSections, setCompletedSections] = useState({});

  // Scroll reset on mount
  useEffect(() => {
    window.scrollTo({ top: 0 });
    if (formScrollRef.current) {
      formScrollRef.current.scrollTop = 0;
    }
    setActiveSection(1);
  }, []);

  // Load Admin Courses on mount
  const loadAdminCourses = async () => {
    setCoursesLoading(true);
    setCoursesError('');
    try {
      const res = await getPublicCourses();
      let list = [];
      if (Array.isArray(res)) {
        list = res;
      } else if (res && Array.isArray(res.courses)) {
        list = res.courses;
      } else if (res && Array.isArray(res.data)) {
        list = res.data;
      }

      const normalized = list.map(c => ({
        id: String(c.id ?? c.course_id),
        title: c.name ?? c.title ?? c.course_name ?? c.course_title ?? 'Untitled Course',
        name: c.name ?? c.title ?? c.course_name ?? c.course_title ?? 'Untitled Course',
        code: c.code ?? c.course_code ?? '',
        duration: c.duration ?? c.total_hours ?? '',
        status: c.status || 'active'
      }));

      setAdminCourses(normalized.filter(c => (c.status || 'active') === 'active'));
    } catch (err) {
      console.error("Failed to fetch public courses:", err);
      setCoursesError("Unable to load courses. Please retry.");
    } finally {
      setCoursesLoading(false);
    }
  };

  useEffect(() => {
    loadAdminCourses();
  }, []);

  // Course Selector Handlers
  const toggleCourseSelection = (courseId) => {
    setFormData(prev => {
      const current = [...prev.selected_course_ids];
      const targetId = String(courseId);
      const exists = current.some(id => String(id) === targetId);
      let updated;
      if (exists) {
        updated = current.filter(id => String(id) !== targetId);
      } else {
        updated = [...current, courseId];
      }
      return { ...prev, selected_course_ids: updated };
    });
    if (formErrors.selected_course_ids) {
      setFormErrors(prev => ({ ...prev, selected_course_ids: '' }));
    }
  };

  const clearAllSelectedCourses = () => {
    setFormData(prev => ({ ...prev, selected_course_ids: [], custom_courses: [] }));
  };

  // Custom Course Builder Handlers
  const handleAddCustomCourse = () => {
    if (!customName.trim()) {
      alert("Course or Program Name is required.");
      return;
    }

    let cleanManualCategory = '';
    let formattedCategory = customCategory;

    if (customCategory === 'Other') {
      cleanManualCategory = customCategoryOther.trim().replace(/-\(Other\)$/i, '').trim();
      if (!cleanManualCategory || cleanManualCategory.toLowerCase() === 'enter the course category manually') {
        setCustomCategoryOtherError("Please enter the course category.");
        return;
      }
      formattedCategory = `${cleanManualCategory}-(Other)`;
    }
    setCustomCategoryOtherError('');
    const newCourse = {
      course_name: customName.trim(),
      courseName: customName.trim(),
      course_category: formattedCategory,
      courseCategory: formattedCategory,
      custom_category: cleanManualCategory,
      manualCategory: cleanManualCategory,
      course_level: customLevel || 'All Levels',
      level: customLevel || 'All Levels',
      course_duration: customDuration.trim(),
      duration: customDuration.trim(),
      subjects_covered: customSubjects,
      mainSubjectsCovered: customSubjects,
      supporting_qualification: customQualification.trim(),
      supportingQualification: customQualification.trim(),
      course_details: customDetails.trim(),
      briefCourseDetails: customDetails.trim()
    };

    setFormData(prev => {
      const updated = [...prev.custom_courses];
      if (editingCustomIdx !== null) {
        updated[editingCustomIdx] = newCourse;
      } else {
        updated.push(newCourse);
      }
      return { ...prev, custom_courses: updated };
    });

    setCustomName('');
    setCustomCategory('Yoga Specialization');
    setCustomCategoryOther('');
    setCustomCategoryOtherError('');
    setCustomLevel('All Levels');
    setCustomDuration('');
    setCustomSubjects([]);
    setCustomSubjectInput('');
    setCustomQualification('');
    setCustomDetails('');
    setEditingCustomIdx(null);
  };

  // Helper to compute selectable course options for previous teaching experience cards
  const getSelectableCoursesOptions = () => {
    const options = [];
    if (Array.isArray(formData.selected_course_ids)) {
      formData.selected_course_ids.forEach(id => {
        if (id !== 'OTHER') {
          const match = adminCourses.find(c => String(c.id) === String(id));
          const title = match ? (match.title || match.name) : `Course #${id}`;
          if (title && !options.includes(title)) options.push(title);
        }
      });
    }
    if (Array.isArray(formData.custom_courses)) {
      formData.custom_courses.forEach(c => {
        const cName = c.course_name || c.courseName;
        if (cName && !options.includes(cName)) options.push(cName);
      });
    }
    return options;
  };

  const formatDuration = (yrsVal, mthsVal) => {
    const yrsNum = yrsVal !== '' && yrsVal !== null && yrsVal !== undefined ? Number(yrsVal) : 0;
    const mthsNum = mthsVal !== '' && mthsVal !== null && mthsVal !== undefined ? Number(mthsVal) : 0;

    let yrsStr = yrsNum > 0 ? `${yrsNum} ${yrsNum === 1 ? 'year' : 'years'}` : '';
    let mthsStr = mthsNum > 0 ? `${mthsNum} ${mthsNum === 1 ? 'month' : 'months'}` : '';

    return [yrsStr, mthsStr].filter(Boolean).join(' ') || '0 months';
  };

  const validateCourseExperienceEntry = (exp, idx) => {
    const errs = {};
    if (!exp) return { isValid: false, errors: {} };

    if (!exp.courseName || !exp.courseName.trim()) {
      errs[`prev_exp_${idx}_courseName`] = "Please select a previously taught program.";
    }
    if (!exp.teachingLocation || !exp.teachingLocation.trim()) {
      errs[`prev_exp_${idx}_teachingLocation`] = "Please enter where you taught this program.";
    }
    const yrsStr = String(exp.experienceYears || '').trim();
    const mthsStr = String(exp.experienceMonths || '').trim();
    const yrs = yrsStr !== '' ? Number(yrsStr) : 0;
    const mths = mthsStr !== '' ? Number(mthsStr) : 0;

    if (yrsStr !== '' && (isNaN(yrs) || yrs < 0)) errs[`prev_exp_${idx}_experienceYears`] = "Years cannot be negative.";
    if (mthsStr !== '' && (isNaN(mths) || mths < 0 || mths > 11)) errs[`prev_exp_${idx}_experienceMonths`] = "Months must be between 0 and 11.";
    if (yrs === 0 && mths === 0) errs[`prev_exp_${idx}_duration`] = "Please enter at least 1 month or 1 year of teaching experience.";

    if (exp.studentsTaught === undefined || exp.studentsTaught === null || String(exp.studentsTaught).trim() === '') {
      errs[`prev_exp_${idx}_studentsTaught`] = "Please enter approximate number of students taught.";
    } else if (isNaN(Number(exp.studentsTaught)) || Number(exp.studentsTaught) < 0) {
      errs[`prev_exp_${idx}_studentsTaught`] = "Student count cannot be negative.";
    }

    return { isValid: Object.keys(errs).length === 0, errors: errs };
  };

  const validateTeachingJourneyEntry = (jEntry, idx) => {
    const errs = {};
    if (!jEntry) return { isValid: false, errors: {} };

    if (!jEntry.role_title || !jEntry.role_title.trim()) {
      errs[`journey_${idx}_role_title`] = "Please enter role or position title.";
    }
    if (!jEntry.organisation || !jEntry.organisation.trim()) {
      errs[`journey_${idx}_organisation`] = "Please enter organisation or studio name.";
    }
    if (!jEntry.country || !jEntry.country.trim()) {
      errs[`journey_${idx}_country`] = "Please select country.";
    }
    if (!jEntry.start_month) {
      errs[`journey_${idx}_start_month`] = "Please select start month.";
    }
    if (!jEntry.start_year) {
      errs[`journey_${idx}_start_year`] = "Please select start year.";
    }
    if (!jEntry.is_current) {
      if (!jEntry.end_month) errs[`journey_${idx}_end_month`] = "Please select end month.";
      if (!jEntry.end_year) errs[`journey_${idx}_end_year`] = "Please select end year.";
    }
    if (!jEntry.description || !jEntry.description.trim()) {
      errs[`journey_${idx}_description`] = "Please enter role description.";
    }

    return { isValid: Object.keys(errs).length === 0, errors: errs };
  };

  const handleAddCourseExperience = () => {
    const list = Array.isArray(formData.previous_course_experiences) ? formData.previous_course_experiences : [];
    const activeIdx = list.findIndex(e => e.isEditing === true);

    if (activeIdx !== -1) {
      const activeItem = list[activeIdx];
      const { isValid, errors } = validateCourseExperienceEntry(activeItem, activeIdx);
      if (!isValid) {
        setFormErrors(prev => ({ ...prev, ...errors }));
        return;
      }
    }

    setFormData(prev => {
      const currentList = Array.isArray(prev.previous_course_experiences) ? prev.previous_course_experiences : [];
      const updatedList = currentList.map(e => ({ ...e, isEditing: false }));
      return {
        ...prev,
        previous_course_experiences: [
          ...updatedList,
          {
            localId: `exp-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
            isEditing: true,
            courseName: '',
            teachingLocation: '',
            experienceYears: '',
            experienceMonths: '',
            studentsTaught: ''
          }
        ]
      };
    });
  };

  const handleEditCourseExperience = (index) => {
    setFormData(prev => {
      const list = Array.isArray(prev.previous_course_experiences) ? [...prev.previous_course_experiences] : [];
      const updatedList = list.map((e, idx) => ({
        ...e,
        isEditing: idx === index
      }));
      return { ...prev, previous_course_experiences: updatedList };
    });
  };

  const handleSaveCourseExperience = (index) => {
    const list = Array.isArray(formData.previous_course_experiences) ? formData.previous_course_experiences : [];
    const item = list[index];
    if (!item) return;

    const { isValid, errors } = validateCourseExperienceEntry(item, index);
    if (!isValid) {
      setFormErrors(prev => ({ ...prev, ...errors }));
      return;
    }

    setFormErrors(prev => {
      const nextErrs = { ...prev };
      Object.keys(nextErrs).forEach(k => {
        if (k.startsWith(`prev_exp_${index}_`)) delete nextErrs[k];
      });
      return nextErrs;
    });

    setFormData(prev => {
      const currentList = Array.isArray(prev.previous_course_experiences) ? [...prev.previous_course_experiences] : [];
      if (currentList[index]) {
        currentList[index] = { ...currentList[index], isEditing: false };
      }
      return { ...prev, previous_course_experiences: currentList };
    });
  };

  const handleRemoveCourseExperience = (index) => {
    const list = Array.isArray(formData.previous_course_experiences) ? formData.previous_course_experiences : [];
    const target = list[index];
    const hasData = target && (target.courseName || target.teachingLocation || target.experienceYears || target.experienceMonths || target.studentsTaught);
    if (hasData) {
      if (!window.confirm('Are you sure you want to remove this course teaching experience record?')) {
        return;
      }
    }
    setFormData(prev => {
      const newList = (Array.isArray(prev.previous_course_experiences) ? prev.previous_course_experiences : []).filter((_, idx) => idx !== index);
      if (newList.length === 0 && prev.previously_taught_choice === 'Yes') {
        newList.push({
          localId: `exp-${Date.now()}`,
          isEditing: true,
          courseName: '',
          teachingLocation: '',
          experienceYears: '',
          experienceMonths: '',
          studentsTaught: ''
        });
      }
      return { ...prev, previous_course_experiences: newList };
    });
  };

  const handleCourseExperienceChange = (index, field, value) => {
    setFormData(prev => {
      const list = Array.isArray(prev.previous_course_experiences) ? [...prev.previous_course_experiences] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], [field]: value };
      return { ...prev, previous_course_experiences: list };
    });
    if (formErrors[`prev_exp_${index}_${field}`]) {
      setFormErrors(p => ({ ...p, [`prev_exp_${index}_${field}`]: '' }));
    }
    if (formErrors[`prev_exp_${index}_duration`]) {
      setFormErrors(p => ({ ...p, [`prev_exp_${index}_duration`]: '' }));
    }
  };

  const monthsList = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const yearsList = Array.from({ length: 45 }, (_, i) => String(new Date().getFullYear() - i));

  const handleAddTeachingJourney = () => {
    const list = Array.isArray(formData.teaching_journey) ? formData.teaching_journey : [];
    const activeIdx = list.findIndex(e => e.isEditing === true);

    if (activeIdx !== -1) {
      const activeItem = list[activeIdx];
      const { isValid, errors } = validateTeachingJourneyEntry(activeItem, activeIdx);
      if (!isValid) {
        setFormErrors(prev => ({ ...prev, ...errors }));
        return;
      }
    }

    setFormData(prev => {
      const currentList = Array.isArray(prev.teaching_journey) ? prev.teaching_journey : [];
      const updatedList = currentList.map(e => ({ ...e, isEditing: false }));
      return {
        ...prev,
        teaching_journey: [
          ...updatedList,
          {
            localId: `journey-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
            isEditing: true,
            role_title: '',
            organisation: '',
            city: '',
            state: '',
            country: 'India',
            start_month: '',
            start_year: '',
            end_month: '',
            end_year: '',
            is_current: false,
            description: ''
          }
        ]
      };
    });
  };

  const handleEditTeachingJourney = (index) => {
    setFormData(prev => {
      const list = Array.isArray(prev.teaching_journey) ? [...prev.teaching_journey] : [];
      const updatedList = list.map((e, idx) => ({
        ...e,
        isEditing: idx === index
      }));
      return { ...prev, teaching_journey: updatedList };
    });
  };

  const handleSaveTeachingJourney = (index) => {
    const list = Array.isArray(formData.teaching_journey) ? formData.teaching_journey : [];
    const item = list[index];
    if (!item) return;

    const { isValid, errors } = validateTeachingJourneyEntry(item, index);
    if (!isValid) {
      setFormErrors(prev => ({ ...prev, ...errors }));
      return;
    }

    setFormErrors(prev => {
      const nextErrs = { ...prev };
      Object.keys(nextErrs).forEach(k => {
        if (k.startsWith(`journey_${index}_`)) delete nextErrs[k];
      });
      return nextErrs;
    });

    setFormData(prev => {
      const currentList = Array.isArray(prev.teaching_journey) ? [...prev.teaching_journey] : [];
      if (currentList[index]) {
        currentList[index] = { ...currentList[index], isEditing: false };
      }
      return { ...prev, teaching_journey: currentList };
    });
  };

  const handleRemoveTeachingJourney = (index) => {
    const list = Array.isArray(formData.teaching_journey) ? formData.teaching_journey : [];
    const target = list[index];
    const hasData = target && (target.role_title || target.organisation || target.description);
    if (hasData) {
      if (!window.confirm('Are you sure you want to remove this journey entry?')) {
        return;
      }
    }
    setFormData(prev => ({
      ...prev,
      teaching_journey: (Array.isArray(prev.teaching_journey) ? prev.teaching_journey : []).filter((_, idx) => idx !== index)
    }));
  };

  const handleTeachingJourneyChange = (index, field, value) => {
    setFormData(prev => {
      const list = Array.isArray(prev.teaching_journey) ? [...prev.teaching_journey] : [];
      if (!list[index]) return prev;
      const updated = { ...list[index], [field]: value };
      if (field === 'is_current' && value === true) {
        updated.end_month = '';
        updated.end_year = '';
      }
      list[index] = updated;
      return { ...prev, teaching_journey: list };
    });
    if (formErrors[`journey_${index}_${field}`]) {
      setFormErrors(p => ({ ...p, [`journey_${index}_${field}`]: '' }));
    }
  };

  const handleEditCustomCourse = (idx) => {
    const c = formData.custom_courses[idx];
    setCustomName(c.course_name || '');

    const rawCat = c.course_category || '';
    if (rawCat === 'Other' || rawCat.endsWith('-(Other)') || rawCat.toLowerCase().includes('(other)') || c.custom_category) {
      setCustomCategory('Other');
      const cleanManual = (c.custom_category || rawCat).replace(/-\(Other\)$/i, '').trim();
      setCustomCategoryOther(cleanManual);
    } else {
      setCustomCategory(rawCat || 'Yoga Specialization');
      setCustomCategoryOther('');
    }
    setCustomCategoryOtherError('');
    setCustomLevel(c.course_level || 'All Levels');
    setCustomDuration(c.course_duration || '');
    setCustomSubjects(c.subjects_covered || []);
    setCustomQualification(c.supporting_qualification || '');
    setCustomDetails(c.course_details || '');
    setEditingCustomIdx(idx);
  };

  const handleRemoveCustomCourse = (idx) => {
    setFormData(prev => {
      const updated = prev.custom_courses.filter((_, i) => i !== idx);
      let updatedIds = prev.selected_course_ids;
      if (updated.length === 0 && updatedIds.includes('OTHER')) {
        updatedIds = updatedIds.filter(id => id !== 'OTHER');
      }
      return { ...prev, custom_courses: updated, selected_course_ids: updatedIds };
    });
    if (editingCustomIdx === idx) setEditingCustomIdx(null);
  };

  // Certifications Handlers (Section 3)
  const handleAddCert = (explicitCertObj = null) => {
    const targetName = explicitCertObj ? explicitCertObj.name : certName;
    const targetDriveLink = explicitCertObj ? explicitCertObj.drive_link : certDriveLink;
    const errs = {};
    if (!targetName || !targetName.trim()) {
      errs.certName = "Certificate Name is required.";
    }
    if (targetDriveLink && targetDriveLink.trim() !== '' && !isValidUrl(targetDriveLink)) {
      errs.certDriveLink = "Enter a valid link, for example www.example.com or https://www.example.com";
    }
    if (Object.keys(errs).length > 0) {
      setFormErrors(prev => ({ ...prev, ...errs }));
      focusFirstErrorField(errs);
      return null;
    }

    const newCert = explicitCertObj || {
      name: certName.trim(),
      issuer: certIssuer.trim(),
      year: certYear.trim(),
      id_no: certId.trim(),
      school_link: certSchoolLink.trim(),
      drive_link: certDriveLink.trim()
    };

    let updatedCerts = Array.isArray(formData.certifications) ? [...formData.certifications] : [];
    if (editingCertIdx !== null) {
      updatedCerts[editingCertIdx] = newCert;
    } else {
      updatedCerts.push(newCert);
    }

    setFormData(prev => ({ ...prev, certifications: updatedCerts }));

    if (!explicitCertObj) {
      setCertName('');
      setCertIssuer('');
      setCertYear('');
      setCertId('');
      setCertSchoolLink('');
      setCertDriveLink('');
      setEditingCertIdx(null);
    }

    setFormErrors(prev => {
      const copy = { ...prev };
      delete copy.certName;
      delete copy.certIssuer;
      delete copy.certYear;
      delete copy.certDriveLink;
      delete copy.certifications;
      return copy;
    });
    return updatedCerts;
  };

  const handleEditCert = (idx) => {
    const list = Array.isArray(formData.certifications) ? formData.certifications : [];
    const item = list[idx];
    if (!item) return;
    setCertName(item.name || item.title || '');
    setCertIssuer(item.issuer || item.organization || '');
    setCertYear(item.year || '');
    setCertId(item.id_no || item.cert_id || '');
    setCertSchoolLink(item.school_link || '');
    setCertDriveLink(item.drive_link || item.link || '');
    setEditingCertIdx(idx);
  };

  const handleRemoveCert = (idx) => {
    setFormData(prev => ({
      ...prev,
      certifications: Array.isArray(prev.certifications) ? prev.certifications.filter((_, i) => i !== idx) : []
    }));
    if (editingCertIdx === idx) setEditingCertIdx(null);
  };

  // Experience Roles Handlers (Section 5)
  const handleAddRole = () => {
    if (!roleTitle.trim()) {
      alert("Role Title / Position is required.");
      return;
    }
    const newRole = {
      years: roleYears.trim(),
      role: roleTitle.trim(),
      institute: roleOrg.trim(),
      location: roleLoc.trim(),
      mode: roleMode,
      description: roleDesc.trim(),
      subjects: roleSubjects.trim()
    };
    setFormData(prev => {
      const current = Array.isArray(prev.experience_timeline) ? [...prev.experience_timeline] : [];
      if (editingRoleIdx !== null) {
        current[editingRoleIdx] = newRole;
      } else {
        current.push(newRole);
      }
      return { ...prev, experience_timeline: current };
    });
    setRoleYears('');
    setRoleTitle('');
    setRoleOrg('');
    setRoleLoc('');
    setRoleMode('Online');
    setRoleDesc('');
    setRoleSubjects('');
    setEditingRoleIdx(null);
  };

  const handleEditRole = (idx) => {
    const list = Array.isArray(formData.experience_timeline) ? formData.experience_timeline : [];
    const item = list[idx];
    if (!item) return;
    setRoleYears(item.years || '');
    setRoleTitle(item.role || item.title || '');
    setRoleOrg(item.institute || item.organization || '');
    setRoleLoc(item.location || '');
    setRoleMode(item.mode || 'Online');
    setRoleDesc(item.description || '');
    setRoleSubjects(item.subjects || '');
    setEditingRoleIdx(idx);
  };

  const handleRemoveRole = (idx) => {
    setFormData(prev => ({
      ...prev,
      experience_timeline: Array.isArray(prev.experience_timeline) ? prev.experience_timeline.filter((_, i) => i !== idx) : []
    }));
    if (editingRoleIdx === idx) setEditingRoleIdx(null);
  };

  // References Handlers (Section 10)
  const handleAddReference = () => {
    if (!refName.trim()) {
      alert("Reference Name is required.");
      return;
    }
    const newRef = {
      name: refName.trim(),
      org: refOrg.trim(),
      designation: refDesig.trim(),
      relationship: refRel.trim(),
      email: refEmail.trim(),
      mobile: refMobile.trim(),
      years_known: refYears.trim(),
      contact_permitted: refContactPermitted
    };
    setFormData(prev => {
      const current = Array.isArray(prev.references) ? [...prev.references] : [];
      if (editingRefIdx !== null) {
        current[editingRefIdx] = newRef;
      } else {
        current.push(newRef);
      }
      return { ...prev, references: current };
    });
    setRefName('');
    setRefOrg('');
    setRefDesig('');
    setRefRel('');
    setRefEmail('');
    setRefMobile('');
    setRefYears('');
    setRefContactPermitted('Yes');
    setEditingRefIdx(null);
  };

  const handleEditReference = (idx) => {
    const list = Array.isArray(formData.references) ? formData.references : [];
    const item = list[idx];
    if (!item) return;
    setRefName(item.name || '');
    setRefOrg(item.org || item.organization || '');
    setRefDesig(item.designation || '');
    setRefRel(item.relationship || '');
    setRefEmail(item.email || '');
    setRefMobile(item.mobile || '');
    setRefYears(item.years_known || '');
    setRefContactPermitted(item.contact_permitted || 'Yes');
    setEditingRefIdx(idx);
  };

  const handleRemoveReference = (idx) => {
    setFormData(prev => ({
      ...prev,
      references: Array.isArray(prev.references) ? prev.references.filter((_, i) => i !== idx) : []
    }));
    if (editingRefIdx === idx) setEditingRefIdx(null);
  };

  // Teaching Format Toggle Handler
  const toggleTeachingFormat = (fmt) => {
    setFormData(prev => {
      const current = [...prev.teaching_formats];
      const updated = current.includes(fmt) ? current.filter(f => f !== fmt) : [...current, fmt];
      return { ...prev, teaching_formats: updated };
    });
    if (formErrors.teaching_formats) {
      setFormErrors(prev => ({ ...prev, teaching_formats: '' }));
    }
  };

  // Previously Taught Courses Toggle Handler
  const togglePrevTaughtCourse = (courseId) => {
    setFormData(prev => {
      const current = [...prev.previously_taught_course_ids];
      const updated = current.includes(courseId) ? current.filter(id => id !== courseId) : [...current, courseId];
      return { ...prev, previously_taught_course_ids: updated };
    });
  };

  // Section 4 Teaching Expertise Checkbox Handler
  const handleExpertiseCheckboxChange = (item) => {
    setFormData(prev => {
      const current = Array.isArray(prev.expertise) ? [...prev.expertise] : [];
      const updated = current.includes(item)
        ? current.filter(x => x !== item)
        : [...current, item];
      return { ...prev, expertise: updated };
    });
    if (formErrors.expertise) {
      setFormErrors(prev => ({ ...prev, expertise: '' }));
    }
  };

  // Section 7 Availability Handlers
  const handleDayToggle = (day, isAvailable) => {
    setFormData(prev => {
      const currentSched = { ...prev.weekly_schedule };
      const dayObj = currentSched[day] ? { ...currentSched[day] } : { available: false, slots: [] };
      dayObj.available = isAvailable;
      if (isAvailable && (!dayObj.slots || dayObj.slots.length === 0)) {
        dayObj.slots = [{ from: '09:00', to: '17:00' }];
      }
      currentSched[day] = dayObj;
      return { ...prev, weekly_schedule: currentSched };
    });
  };

  const handleAddSlot = (day) => {
    setFormData(prev => {
      const currentSched = { ...prev.weekly_schedule };
      const dayObj = currentSched[day] ? { ...currentSched[day] } : { available: true, slots: [] };
      const slots = Array.isArray(dayObj.slots) ? [...dayObj.slots] : [];
      slots.push({ from: '09:00', to: '17:00' });
      dayObj.available = true;
      dayObj.slots = slots;
      currentSched[day] = dayObj;
      return { ...prev, weekly_schedule: currentSched };
    });
  };

  const handleSlotChange = (day, slotIdx, field, val) => {
    setFormData(prev => {
      const currentSched = { ...prev.weekly_schedule };
      const dayObj = currentSched[day] ? { ...currentSched[day] } : { available: true, slots: [] };
      const slots = Array.isArray(dayObj.slots) ? [...dayObj.slots] : [];
      if (slots[slotIdx]) {
        slots[slotIdx] = { ...slots[slotIdx], [field]: val };
      }
      dayObj.slots = slots;
      currentSched[day] = dayObj;
      return { ...prev, weekly_schedule: currentSched };
    });
  };

  const handleRemoveSlot = (day, slotIdx) => {
    setFormData(prev => {
      const currentSched = { ...prev.weekly_schedule };
      const dayObj = currentSched[day] ? { ...currentSched[day] } : { available: true, slots: [] };
      const slots = Array.isArray(dayObj.slots) ? dayObj.slots.filter((_, i) => i !== slotIdx) : [];
      dayObj.slots = slots;
      if (slots.length === 0) {
        dayObj.available = false;
      }
      currentSched[day] = dayObj;
      return { ...prev, weekly_schedule: currentSched };
    });
  };

  // Helper to ensure all array fields are valid arrays when restoring drafts
  const sanitizeDraftData = (draftFd) => {
    if (!draftFd || typeof draftFd !== 'object') return {};
    let rawCerts = Array.isArray(draftFd.certifications) ? draftFd.certifications : [];
    let normalizedCerts = rawCerts.map(c => {
      if (typeof c === 'string') return { name: c, issuer: '', year: '', id_no: '', school_link: '', drive_link: '' };
      if (c && typeof c === 'object') {
        return {
          name: c.name || c.title || c.certificate_name || '',
          issuer: c.issuer || c.organization || c.issuing_organization || '',
          year: c.year || c.year_of_completion || '',
          id_no: c.id_no || c.cert_id || c.certificate_id || '',
          school_link: c.school_link || c.institute_link || '',
          drive_link: c.drive_link || c.certificate_drive_link || c.link || ''
        };
      }
      return null;
    }).filter(Boolean);

    return {
      ...draftFd,
      certifications: normalizedCerts,
      certs_confirmed: Boolean(draftFd.certs_confirmed),
      experience_timeline: Array.isArray(draftFd.experience_timeline) ? draftFd.experience_timeline : [],
      domestic_courses_taught: Array.isArray(draftFd.domestic_courses_taught) ? draftFd.domestic_courses_taught : (typeof draftFd.domestic_courses_taught === 'string' ? draftFd.domestic_courses_taught.split(',').map(s => s.trim()).filter(Boolean) : []),
      domestic_organisations: Array.isArray(draftFd.domestic_organisations) ? draftFd.domestic_organisations : (typeof draftFd.domestic_organisations === 'string' ? draftFd.domestic_organisations.split(',').map(s => s.trim()).filter(Boolean) : []),
      intl_countries_taught: Array.isArray(draftFd.intl_countries_taught) ? draftFd.intl_countries_taught : (typeof draftFd.intl_countries_taught === 'string' ? draftFd.intl_countries_taught.split(',').map(s => s.trim()).filter(Boolean) : []),
      intl_student_countries: Array.isArray(draftFd.intl_student_countries) ? draftFd.intl_student_countries : (typeof draftFd.intl_student_countries === 'string' ? draftFd.intl_student_countries.split(',').map(s => s.trim()).filter(Boolean) : []),
      intl_courses: Array.isArray(draftFd.intl_courses) ? draftFd.intl_courses : (typeof draftFd.intl_courses === 'string' ? draftFd.intl_courses.split(',').map(s => s.trim()).filter(Boolean) : []),
      intl_organisations: Array.isArray(draftFd.intl_organisations) ? draftFd.intl_organisations : (typeof draftFd.intl_organisations === 'string' ? draftFd.intl_organisations.split(',').map(s => s.trim()).filter(Boolean) : []),
      custom_courses: Array.isArray(draftFd.custom_courses) ? draftFd.custom_courses.map(c => {
        if (!c) return c;
        const rawCat = c.course_category || '';
        if (rawCat.endsWith('-(Other)') || rawCat === 'Other' || c.custom_category) {
          const cleanManual = (c.custom_category || rawCat).replace(/-\(Other\)$/i, '').trim();
          return {
            ...c,
            course_category: `${cleanManual}-(Other)`,
            custom_category: cleanManual
          };
        }
        return c;
      }) : [],
      selected_course_ids: Array.isArray(draftFd.selected_course_ids) ? draftFd.selected_course_ids : [],
      teaching_formats: Array.isArray(draftFd.teaching_formats) ? draftFd.teaching_formats : [],
      previous_course_experiences: (() => {
        if (Array.isArray(draftFd.previous_course_experiences) && draftFd.previous_course_experiences.length > 0) {
          return draftFd.previous_course_experiences.map((exp, idx) => {
            const cName = exp.courseName || exp.course_name || '';
            const tLoc = exp.teachingLocation || exp.teaching_location || exp.where_taught || draftFd.prev_batches_taught || '';
            const isComplete = Boolean(cName && tLoc);
            return {
              localId: exp.localId || `exp-${Date.now()}-${idx}`,
              isEditing: exp.isEditing !== undefined ? Boolean(exp.isEditing) : !isComplete,
              courseName: cName,
              teachingLocation: tLoc,
              experienceYears: exp.experienceYears !== undefined && exp.experienceYears !== null ? String(exp.experienceYears) : (exp.experience_years !== undefined ? String(exp.experience_years) : (draftFd.prev_recent_year || '')),
              experienceMonths: exp.experienceMonths !== undefined && exp.experienceMonths !== null ? String(exp.experienceMonths) : (exp.experience_months !== undefined ? String(exp.experience_months) : ''),
              studentsTaught: exp.studentsTaught !== undefined && exp.studentsTaught !== null ? String(exp.studentsTaught) : (exp.students_taught !== undefined ? String(exp.students_taught) : (draftFd.prev_students_taught || ''))
            };
          });
        }
        if (draftFd.previously_taught_choice === 'Yes' || draftFd.prev_batches_taught || draftFd.prev_recent_year || draftFd.prev_students_taught) {
          const rawCourse = Array.isArray(draftFd.previously_taught_course_ids) ? draftFd.previously_taught_course_ids.join(', ') : (draftFd.previously_taught_course_ids || '');
          return [{
            localId: 'legacy-1',
            isEditing: false,
            courseName: rawCourse,
            teachingLocation: draftFd.prev_batches_taught || '',
            experienceYears: draftFd.prev_recent_year || '',
            experienceMonths: '',
            studentsTaught: draftFd.prev_students_taught || ''
          }];
        }
        return [{
          localId: 'init-1',
          isEditing: true,
          courseName: '',
          teachingLocation: '',
          experienceYears: '',
          experienceMonths: '',
          studentsTaught: ''
        }];
      })(),
      previously_taught_course_ids: Array.isArray(draftFd.previously_taught_course_ids) ? draftFd.previously_taught_course_ids : [],
      references: Array.isArray(draftFd.references) ? draftFd.references : [],
      expertise: Array.isArray(draftFd.expertise) ? draftFd.expertise : [],
      teaching_journey: Array.isArray(draftFd.teaching_journey) ? draftFd.teaching_journey.map((j, idx) => {
        const role = j.role_title || j.role || '';
        const org = j.organisation || j.institute || '';
        const isComplete = Boolean(role && org);
        return {
          localId: j.localId || `journey-${Date.now()}-${idx}`,
          isEditing: j.isEditing !== undefined ? Boolean(j.isEditing) : !isComplete,
          role_title: role,
          organisation: org,
          city: j.city || '',
          state: j.state || '',
          country: j.country || 'India',
          start_month: j.start_month || '',
          start_year: j.start_year || '',
          end_month: j.end_month || '',
          end_year: j.end_year || '',
          is_current: Boolean(j.is_current),
          description: j.description || j.desc || ''
        };
      }) : []
    };
  };

  // Auto-load draft from DB upon mount if email exists in localStorage (no scrolling)
  useEffect(() => {
    const savedEmail = localStorage.getItem('pranaflow_draft_email');
    if (savedEmail) {
      const loadDraft = async () => {
        try {
          const res = await getTeacherApplicationDraft(savedEmail);
          if (res && res.success && res.draft) {
            const draftFd = res.draft.form_data || {};
            const restoredPhone = draftFd.mobile || draftFd.whatsapp || draftFd.mobile_number || draftFd.whatsapp_number || '';
            const cleanDraft = sanitizeDraftData(draftFd);
            setFormData(prev => ({
              ...prev,
              ...cleanDraft,
              mobile: restoredPhone,
              whatsapp: restoredPhone
            }));
            setActiveSection('basic-information');
          }
        } catch (err) {
          console.error("Auto-load draft error:", err);
        }
      };
      loadDraft();
    }
  }, []);

  // ── SEO Metadata & Schema ──────────────────────────────────────────────────
  useEffect(() => {
    document.title = "Apply to Teach Yoga | Yoga Teacher Application | PranaFlow Academy";

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content',
      "Apply to join PranaFlow Academy as a partner yoga teacher or faculty member. Share your experience, teaching qualifications, and join our global yoga learning platform."
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/teacher-apply");

    const ogTags = [
      { property: 'og:title', content: "Apply to Teach Yoga | Yoga Teacher Application | PranaFlow Academy" },
      { property: 'og:description', content: "Apply to join PranaFlow Academy as a partner yoga teacher or faculty member. Share your experience, teaching qualifications, and join our global yoga learning platform." },
      { property: 'og:type', content: "website" },
      { property: 'og:url', content: "https://pranaflowacademy.com/teacher-apply" },
      { property: 'og:image', content: "https://pranaflowacademy.com/assets/rishikesh_hero.webp" },
      { property: 'og:site_name', content: "PranaFlow Academy" }
    ];

    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "Apply to Teach Yoga | Yoga Teacher Application | PranaFlow Academy" },
      { name: 'twitter:description', content: "Apply to join PranaFlow Academy as a partner yoga teacher or faculty member. Share your experience, teaching qualifications, and join our global yoga learning platform." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/assets/rishikesh_hero.webp" }
    ];

    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Dynamic JSON-LD Schema Injection
    const scriptId = 'jsonld-schema-teacher-apply';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const webpageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://pranaflowacademy.com/teacher-apply#webpage",
      "url": "https://pranaflowacademy.com/teacher-apply",
      "name": "Apply to Teach Yoga | Yoga Teacher Application | PranaFlow Academy",
      "description": "Apply to join PranaFlow Academy as a partner yoga teacher or faculty member. Share your experience, teaching qualifications, and join our global yoga learning platform.",
      "isPartOf": {
        "@id": "https://pranaflowacademy.com/#website"
      },
      "about": {
        "@id": "https://pranaflowacademy.com/#organization"
      }
    };
    script.textContent = JSON.stringify([webpageSchema]);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Monitor scroll positioning of form sections to update the active left menu item
  useEffect(() => {
    const handleScroll = () => {
      if (isScrollingRef.current) return;
      const navbarOffset = 110;
      let currentSectionId = 'basic-information';

      for (let s of sections) {
        const el = document.getElementById(`section-${s.id}`) || document.getElementById(`section-${s.num}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= navbarOffset) {
            currentSectionId = s.id;
          }
        }
      }
      setActiveSection(currentSectionId);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    const container = formScrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Click outside and Escape key listener for custom dropdown selectors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (intlCountriesRef.current && !intlCountriesRef.current.contains(e.target)) {
        setShowIntlCountriesDropdown(false);
      }
      if (intlStudentCountriesRef.current && !intlStudentCountriesRef.current.contains(e.target)) {
        setShowIntlStudentCountriesDropdown(false);
      }
      if (intlCoursesRef.current && !intlCoursesRef.current.contains(e.target)) {
        setShowIntlCoursesDropdown(false);
      }
      if (domesticCoursesRef.current && !domesticCoursesRef.current.contains(e.target)) {
        setShowDomesticCoursesDropdown(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowIntlCountriesDropdown(false);
        setShowIntlStudentCountriesDropdown(false);
        setShowIntlCoursesDropdown(false);
        setShowDomesticCoursesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Auto-calculate Total Domestic Students Taught in India (Online + In-person)
  useEffect(() => {
    const onlineCount = formData.domestic_taught_online === 'Yes' ? (parseInt(formData.domestic_online_student_count, 10) || 0) : 0;
    const inPersonCount = formData.domestic_taught_in_person === 'Yes' ? (parseInt(formData.domestic_in_person_student_count, 10) || 0) : 0;
    const total = onlineCount + inPersonCount;
    setFormData(prev => ({
      ...prev,
      domestic_total_student_count: (formData.domestic_taught_online === 'Yes' || formData.domestic_taught_in_person === 'Yes') ? String(total) : ''
    }));
  }, [
    formData.domestic_taught_online,
    formData.domestic_online_student_count,
    formData.domestic_taught_in_person,
    formData.domestic_in_person_student_count
  ]);

  // Calculate overall application progress percentage
  const calculateCompletion = () => {
    const required = [
      formData.full_name,
      formData.email,
      formData.mobile,
      formData.country,
      formData.city,
      formData.current_location,
      formData.languages,
      formData.time_zone,
      formData.designation,
      formData.total_experience_years,
      formData.total_teaching_hours,
      formData.intro_video_link,
      formData.demo_video_link
    ];
    const filled = required.filter(val => val && String(val).trim() !== '');
    
    let score = filled.length;
    let total = required.length;

    if (Array.isArray(formData.certifications) && formData.certifications.length > 0) score++;
    total++;

    if (Array.isArray(formData.expertise) && formData.expertise.length > 0) score++;
    total++;

    if (formData.decl_boundaries && formData.decl_privacy && formData.decl_recording && formData.decl_medical && formData.decl_policies && formData.decl_accuracy) {
      score++;
    }
    total++;

    return Math.round((score / total) * 100);
  };

  // Website URL normalization helper: prepends https:// if protocol is missing
  const normalizeWebsiteUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    let trimmed = url.trim();
    if (!trimmed) return '';

    // Reject unsafe schemes, JavaScript URLs, HTML tags, or scripts
    if (/^(javascript|data|vbscript|file):/i.test(trimmed) || /<[^>]*>/g.test(trimmed)) {
      return null;
    }

    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    if (/^\/\//.test(trimmed)) {
      return 'https:' + trimmed;
    }

    return 'https://' + trimmed;
  };

  // URL validation check accepting valid domain names with or without http/https protocol
  const isValidWebsiteUrl = (url) => {
    if (!url || String(url).trim() === '') return true; // Optional empty field is valid

    const normalized = normalizeWebsiteUrl(url);
    if (normalized === null) return false;

    try {
      const parsed = new URL(normalized);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;

      const host = parsed.hostname;
      if (!host || !host.includes('.')) return false;

      const domainRegex = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
      return domainRegex.test(host);
    } catch (_) {
      return false;
    }
  };

  // Alias for backward compatibility across section checks
  const isValidUrl = isValidWebsiteUrl;

  // Save Draft to database
  const handleSaveDraft = async () => {
    if (!formData.email) {
      alert("Please fill in your Email Address in Section 1 to save draft progress.");
      return;
    }
    try {
      setIsSavingDraft(true);
      setErrorMsg('');
      
      const pct = calculateCompletion();
      const res = await saveTeacherApplicationDraft({
        email: formData.email,
        form_data: formData,
        current_section: getSectionNum(activeSection),
        completion_percentage: pct
      });
      
      if (res && res.success) {
        localStorage.setItem('pranaflow_draft_email', formData.email);
        alert("Application draft saved successfully in the database.");
      } else {
        alert(res.message || "Failed to save draft.");
      }
    } catch (err) {
      alert("Error saving draft: " + err.message);
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Silent draft sync during section transitions
  const handleSaveDraftSilent = async () => {
    if (!formData.email) return;
    try {
      const pct = calculateCompletion();
      await saveTeacherApplicationDraft({
        email: formData.email,
        form_data: formData,
        current_section: getSectionNum(activeSection),
        completion_percentage: pct
      });
      localStorage.setItem('pranaflow_draft_email', formData.email);
    } catch (_) {
      // Ignore silent background draft sync failures
    }
  };

  // Fetch saved draft on demand when email loses focus (onBlur)
  const handleEmailBlur = async () => {
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return;
    try {
      const res = await getTeacherApplicationDraft(formData.email);
      if (res && res.success && res.draft) {
        const confirmLoad = window.confirm("We found a saved draft for this email address. Would you like to restore your progress?");
        if (confirmLoad) {
          const draftFd = res.draft.form_data || {};
          const restoredPhone = draftFd.mobile || draftFd.whatsapp || draftFd.mobile_number || draftFd.whatsapp_number || '';
          const cleanDraft = sanitizeDraftData(draftFd);
          setFormData(prev => ({
            ...prev,
            ...cleanDraft,
            mobile: restoredPhone,
            whatsapp: restoredPhone
          }));
          const targetSecId = getSectionId(res.draft.current_section || 1);
          setActiveSection(targetSecId);
          localStorage.setItem('pranaflow_draft_email', formData.email);
          
          // Scroll to restored section
          setTimeout(() => {
            handleMenuClick(targetSecId);
          }, 200);
        }
      }
    } catch (err) {
      console.warn("Failed to retrieve draft lookup:", err);
    }
  };

  // Smooth scroll handler on section menu clicks
  const handleMenuClick = (target) => {
    const secId = getSectionId(target);
    const secNum = getSectionNum(target);

    setActiveSection(secId);
    setExpanded(prev => ({ ...prev, [secId]: true }));

    setTimeout(() => {
      const el = document.getElementById(`section-${secId}`) || document.getElementById(`section-${secNum}`);
      if (el) {
        isScrollingRef.current = true;

        const navbarOffset = 95;
        const rect = el.getBoundingClientRect();
        const targetWindowScroll = Math.max(0, rect.top + window.pageYOffset - navbarOffset);

        window.scrollTo({
          top: targetWindowScroll,
          behavior: 'smooth'
        });

        setTimeout(() => {
          isScrollingRef.current = false;
        }, 600);
      }
    }, 40);
  };

  // Handle standard input updates
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let updatedVal = type === 'checkbox' ? checked : value;

    if (name === 'can_issue_certificates') {
      setFormData(prev => ({
        ...prev,
        can_issue_certificates: value,
        certificate_types_can_issue: value === 'Yes' ? prev.certificate_types_can_issue : ''
      }));
      if (formErrors.can_issue_certificates || formErrors.certificate_types_can_issue) {
        setFormErrors(prev => ({
          ...prev,
          can_issue_certificates: '',
          certificate_types_can_issue: ''
        }));
      }
      return;
    }

    if (name === 'yearsOfTeachingExperience' || name === 'total_experience_years') {
      setFormData(prev => ({
        ...prev,
        yearsOfTeachingExperience: value,
        total_experience_years: value
      }));
      if (formErrors.total_experience_years || formErrors.yearsOfTeachingExperience) {
        setFormErrors(prev => ({
          ...prev,
          total_experience_years: '',
          yearsOfTeachingExperience: ''
        }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [name]: updatedVal }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Numeric validation helper for Years of Teaching Experience
  const validateYearsOfExperience = (val) => {
    if (val === undefined || val === null || String(val).trim() === '') {
      return "Years of teaching experience is required.";
    }
    const cleanVal = String(val).replace(/[^0-9.]/g, '');
    if (cleanVal === '') {
      return "Please enter a valid number of years.";
    }
    const num = parseFloat(cleanVal);
    if (isNaN(num) || num < 0) {
      return "Please enter a valid positive number of years.";
    }
    return '';
  };

  // Mapping between error keys and section IDs for auto-expansion and scrolling
  const fieldToSectionMap = {
    full_name: 'basic-information',
    name: 'basic-information',
    email: 'basic-information',
    mobile: 'basic-information',
    whatsapp: 'basic-information',
    country: 'basic-information',
    state: 'basic-information',
    city: 'basic-information',
    current_location: 'basic-information',
    languages: 'basic-information',
    preferred_language: 'basic-information',
    designation: 'basic-information',
    
    yearsOfTeachingExperience: 'professional-profile',
    total_experience_years: 'professional-profile',
    total_teaching_hours: 'professional-profile',
    
    highest_certification: 'qualifications-certificates',
    other_highest_certification_name: 'qualifications-certificates',
    certifications: 'qualifications-certificates',
    certs_confirmed: 'qualifications-certificates',
    
    expertise: 'teaching-expertise',
    student_types_taught: 'teaching-expertise',
    other_student_types_taught: 'teaching-expertise',
    
    intl_has_experience: 'experience-background',
    intl_taught_outside_home: 'experience-background',
    intl_taught_online_intl: 'experience-background',
    intl_teaching_mode: 'experience-background',
    intl_countries_taught: 'experience-background',
    intl_student_countries: 'experience-background',
    intl_courses: 'experience-background',
    intl_student_count: 'experience-background',
    intl_description: 'experience-background',
    intl_evidence_url: 'experience-background',

    domestic_has_experience: 'experience-background',
    domestic_teaching_mode: 'experience-background',
    domestic_courses_taught: 'experience-background',
    domestic_taught_online: 'experience-background',
    domestic_online_student_count: 'experience-background',
    domestic_taught_in_person: 'experience-background',
    domestic_in_person_student_count: 'experience-background',
    domestic_total_student_count: 'experience-background',
    domestic_teaching_hours: 'experience-background',
    domestic_organisations: 'experience-background',
    domestic_description: 'experience-background',

    selected_course_ids: 'courses-programs',
    custom_courses: 'courses-programs',
    courses_can_teach: 'courses-programs',
    other_courses_can_teach: 'courses-programs',
    
    teaching_formats: 'availability',
    previously_taught_choice: 'experience-background',
    previous_course_experiences: 'experience-background',
    
    weekly_schedule: 'availability',
    available_days: 'availability',
    available_time_slots: 'availability',
    
    intro_video_link: 'videos-links',
    demo_video_link: 'videos-links',
    resume_drive_link: 'videos-links',
    certificate_drive_link: 'videos-links',
    government_id_drive_link: 'videos-links',
    
    owns_or_represents_school: 'school-affiliation',
    partner_school_name: 'school-affiliation',
    can_issue_certificates: 'school-affiliation',
    certificate_types_can_issue: 'school-affiliation',
    other_certificate_type: 'school-affiliation',
    
    references: 'references',
    ref_name: 'references',
    ref_email: 'references',
    ref_mobile: 'references',
    reference_name: 'references',
    reference_contact: 'references',
    
    motivation_statement: 'motivation-strengths',
    teaching_strengths: 'motivation-strengths',
    why_partner_with_us: 'motivation-strengths',
    why_good_teacher: 'motivation-strengths',
    teaching_style_approach: 'motivation-strengths',
    professional_summary: 'motivation-strengths',
    why_join_pranaflow: 'motivation-strengths',
    what_makes_me_exceptional: 'motivation-strengths',
    
    declarations: 'verification',
    declaration_accepted: 'verification',
    consent_accepted: 'verification',
    decl_boundaries: 'verification',
    decl_privacy: 'verification',
    decl_policies: 'verification',
    decl_accuracy: 'verification'
  };
  const focusFirstErrorField = (errors) => {
    const firstErrKey = Object.keys(errors)[0];
    if (!firstErrKey) return;
    let secId = fieldToSectionMap[firstErrKey];
    if (!secId) {
      if (firstErrKey.startsWith('prev_exp_')) secId = 'experience-background';
      else if (firstErrKey.startsWith('journey_')) secId = 'experience-background';
      else secId = 'basic-information';
    }

    setCompletedSections(prev => ({ ...prev, [secId]: false }));
    setExpanded(prev => ({ ...prev, [secId]: true }));
    setActiveSection(secId);

    setTimeout(() => {
      let el = document.querySelector(`[name="${firstErrKey}"]`) || 
               document.getElementById(firstErrKey) || 
               document.getElementById(`${firstErrKey}-error`);

      if (!el && firstErrKey.startsWith('prev_exp_')) {
        el = document.getElementById('previous-experiences-container') || document.getElementById('section-experience-background');
      }
      if (!el && firstErrKey.startsWith('journey_')) {
        el = document.getElementById('teaching-journey-container') || document.getElementById('section-experience-background');
      }
      if (!el) {
        el = document.getElementById(`section-${secId}`);
      }

      if (el) {
        const headerOffset = 120;
        const elementPosition = el.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth'
        });

        setTimeout(() => {
          if (typeof el.focus === 'function') {
            el.focus();
          } else {
            const focusable = el.querySelector('input, select, textarea, button, [tabindex="0"]');
            if (focusable && typeof focusable.focus === 'function') {
              focusable.focus();
            }
          }
        }, 220);
      }
    }, 150);
  };

  // Section validation for transition and submission
  const validateSection = (s) => {
    const sNum = getSectionNum(s);
    const errors = {};
    if (sNum === 1) {
      if (!formData.full_name.trim()) errors.full_name = "Please enter your full name. Example: Priya Sharma";
      if (!formData.email.trim()) {
        errors.email = "Please enter a valid email address. Example: priya@example.com";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = "Please enter a valid email address. Example: priya@example.com";
      }
      if (!formData.mobile.trim()) errors.mobile = "Please enter a valid phone number. Example: 9876543210";
      if (!formData.country.trim()) errors.country = "Please select your country. Example: India";
      if (!formData.state.trim()) errors.state = "Please enter your state or province. Example: Uttarakhand";
      if (!formData.city.trim()) errors.city = "Please enter your city. Example: Rishikesh";
      if (!formData.current_location.trim()) errors.current_location = "Please enter your current location. Example: Rishikesh, India";
      if (!formData.languages.trim()) errors.languages = "Please enter the languages you can teach. Example: English, Hindi";
      if (!formData.designation.trim()) errors.designation = "Please enter your professional title or designation. Example: Senior Hatha & Vinyasa Yoga Teacher";
    }
    if (sNum === 2) {
      const expVal = formData.yearsOfTeachingExperience || formData.total_experience_years;
      const expErr = validateYearsOfExperience(expVal);
      if (expErr) {
        const msg = "Please enter your teaching experience in years. Example: 3";
        errors.total_experience_years = msg;
        errors.yearsOfTeachingExperience = msg;
      }
      if (!formData.total_teaching_hours || !formData.total_teaching_hours.trim()) {
        errors.total_teaching_hours = "Please enter your approximate total teaching hours. Example: 800";
      }
    }
    if (sNum === 3) {
      let currentCerts = Array.isArray(formData.certifications) ? [...formData.certifications] : [];

      if (certName && certName.trim() !== '') {
        const updated = handleAddCert();
        if (!updated) {
          if (!certName.trim()) errors.certName = "Please add at least one qualification or certificate. Example: 200-Hour RYT Yoga Teacher Training";
          if (certDriveLink && certDriveLink.trim() !== '' && !isValidWebsiteUrl(certDriveLink)) {
            errors.certDriveLink = "Please enter a valid Google Drive URL. Example: www.drive.google.com/file/...";
          }
          return errors;
        }
        currentCerts = updated;
      }

      if (currentCerts.length === 0) {
        errors.certName = "Please add at least one qualification or certificate. Example: 200-Hour RYT Yoga Teacher Training";
      }

      if (certDriveLink && certDriveLink.trim() !== '' && !isValidWebsiteUrl(certDriveLink)) {
        errors.certDriveLink = "Please enter a valid Google Drive URL. Example: www.drive.google.com/file/...";
      }

      if (!formData.certs_confirmed) {
        errors.certs_confirmed = "You must confirm that your submitted certificates are genuine before proceeding.";
      }
    }
    if (sNum === 4) {
      if (!Array.isArray(formData.expertise) || formData.expertise.length === 0) {
        errors.expertise = "Please select at least one teaching subject or specialization. Example: Hatha Yoga, Pranayama";
      }
    }
    if (sNum === 5) {
      if (!Array.isArray(formData.selected_course_ids) || formData.selected_course_ids.length === 0) {
        errors.selected_course_ids = "Please select at least one course or program you can teach.";
      }
      if (formData.selected_course_ids.includes('OTHER') && (!Array.isArray(formData.custom_courses) || formData.custom_courses.length === 0)) {
        errors.custom_courses = "Please add at least one unlisted custom course, or deselect 'Other — Course Not Listed'. Example: Sound Healing";
      }
    }
    if (sNum === 6) {
      if (!formData.intl_has_experience) {
        errors.intl_has_experience = "Please select whether you have international teaching experience.";
      } else if (formData.intl_has_experience === 'Yes') {
        if (!formData.intl_taught_outside_home) errors.intl_taught_outside_home = "Please specify if you taught outside your home country.";
        if (formData.intl_taught_outside_home === 'Yes' && (!Array.isArray(formData.intl_countries_taught) || formData.intl_countries_taught.length === 0)) {
          errors.intl_countries_taught = "Please select at least one country where you taught physically. Example: United Kingdom, UAE";
        }
        if (!formData.intl_taught_online_intl) errors.intl_taught_online_intl = "Please specify if you taught international students online.";
        if (formData.intl_taught_online_intl === 'Yes' && (!Array.isArray(formData.intl_student_countries) || formData.intl_student_countries.length === 0)) {
          errors.intl_student_countries = "Please select at least one country of your online international students. Example: USA, Germany";
        }
        if (!formData.intl_teaching_mode) errors.intl_teaching_mode = "Please select your international teaching mode. Example: Online, In-Person";
        if (!Array.isArray(formData.intl_courses) || formData.intl_courses.length === 0) errors.intl_courses = "Please select or enter courses taught internationally. Example: 200-Hour RYT";
        if (!formData.intl_student_count || String(formData.intl_student_count).trim() === '') errors.intl_student_count = "Please enter approximate number of international students taught. Example: 150";
        if (!formData.intl_description || !formData.intl_description.trim()) errors.intl_description = "Please describe your international teaching experience. Example: Taught online YTT workshops for European students.";
      }

      if (!formData.domestic_has_experience) {
        errors.domestic_has_experience = "Please select whether you have taught students within India.";
      } else if (formData.domestic_has_experience === 'Yes') {
        if (!formData.domestic_teaching_mode) errors.domestic_teaching_mode = "Please select your domestic teaching mode in India. Example: Offline Studio, Online";
        if (!Array.isArray(formData.domestic_courses_taught) || formData.domestic_courses_taught.length === 0) errors.domestic_courses_taught = "Please select or enter courses/subjects taught in India. Example: Hatha Yoga, Pranayama";
        if (!formData.domestic_taught_online) errors.domestic_taught_online = "Please select whether you have taught students online within India.";
        if (formData.domestic_taught_online === 'Yes' && (!formData.domestic_online_student_count || String(formData.domestic_online_student_count).trim() === '')) {
          errors.domestic_online_student_count = "Please enter approximate number of students taught online in India. Example: 300";
        }
        if (!formData.domestic_taught_in_person) errors.domestic_taught_in_person = "Please select whether you have taught students in person within India.";
        if (formData.domestic_taught_in_person === 'Yes' && (!formData.domestic_in_person_student_count || String(formData.domestic_in_person_student_count).trim() === '')) {
          errors.domestic_in_person_student_count = "Please enter approximate number of students taught in person in India. Example: 200";
        }
        if (!formData.domestic_description || !formData.domestic_description.trim()) errors.domestic_description = "Please describe your domestic teaching experience in India. Example: Led daily Hatha classes at Rishikesh Yoga Center.";
      }

      if (!formData.previously_taught_choice) {
        errors.previously_taught_choice = "Please indicate whether you have previously taught these programs.";
      } else if (formData.previously_taught_choice === 'Yes') {
        const expList = Array.isArray(formData.previous_course_experiences) ? formData.previous_course_experiences : [];
        if (expList.length === 0) {
          errors.previous_course_experiences = "Please add at least one program experience record.";
        } else {
          expList.forEach((exp, idx) => {
            if (!exp.courseName || !exp.courseName.trim()) {
              errors[`prev_exp_${idx}_courseName`] = "Please select a previously taught program. Example: 200-Hour RYT";
            }
            if (!exp.teachingLocation || !exp.teachingLocation.trim()) {
              errors[`prev_exp_${idx}_teachingLocation`] = "Please enter where you taught this program. Example: Rishikesh, India";
            }
            const yrsStr = String(exp.experienceYears || '').trim();
            const mthsStr = String(exp.experienceMonths || '').trim();
            const yrs = yrsStr !== '' ? Number(yrsStr) : 0;
            const mths = mthsStr !== '' ? Number(mthsStr) : 0;
            if (yrsStr !== '' && (isNaN(yrs) || yrs < 0)) errors[`prev_exp_${idx}_experienceYears`] = "Years cannot be negative.";
            if (mthsStr !== '' && (isNaN(mths) || mths < 0 || mths > 11)) errors[`prev_exp_${idx}_experienceMonths`] = "Months must be between 0 and 11.";
            if (yrs === 0 && mths === 0) errors[`prev_exp_${idx}_duration`] = "Please enter at least 1 month or 1 year of teaching experience.";
            if (exp.studentsTaught === undefined || exp.studentsTaught === null || String(exp.studentsTaught).trim() === '') {
              errors[`prev_exp_${idx}_studentsTaught`] = "Please enter approximate number of students taught. Example: 50";
            } else if (isNaN(Number(exp.studentsTaught)) || Number(exp.studentsTaught) < 0) {
              errors[`prev_exp_${idx}_studentsTaught`] = "Student count cannot be negative.";
            }
          });
        }
      }

      const journeyList = Array.isArray(formData.teaching_journey) ? formData.teaching_journey : [];
      journeyList.forEach((jEntry, idx) => {
        const hasAnyVal = jEntry.role_title || jEntry.organisation || jEntry.city || jEntry.description || jEntry.start_month || jEntry.start_year;
        if (hasAnyVal) {
          if (!jEntry.role_title || !jEntry.role_title.trim()) errors[`journey_${idx}_role_title`] = "Please enter role or position title. Example: Lead Yoga Instructor";
          if (!jEntry.organisation || !jEntry.organisation.trim()) errors[`journey_${idx}_organisation`] = "Please enter organisation or studio name. Example: PranaFlow Academy";
          if (!jEntry.country || !jEntry.country.trim()) errors[`journey_${idx}_country`] = "Please select country. Example: India";
          if (!jEntry.start_month) errors[`journey_${idx}_start_month`] = "Please select start month.";
          if (!jEntry.start_year) errors[`journey_${idx}_start_year`] = "Please select start year.";
          if (!jEntry.is_current) {
            if (!jEntry.end_month) errors[`journey_${idx}_end_month`] = "Please select end month.";
            if (!jEntry.end_year) errors[`journey_${idx}_end_year`] = "Please select end year.";
          }
          if (!jEntry.description || !jEntry.description.trim()) errors[`journey_${idx}_description`] = "Please enter role description. Example: Taught 200-Hour YTT modules.";
        }
      });
    }
    if (sNum === 7) {
      if (!Array.isArray(formData.teaching_formats) || formData.teaching_formats.length === 0) {
        errors.teaching_formats = "Please select at least one preferred teaching format. Example: Online Live Classes";
      }
    }
    if (sNum === 8) {
      if (!formData.intro_video_link.trim()) {
        errors.intro_video_link = "Enter a valid link, for example www.example.com or https://www.example.com";
      } else if (!isValidWebsiteUrl(formData.intro_video_link)) {
        errors.intro_video_link = "Enter a valid link, for example www.example.com or https://www.example.com";
      }
      if (!formData.demo_video_link.trim()) {
        errors.demo_video_link = "Enter a valid link, for example www.example.com or https://www.example.com";
      } else if (!isValidWebsiteUrl(formData.demo_video_link)) {
        errors.demo_video_link = "Enter a valid link, for example www.example.com or https://www.example.com";
      }
      if (formData.resume_drive_link && formData.resume_drive_link.trim() !== '' && !isValidWebsiteUrl(formData.resume_drive_link)) {
        errors.resume_drive_link = "Enter a valid link, for example www.example.com or https://www.example.com";
      }
    }
    if (sNum === 9) {
      if (formData.can_issue_certificates === 'Yes') {
        if (!formData.certificate_types_can_issue || !formData.certificate_types_can_issue.trim()) {
          errors.certificate_types_can_issue = "Please enter at least one recognized certificate type. Example: 200-Hour RYT Certificate";
        }
      }
    }
    if (sNum === 12) {
      if (formData.government_id_drive_link && formData.government_id_drive_link.trim() !== '' && !isValidWebsiteUrl(formData.government_id_drive_link)) {
        errors.government_id_drive_link = "Enter a valid link, for example www.example.com or https://www.example.com";
      }
    }
    if (sNum === 11) {
      const tsaVal = (formData.teaching_style_approach || '').trim();
      const psVal = (formData.professional_summary || '').trim();
      const wjVal = (formData.why_join_pranaflow || '').trim();
      const wmVal = (formData.what_makes_me_exceptional || '').trim();
      if (!tsaVal) {
        errors.teaching_style_approach = "Please describe your teaching style and approach (at least 100 characters). Example: I teach classical Hatha Yoga focusing on breath synchronization, alignment, and mindful movement.";
      } else if (tsaVal.length < 100) {
        errors.teaching_style_approach = `Answer must be at least 100 characters (currently ${tsaVal.length}). Example: I teach classical Hatha Yoga focusing on breath synchronization, alignment, and mindful movement.`;
      }
      if (!psVal) {
        errors.professional_summary = "Please provide your professional summary (at least 100 characters). Example: Certified E-RYT 500 Yoga Teacher with 5 years of experience leading teacher training courses.";
      } else if (psVal.length < 100) {
        errors.professional_summary = `Answer must be at least 100 characters (currently ${psVal.length}). Example: Certified E-RYT 500 Yoga Teacher with 5 years of experience leading teacher training courses.`;
      }
      if (!wjVal) {
        errors.why_join_pranaflow = "Please explain why you want to join PranaFlow Academy (at least 100 characters). Example: I resonate with PranaFlow's authentic approach to yoga education and want to empower global students.";
      } else if (wjVal.length < 100) {
        errors.why_join_pranaflow = `Answer must be at least 100 characters (currently ${wjVal.length}). Example: I resonate with PranaFlow's authentic approach to yoga education and want to empower global students.`;
      }
      if (!wmVal) {
        errors.what_makes_me_exceptional = "Please describe what makes you an exceptional teacher (at least 100 characters). Example: My ability to simplify complex yogic philosophy and adapt postures for students of all body types.";
      } else if (wmVal.length < 100) {
        errors.what_makes_me_exceptional = `Answer must be at least 100 characters (currently ${wmVal.length}). Example: My ability to simplify complex yogic philosophy and adapt postures for students of all body types.`;
      }
    }
    return errors;
  };

  // Save & Continue Action
  const handleSectionContinue = (s) => {
    const sNum = getSectionNum(s);
    const secId = getSectionId(s);

    if (secId === 'experience-background' || sNum === 6) {
      let courseExps = Array.isArray(formData.previous_course_experiences) ? [...formData.previous_course_experiences] : [];
      if (courseExps.length > 1) {
        courseExps = courseExps.filter(exp => Boolean(exp.courseName || exp.teachingLocation || exp.experienceYears || exp.experienceMonths || exp.studentsTaught));
      }
      courseExps = courseExps.map(e => ({ ...e, isEditing: false }));

      let journeys = Array.isArray(formData.teaching_journey) ? [...formData.teaching_journey] : [];
      if (journeys.length > 0) {
        journeys = journeys.filter(j => Boolean(j.role_title || j.organisation || j.description || j.city || j.state));
      }
      journeys = journeys.map(j => ({ ...j, isEditing: false }));

      setFormData(prev => ({
        ...prev,
        previous_course_experiences: courseExps,
        teaching_journey: journeys
      }));
    }

    const errors = validateSection(sNum);
    if (Object.keys(errors).length > 0) {
      setCompletedSections(prev => ({ ...prev, [secId]: false }));
      setFormErrors(errors);
      focusFirstErrorField(errors);
      return;
    }
    setFormErrors({});
    handleSaveDraftSilent();
    
    setCompletedSections(prev => ({ ...prev, [secId]: true }));
    
    const nextSecNum = sNum + 1;
    if (nextSecNum <= 12) {
      handleMenuClick(nextSecNum);
    }
  };

  // Edit action for completed sections
  const handleEditSection = (target) => {
    const secId = getSectionId(target);
    setCompletedSections(prev => ({ ...prev, [secId]: false }));
    setExpanded(prev => ({ ...prev, [secId]: true }));
    setActiveSection(secId);
    setTimeout(() => {
      handleMenuClick(secId);
    }, 40);
  };

  // Complete application submission
  const handleFinalSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSubmitting) return;

    setErrorMsg('');
    setFormErrors({});

    const errors = {};

    // Validate every single section (1 through 12)
    for (let sNum = 1; sNum <= 12; sNum++) {
      const sErrors = validateSection(sNum);
      Object.assign(errors, sErrors);
    }

    // Validate URL formats for all URL fields
    const linksToValidate = [
      { field: 'intro_video_link', label: 'Introduction Video Link', example: 'www.youtube.com/watch?v=... or https://drive.google.com/...' },
      { field: 'demo_video_link', label: 'Demo Teaching Video Link', example: 'www.youtube.com/watch?v=... or https://drive.google.com/...' },
      { field: 'class_sample_link', label: 'Recorded Class Sample Link', example: 'www.youtube.com/watch?v=... or https://drive.google.com/...' },
      { field: 'resume_drive_link', label: 'Resume / CV Google Drive Link', example: 'www.drive.google.com/file/...' },
      { field: 'setup_photo_link', label: 'Setup Photo Link', example: 'www.drive.google.com/file/...' },
      { field: 'website', label: 'Personal Website', example: 'www.yourwebsite.com' },
      { field: 'instagram', label: 'Instagram Profile Link', example: 'www.instagram.com/yourprofile' },
      { field: 'facebook', label: 'Facebook Profile Link', example: 'www.facebook.com/yourprofile' },
      { field: 'linkedin', label: 'LinkedIn Profile Link', example: 'www.linkedin.com/in/yourprofile' },
      { field: 'youtube_channel', label: 'YouTube Channel Link', example: 'www.youtube.com/@yourchannel' },
      { field: 'yoga_alliance_link', label: 'Yoga Alliance Profile Link', example: 'www.yogaalliance.org/...' },
      { field: 'government_id_drive_link', label: 'Government ID Google Drive Link', example: 'www.drive.google.com/file/...' },
      { field: 'school_website_link', label: 'School Website Link', example: 'www.yourwebsite.com' },
      { field: 'yoga_alliance_school_link', label: 'Yoga Alliance School Link', example: 'www.yogaalliance.org/...' },
      { field: 'intl_evidence_url', label: 'International Evidence Link', example: 'www.yourwebsite.com' }
    ];

    linksToValidate.forEach(item => {
      const val = formData[item.field];
      if (val && val.trim() !== '' && !isValidWebsiteUrl(val)) {
        errors[item.field] = `Please enter a valid website. Example: ${item.example}`;
      }
    });

    if (!formData.decl_boundaries || !formData.decl_privacy || !formData.decl_policies || !formData.decl_accuracy) {
      errors.declarations = "You must confirm all professional and code-of-conduct declarations before submitting your application.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setErrorMsg("Please resolve all validation errors highlighted in the form sections.");
      focusFirstErrorField(errors);
      return;
    }

    setIsSubmitting(true);

    const formattedMobile = mobileCountryCode + formData.mobile.replace(/[^\d]/g, '');
    const formattedWhatsapp = formattedMobile;

    const daysArr = Object.keys(formData.weekly_schedule).filter(day => formData.weekly_schedule[day].available);
    const slotsArr = [];
    Object.keys(formData.weekly_schedule).forEach(day => {
      if (formData.weekly_schedule[day].available) {
        const daySlots = formData.weekly_schedule[day].slots.map(s => `${s.from}-${s.to}`).join(', ');
        slotsArr.push(`${day}: ${daySlots || 'No slots'}`);
      }
    });

    const selectedAcademyCourseObjects = adminCourses.filter(c => formData.selected_course_ids.includes(c.id));
    const selectedCourseTitles = selectedAcademyCourseObjects.map(c => c.title || c.name);
    const customCourseTitles = formData.custom_courses.map(c => c.course_name);
    const allCourseTitles = [...selectedCourseTitles, ...customCourseTitles];

    const payload = {
      full_name: formData.full_name.trim(),
      email: formData.email.trim(),
      mobile: formattedMobile,
      whatsapp: formattedWhatsapp,
      country: formData.country.trim(),
      state: formData.state.trim(),
      city: formData.city.trim(),
      current_location: formData.current_location.trim(),
      preferred_language: formData.languages.trim(),
      time_zone: formData.time_zone.trim(),
      address: '',
      designation: formData.designation.trim(),
      total_experience_years: formData.total_experience_years.trim(),
      total_training_hours: parseInt(formData.total_teaching_hours) || null,
      students_trained: formData.students_trained.trim() || 'None',
      certification_badge: formData.certifications.length > 0 ? formData.certifications[0].name : 'Certified Teacher',
      about: formData.about.trim(),
      professional_bio: formData.about.trim(),
      student_types_taught: formData.types_students_taught || 'All',
      courses_can_teach: allCourseTitles.join(', ') || 'General Yoga',
      
      courses_you_can_teach: {
        selected_course_ids: formData.selected_course_ids.filter(id => id !== 'OTHER'),
        has_other_unlisted: formData.selected_course_ids.includes('OTHER'),
        academy_courses: selectedAcademyCourseObjects.map(c => ({
          id: c.id,
          title: c.title || c.name,
          code: c.code || '',
          duration: c.duration || ''
        })),
        custom_courses: formData.custom_courses,
        teaching_formats: formData.teaching_formats,
        previously_taught_choice: formData.previously_taught_choice,
        previous_course_experiences: formData.previous_course_experiences,
        previously_taught_courses: formData.previously_taught_course_ids,
        prev_batches_taught: formData.prev_batches_taught,
        prev_students_taught: formData.prev_students_taught,
        prev_recent_year: formData.prev_recent_year,
        prev_teaching_desc: formData.prev_teaching_desc
      },
      previously_taught_choice: formData.previously_taught_choice,
      previous_course_experiences: formData.previous_course_experiences,

      owns_or_represents_school: formData.owns_or_represents_school,
      partner_school_name: formData.partner_school_name,
      yoga_alliance_school_link: normalizeWebsiteUrl(formData.yoga_alliance_school_link) || '',
      yoga_alliance_school_id: formData.yoga_alliance_school_id,
      school_website_link: normalizeWebsiteUrl(formData.school_website_link) || '',
      school_address: formData.school_address,
      certificate_types_can_issue: formData.certificate_types_can_issue,
      authorized_person_name: formData.authorized_person_name,
      authorized_person_contact: formData.authorized_person_contact,
      certifications: formData.certifications,
      experience_timeline: formData.experience_timeline,
      expertise: formData.expertise,
      philosophy: [formData.teaching_style || 'Traditional Yoga Methodologies'],
      available_days: daysArr,
      available_time_slots: slotsArr,
      demo_available: formData.demo_available,
      morning_availability: formData.avail_morning,
      evening_availability: formData.avail_evening,
      other_time_slots: JSON.stringify(formData.weekly_schedule),
      resume_drive_link: normalizeWebsiteUrl(formData.resume_drive_link) || '',
      demo_video_link: normalizeWebsiteUrl(formData.demo_video_link) || '',
      government_id_drive_link: normalizeWebsiteUrl(formData.government_id_drive_link) || '',
      social_links: {
        website: normalizeWebsiteUrl(formData.website) || '',
        instagram: normalizeWebsiteUrl(formData.instagram) || '',
        facebook: normalizeWebsiteUrl(formData.facebook) || '',
        linkedin: normalizeWebsiteUrl(formData.linkedin) || '',
        youtube_channel: normalizeWebsiteUrl(formData.youtube_channel) || '',
        yoga_alliance: normalizeWebsiteUrl(formData.yoga_alliance_link) || ''
      },
      reference_name: formData.ref_name ? formData.ref_name.trim() : (formData.references.length > 0 ? formData.references[0].name : ''),
      reference_contact: formData.ref_email ? `${formData.ref_email.trim()} / ${formData.ref_mobile || ''}` : (formData.references.length > 0 ? `${formData.references[0].email} / ${formData.references[0].mobile}` : ''),
      reference_organisation: formData.ref_org ? formData.ref_org.trim() : '',
      hear_about_us: formData.hear_about_us || '',
      hear_about_us_other: formData.hear_about_us_other || '',
      teaching_style_approach: formData.teaching_style_approach ? formData.teaching_style_approach.trim() : '',
      professional_summary: formData.professional_summary ? formData.professional_summary.trim() : '',
      why_join_pranaflow: formData.why_join_pranaflow ? formData.why_join_pranaflow.trim() : '',
      what_makes_me_exceptional: formData.what_makes_me_exceptional ? formData.what_makes_me_exceptional.trim() : '',
      // Backward-compat aliases sent to backend DB columns
      why_partner_with_us: formData.why_join_pranaflow ? formData.why_join_pranaflow.trim() : (formData.motivation_statement ? formData.motivation_statement.trim() : ''),
      why_good_teacher: formData.what_makes_me_exceptional ? formData.what_makes_me_exceptional.trim() : (formData.teaching_strengths ? formData.teaching_strengths.trim() : ''),
      motivation_statement: formData.motivation_statement ? formData.motivation_statement.trim() : '',
      teaching_strengths: formData.teaching_strengths ? formData.teaching_strengths.trim() : '',
      declaration_accepted: 1,
      consent_accepted: 1,
      one_to_one_teaching_hours: formData.one_to_one_teaching_hours ? formData.one_to_one_teaching_hours.trim() : '',
      teaching_style: formData.teaching_style ? formData.teaching_style.trim() : '',
      certs_confirmed: formData.certs_confirmed ? 1 : 0,
      custom_specialization: formData.custom_specialization ? formData.custom_specialization.trim() : '',
      intro_video_link: normalizeWebsiteUrl(formData.intro_video_link) || '',
      class_sample_link: normalizeWebsiteUrl(formData.class_sample_link) || '',
      setup_photo_link: normalizeWebsiteUrl(formData.setup_photo_link) || '',
      website: normalizeWebsiteUrl(formData.website) || '',
      instagram: normalizeWebsiteUrl(formData.instagram) || '',
      facebook: normalizeWebsiteUrl(formData.facebook) || '',
      linkedin: normalizeWebsiteUrl(formData.linkedin) || '',
      youtube_channel: normalizeWebsiteUrl(formData.youtube_channel) || '',
      yoga_alliance_link: normalizeWebsiteUrl(formData.yoga_alliance_link) || '',
      intl_evidence_url: normalizeWebsiteUrl(formData.intl_evidence_url) || '',
      ref_name: formData.ref_name ? formData.ref_name.trim() : '',
      ref_org: formData.ref_org ? formData.ref_org.trim() : '',
      ref_email: formData.ref_email ? formData.ref_email.trim() : '',
      ref_designation: formData.ref_designation ? formData.ref_designation.trim() : '',
      ref_mobile: formData.ref_mobile ? formData.ref_mobile.trim() : '',
      references: formData.references || [],
      declarations: {
        decl_boundaries: Boolean(formData.decl_boundaries),
        decl_privacy: Boolean(formData.decl_privacy),
        decl_policies: Boolean(formData.decl_policies),
        decl_accuracy: Boolean(formData.decl_accuracy)
      },
      teaching_journey: (Array.isArray(formData.teaching_journey) ? formData.teaching_journey : []).filter(j => j && (j.role_title || j.organisation)),

      // International Teaching Experience fields
      intl_has_experience: formData.intl_has_experience,
      intl_taught_outside_home: formData.intl_taught_outside_home,
      intl_taught_online_intl: formData.intl_taught_online_intl,
      intl_teaching_mode: formData.intl_teaching_mode,
      intl_courses_taught: formData.intl_courses,
      intl_student_count: formData.intl_student_count,
      intl_teaching_hours: formData.intl_teaching_hours,
      intl_organisations: formData.intl_organisations,
      intl_description: formData.intl_description,
      intl_countries_taught: formData.intl_countries_taught,
      intl_student_countries: formData.intl_student_countries,

      // Domestic Teaching Experience – India fields
      domestic_has_experience: formData.domestic_has_experience,
      domestic_teaching_mode: formData.domestic_teaching_mode,
      domestic_courses_taught: formData.domestic_courses_taught,
      domestic_taught_online: formData.domestic_taught_online,
      domestic_online_student_count: formData.domestic_online_student_count,
      domestic_taught_in_person: formData.domestic_taught_in_person,
      domestic_in_person_student_count: formData.domestic_in_person_student_count,
      domestic_total_student_count: formData.domestic_total_student_count,
      domestic_teaching_hours: formData.domestic_teaching_hours,
      domestic_organisations: formData.domestic_organisations,
      domestic_description: formData.domestic_description,
      domestic_experience: formData.domestic_has_experience === 'Yes' ? {
        has_experience: true,
        teaching_mode: formData.domestic_teaching_mode,
        courses_taught: formData.domestic_courses_taught,
        taught_online: formData.domestic_taught_online === 'Yes',
        online_student_count: parseInt(formData.domestic_online_student_count) || 0,
        taught_in_person: formData.domestic_taught_in_person === 'Yes',
        in_person_student_count: parseInt(formData.domestic_in_person_student_count) || 0,
        total_student_count: parseInt(formData.domestic_total_student_count) || 0,
        teaching_hours: parseInt(formData.domestic_teaching_hours) || 0,
        organisations: formData.domestic_organisations,
        description: formData.domestic_description.trim()
      } : { has_experience: false },

      international_experience: formData.intl_has_experience === 'Yes' ? {
        has_experience: true,
        has_international_teaching_experience: true,
        taught_outside_home_country: formData.intl_taught_outside_home === 'Yes',
        has_taught_outside_home_country: formData.intl_taught_outside_home === 'Yes',
        taught_international_students_online: formData.intl_taught_online_intl === 'Yes',
        has_taught_international_students_online: formData.intl_taught_online_intl === 'Yes',
        international_teaching_mode: formData.intl_teaching_mode,
        countries_taught_in: formData.intl_countries_taught,
        international_student_countries: formData.intl_student_countries,
        international_courses_taught: formData.intl_courses,
        international_student_count: parseInt(formData.intl_student_count) || 0,
        international_teaching_hours: parseInt(formData.intl_teaching_hours) || 0,
        international_organisations: formData.intl_organisations,
        international_experience_description: formData.intl_description.trim(),
        international_evidence_url: formData.intl_evidence_url.trim()
      } : { has_experience: false, has_international_teaching_experience: false }
    };

    // Validate section 5 before final submission
    const sec5Errors = validateSection(5);
    if (Object.keys(sec5Errors).length > 0) {
      Object.assign(errors, sec5Errors);
    }

    try {
      const res = await submitTeacherApplication(payload);
      if (res && res.success) {
        setSuccessData({
          ref_no: `PF-APP-2026-${res.application_id || res.id || Math.floor(1000 + Math.random() * 9000)}`,
          date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
          email_sent: res.email_sent !== false,
          custom_message: res.message || null
        });
        setIsSubmitted(true);
        localStorage.removeItem('pranaflow_draft_email');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setErrorMsg(res.message || "Failed to submit application. Please review details.");
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      const backendErrors = err.errors || {};
      if (Object.keys(backendErrors).length > 0) {
        setFormErrors(prev => ({ ...prev, ...backendErrors }));
        const firstErr = Object.values(backendErrors)[0];
        setErrorMsg(err.message || firstErr || "Please resolve all validation errors highlighted in the form sections.");
        focusFirstErrorField(backendErrors);
      } else {
        setErrorMsg(err.message || "A network or server error occurred. Please try again.");
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="apply-outer-container">
      {/* Complete CSS styling system matching reference design */}
      <style>{`
        /* ============================================================
           GLOBAL BASE
           ============================================================ */
        .apply-outer-container {
          background-color: #f4f7f5;
          color: #172b26;
          min-height: 100vh;
          font-family: var(--body-font-family);
          box-sizing: border-box;
          font-size: 15px;
        }
        .apply-outer-container *, .apply-outer-container *::before, .apply-outer-container *::after {
          box-sizing: border-box;
        }
        .apply-input-error,
        .apply-input.apply-input-error,
        .apply-select.apply-input-error,
        .apply-textarea.apply-input-error,
        .phone-field.apply-input-error,
        .country-phone-wrapper.apply-input-error,
        .apply-decl-panel.apply-input-error,
        .apply-tag-container.apply-input-error {
          border-color: #dc2626 !important;
          background-color: #fffafa !important;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15) !important;
        }
        .apply-error-text {
          color: #dc2626 !important;
          font-size: 12.5px !important;
          font-weight: 600 !important;
          margin-top: 5px !important;
          display: flex !important;
          align-items: center !important;
          gap: 4px !important;
          line-height: 1.4 !important;
        }
        .apply-nav-item.has-error .apply-nav-icon-circle {
          background: #dc2626 !important;
          color: #fff !important;
        }
        .apply-nav-item.has-error .apply-nav-label {
          color: #dc2626 !important;
        }

        /* ============================================================
           PAGE WRAPPER
           ============================================================ */
        .apply-main-wrapper {
          max-width: 1400px;
          width: calc(100% - 32px);
          margin: 0 auto;
          padding: 96px 0 40px 0;
        }

        @media (max-width: 991px) {
          .apply-main-wrapper {
            padding-top: 86px;
          }
        }

        @media (max-width: 767px) {
          .apply-main-wrapper {
            padding-top: 80px;
            width: calc(100% - 24px);
          }
        }

        /* Slim page top header bar */
        .apply-page-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 0 12px 0;
        }
        .apply-page-topbar-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .apply-page-logo-text {
          font-size: 16px;
          font-weight: 800;
          color: #172b26;
        }
        .apply-page-logo-sub {
          font-size: 10px;
          color: #7a8580;
        }
        .apply-page-heading {
          text-align: center;
        }
        .apply-page-heading h1 {
          margin: 0;
          font-size: 19px;
          font-weight: 700;
          color: #172b26;
        }
        .apply-page-heading p {
          margin: 2px 0 0 0;
          font-size: 11.5px;
          color: #7a8580;
        }

        /* ============================================================
           PRANAFLOW THEME VARIABLES
           ============================================================ */
        .apply-outer-container {
          --prana-primary: #0c4f47;
          --prana-primary-dark: #073d37;
          --prana-primary-soft: #edf5f2;
          --prana-accent: #d78b2d;
          --prana-border: #d5e1dc;
          --prana-text: #203832;
          --prana-muted: #71817c;
        }

        /* Course Options Menu Scrollbar */
        .course-options-menu::-webkit-scrollbar {
          width: 7px;
        }
        .course-options-menu::-webkit-scrollbar-track {
          background: #f1f5f3;
          border-radius: 8px;
        }
        .course-options-menu::-webkit-scrollbar-thumb {
          background: #7fa69a;
          border-radius: 999px;
        }
        .course-options-menu::-webkit-scrollbar-thumb:hover {
          background: #0c4f47;
        }

        /* Course Section Premium Styles */
        .course-section .form-label {
          margin-bottom: 8px;
          color: var(--prana-text);
          font-size: 14px;
          font-weight: 600;
        }

        .course-section .form-control,
        .course-section .form-select,
        .course-section .multi-select-control {
          min-height: 48px;
          padding: 12px 15px;
          border: 1px solid #d5e1dc;
          border-radius: 11px;
          background: linear-gradient(180deg, #ffffff 0%, #fbfdfc 100%);
          color: #203832;
          font-size: 15px;
          box-shadow:
            0 1px 2px rgba(12, 79, 71, 0.03),
            0 5px 15px rgba(12, 79, 71, 0.035);
        }

        .course-section .form-control:focus,
        .course-section .form-select:focus,
        .course-section .multi-select-control:focus-within {
          border-color: var(--prana-primary);
          outline: none;
          box-shadow:
            0 0 0 4px rgba(12, 79, 71, 0.10),
            0 7px 20px rgba(12, 79, 71, 0.07);
        }

        /* Progress Card top-right */
        .apply-progress-card {
          background: var(--prana-primary-soft);
          border: 1px solid var(--prana-border);
          border-radius: 8px;
          padding: 8px 14px;
          min-width: 160px;
        }
        .apply-progress-label {
          font-size: 10.5px;
          font-weight: 600;
          color: var(--prana-primary);
          margin-bottom: 5px;
        }
        .apply-progress-track {
          width: 100%;
          height: 6px;
          background: #c8dbd8;
          border-radius: 3px;
          overflow: hidden;
        }
        .apply-progress-fill {
          height: 100%;
          background: var(--prana-primary);
          border-radius: 3px;
          transition: width 0.4s ease;
        }

        /* Course Selectable Pill Buttons UI */
        .course-pill-list {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 10px;
          width: 100%;
        }

        .course-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          min-height: 44px;
          padding: 10px 18px;
          border: 1px solid #cfded8;
          border-radius: 10px;
          background: #ffffff;
          color: var(--prana-text);
          font-size: 14px;
          font-weight: 650;
          line-height: 1.25;
          cursor: pointer;
          transition:
            border-color 0.2s ease,
            background-color 0.2s ease,
            color 0.2s ease,
            box-shadow 0.2s ease,
            transform 0.2s ease;
          font-family: inherit;
        }

        .course-pill:hover {
          border-color: var(--prana-primary);
          background: var(--prana-primary-soft);
          color: var(--prana-primary);
          box-shadow: 0 4px 12px rgba(12, 79, 71, 0.08);
          transform: translateY(-1px);
        }

        .course-pill.is-selected {
          border-color: var(--prana-primary);
          background: var(--prana-primary);
          color: #ffffff;
          box-shadow: 0 5px 14px rgba(12, 79, 71, 0.18);
          transform: none;
        }

        .course-pill-other {
          background: #fffaf1;
          border-color: #e2d5c3;
          color: #0c4f47;
          font-weight: 700;
        }

        .course-pill-other:hover {
          background: #f5ece0;
          border-color: var(--prana-primary);
          color: var(--prana-primary);
        }

        .course-pill-other.is-selected {
          border-color: var(--prana-primary);
          background: var(--prana-primary);
          color: #ffffff;
        }

        @media (max-width: 600px) {
          .course-pill-list {
            gap: 8px;
          }
          .course-pill {
            flex: 1 1 100%;
            justify-content: flex-start;
            text-align: left;
          }
        }

        /* ============================================================
           APPLICATION BODY: SIDEBAR + CONTENT
           ============================================================ */
        .apply-app-body {
          display: flex;
          align-items: flex-start;
          gap: 24px;
          min-height: calc(100vh - 160px);
          background: #f4f7f5;
        }

        /* -- LEFT SIDEBAR -- */
        .apply-sidebar-col {
          width: 280px;
          min-width: 280px;
          max-width: 280px;
          flex: 0 0 280px;
          flex-shrink: 0;
          background: #ffffff;
          border: 1px solid #e2e8e5;
          border-radius: 10px;
          overflow-y: auto;
          padding: 18px 14px 24px 14px;
          display: flex;
          flex-direction: column;
          gap: 0;
          position: sticky;
          top: 96px;
          align-self: flex-start;
          max-height: calc(100vh - 116px);
        }

        .apply-nav-title {
          font-size: 11px;
          text-transform: uppercase;
          font-weight: 700;
          color: #8fa39b;
          letter-spacing: 1.2px;
          margin-bottom: 12px;
          padding: 0 4px;
          white-space: nowrap;
        }

        .apply-nav-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-bottom: 20px;
        }

        .apply-nav-item {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 44px;
          padding: 8px 12px;
          border-radius: 8px;
          background: none;
          border: 1px solid transparent;
          color: #3b4d47;
          font-size: 13.5px;
          font-weight: 550;
          text-align: left;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
          font-family: inherit;
          line-height: 1.35;
          width: 100%;
          white-space: normal;
          word-break: normal;
          overflow-wrap: normal;
        }
        .apply-nav-item:hover {
          background: var(--prana-primary-soft);
          color: var(--prana-primary);
        }
        .apply-nav-item.active {
          background: var(--prana-primary-soft);
          border-color: rgba(12, 79, 71, 0.3);
          color: var(--prana-primary);
          font-weight: 700;
          border-left: 3px solid var(--prana-accent);
        }

        .apply-nav-label {
          flex: 1;
          min-width: 0;
          white-space: normal;
          word-break: normal;
          overflow-wrap: normal;
          line-height: 1.35;
        }

        .apply-nav-icon-circle {
          width: 24px;
          min-width: 24px;
          height: 24px;
          flex: 0 0 24px;
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          border: 1px solid rgba(12, 79, 71, 0.35);
          color: var(--prana-primary);
          font-size: 11.5px;
          font-weight: 700;
          white-space: nowrap;
        }
        .apply-nav-item.active .apply-nav-icon-circle {
          background: var(--prana-primary);
          border-color: var(--prana-primary);
          color: #fff;
        }
        .apply-nav-icon-circle svg {
          width: 12px;
          height: 12px;
        }

        /* Need Help sidebar card */
        .apply-sidebar-help-card {
          background: #fff;
          border: 1px solid #e2e8e5;
          border-radius: 8px;
          padding: 13px 14px;
          margin-top: 6px;
        }
        .apply-sidebar-help-title {
          font-size: 13.5px;
          font-weight: 700;
          color: #172b26;
          margin: 0 0 5px 0;
        }
        .apply-sidebar-help-text {
          font-size: 12.5px;
          color: #64748b;
          margin: 0 0 10px 0;
          line-height: 1.45;
        }
        .apply-sidebar-help-row {
          display: flex;
          align-items: flex-start;
          gap: 7px;
          font-size: 12.5px;
          color: #172b26;
          margin-top: 6px;
          font-weight: 500;
          word-break: break-all;
          overflow-wrap: anywhere;
        }
        .apply-sidebar-help-row svg {
          width: 15px;
          height: 15px;
          color: var(--prana-primary);
          flex-shrink: 0;
          margin-top: 2px;
        }
        .apply-sidebar-help-email {
          color: #0c4f47;
          word-break: break-all;
          overflow-wrap: anywhere;
          text-decoration: none;
        }
        .apply-sidebar-help-email:hover {
          text-decoration: underline;
        }
        .apply-sidebar-whatsapp-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          margin-top: 12px;
          padding: 9px 12px;
          background: #25d366;
          color: #ffffff !important;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 650;
          text-decoration: none !important;
          border: none;
          cursor: pointer;
          transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
          box-shadow: 0 3px 10px rgba(37, 211, 102, 0.2);
        }
        .apply-sidebar-whatsapp-btn:hover {
          background: #1eb855;
          color: #ffffff !important;
          transform: translateY(-1px);
          box-shadow: 0 5px 14px rgba(37, 211, 102, 0.32);
        }
        .apply-sidebar-whatsapp-btn svg {
          width: 16px;
          height: 16px;
          fill: currentColor;
          flex-shrink: 0;
        }

        /* Important Note sidebar card */
        .apply-sidebar-note-card {
          background: var(--prana-primary-soft);
          border: 1px solid rgba(12, 79, 71, 0.25);
          border-radius: 8px;
          padding: 13px 14px;
          margin-top: 10px;
        }
        .apply-sidebar-note-title {
          font-size: 13.5px;
          font-weight: 700;
          color: var(--prana-primary);
          margin: 0 0 5px 0;
        }
        .apply-sidebar-note-text {
          font-size: 12.5px;
          color: var(--prana-text);
          line-height: 1.45;
          margin: 0;
        }

        /* -- RIGHT CONTENT AREA -- */
        .apply-form-column {
          flex: 1;
          min-width: 0;
          padding: 0 0 0 16px;
        }

        /* Required note line above grid */
        .apply-required-note {
          font-size: 10.5px;
          color: #7a8580;
          text-align: right;
          margin-bottom: 10px;
        }
        .apply-required-note span {
          color: #dc2626;
        }

        /* ============================================================
           SECTION ACCORDION LIST (single column)
           ============================================================ */
        .apply-sections-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
          width: 100%;
        }

        /* Keep these for backward compat but both span full width */
        .section-full  { width: 100%; }
        .section-half  { width: 100%; }

        /* -- SECTION CARD / ACCORDION -- */
        .apply-card {
          width: 100%;
          background: #ffffff;
          border: 1px solid var(--prana-border);
          border-radius: 10px;
          overflow: hidden;
          min-width: 0;
          margin: 0;
          padding: 0;
          box-shadow: 0 3px 12px rgba(12, 79, 71, 0.04);
          transition:
            border-color 0.2s ease,
            box-shadow 0.2s ease,
            background-color 0.2s ease;
        }
        .apply-card:hover {
          border-color: rgba(12, 79, 71, 0.35);
          box-shadow: 0 6px 18px rgba(12, 79, 71, 0.07);
        }
        .apply-card.collapsed {
          cursor: pointer;
        }
        .apply-card.collapsed:hover {
          background-color: var(--prana-primary-soft);
        }

        /* Section Card Header Button */
        button.apply-section-title-row,
        .apply-section-title-row {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-height: 60px;
          padding: 14px 18px;
          border: none;
          border-bottom: 1px solid var(--prana-border);
          cursor: pointer;
          user-select: none;
          gap: 12px;
          background: #ffffff;
          font-family: inherit;
          font-size: inherit;
          text-align: left;
          color: inherit;
          outline: none;
          margin: 0;
        }
        .apply-card.collapsed button.apply-section-title-row,
        .apply-card.collapsed .apply-section-title-row {
          border-bottom: none;
        }
        /* Open accordion header gets soft teal bg */
        .apply-card:not(.collapsed) button.apply-section-title-row,
        .apply-card:not(.collapsed) .apply-section-title-row {
          background: var(--prana-primary-soft);
        }
        button.apply-section-title-row:focus-visible {
          outline: 2px solid var(--prana-primary);
          outline-offset: -2px;
        }
        .apply-section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
          min-width: 0;
        }
        .apply-section-badge {
          width: 31px;
          height: 31px;
          flex-shrink: 0;
          border-radius: 50%;
          background: var(--prana-primary);
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 700;
          transition: box-shadow 0.2s ease;
        }
        /* Gold ring on open section badge */
        .apply-card:not(.collapsed) .apply-section-badge {
          box-shadow: 0 0 0 3px rgba(215, 139, 45, 0.22);
        }
        .apply-section-title {
          font-size: 17px;
          font-weight: 700;
          color: var(--prana-text);
          margin: 0;
          flex: 1;
        }
        .apply-section-subtitle {
          font-size: 12px;
          color: var(--prana-muted);
          font-weight: 400;
          display: block;
          margin-top: 1px;
        }
        .apply-section-toggle-btn {
          background: none;
          border: none;
          color: var(--prana-muted);
          cursor: pointer;
          display: flex;
          align-items: center;
          padding: 3px;
          border-radius: 50%;
          flex-shrink: 0;
          transition: color 0.2s ease;
        }
        .apply-card:not(.collapsed) .apply-section-toggle-btn {
          color: var(--prana-primary);
        }
        .apply-section-toggle-btn:hover {
          color: var(--prana-primary);
          background: rgba(12, 79, 71, 0.08);
        }
        /* Chevron rotation handled via class toggle in JSX, keep transform here */

        /* Section Add Button (right side of header) */
        .apply-section-add-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 5px 12px;
          background: var(--prana-primary);
          color: #fff;
          border: 1px solid var(--prana-primary);
          border-radius: 7px;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          flex-shrink: 0;
          transition: background 0.2s ease, box-shadow 0.2s ease;
        }
        .apply-section-add-btn:hover {
          background: var(--prana-primary-dark);
          border-color: var(--prana-primary-dark);
          box-shadow: 0 4px 10px rgba(12, 79, 71, 0.18);
        }

        /* Section Card Body */
        .apply-section-content {
          padding: 15px 15px 16px 15px;
        }

        /* ============================================================
           DRIVE INFO BOX (blue)
           ============================================================ */
        .apply-drive-info-box {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 9px 12px;
          margin-bottom: 13px;
          border: 1px solid #c8dcf7;
          border-radius: 5px;
          background: #eef6ff;
          color: #3469a5;
          font-size: 10.5px;
          line-height: 1.5;
        }
        .apply-drive-info-box svg {
          width: 14px;
          height: 14px;
          flex-shrink: 0;
          margin-top: 1px;
          color: #3469a5;
        }

        /* ============================================================
           FORM FIELD GRID
           ============================================================ */
        .apply-form-grid {
          display: grid;
          grid-template-columns: repeat(12, minmax(0, 1fr));
          gap: 12px 14px;
        }
        /* legacy grids for sections not yet converted */
        .apply-grid-3 {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px 14px;
        }
        .apply-grid-2 {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px 14px;
        }
        .apply-grid-4 {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px 14px;
        }
        .apply-row-flex {
          display: grid;
          grid-template-columns: 1fr 1fr 1.5fr;
          gap: 12px 14px;
          margin-top: 12px;
        }
        .apply-profile-grid {
          display: grid;
          grid-template-columns: 1fr 1.5fr;
          gap: 12px 14px;
          margin-top: 12px;
        }
        .apply-grid-mt {
          margin-top: 12px;
        }

        /* span helpers inside form-grid */
        .fg-12 { grid-column: span 12; }
        .fg-8  { grid-column: span 8;  }
        .fg-6  { grid-column: span 6;  }
        .fg-4  { grid-column: span 4;  }
        .fg-3  { grid-column: span 3;  }

        /* -- FIELD WRAPPER -- */
        .apply-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
          min-width: 0;
        }

        /* -- LABEL -- */
        .apply-label {
          display: flex;
          align-items: center;
          gap: 5px;
          margin-bottom: 7px;
          color: #263b33;
          font-size: 13.5px;
          line-height: 1.4;
          font-weight: 650;
          letter-spacing: 0.01em;
        }
        .apply-required-star {
          color: #d84b4b;
          font-weight: 700;
        }
        .apply-field-hint {
          display: flex;
          align-items: flex-start;
          gap: 5px;
          margin-top: 6px;
          color: #718079;
          font-size: 12px;
          line-height: 1.5;
        }

        /* -- INPUTS -- */
        .apply-input,
        .apply-select,
        .premium-input {
          width: 100%;
          min-height: 46px;
          padding: 11px 14px;
          border: 1px solid #d7e2dc;
          border-radius: 10px;
          background: linear-gradient(180deg, #ffffff 0%, #fbfdfc 100%);
          color: #1f332c;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.4;
          box-shadow:
            0 1px 2px rgba(15, 70, 52, 0.03),
            0 4px 12px rgba(15, 70, 52, 0.025);
          transition:
            border-color 0.2s ease,
            box-shadow 0.2s ease,
            background-color 0.2s ease,
            transform 0.2s ease;
          outline: none;
        }
        .apply-input::placeholder,
        .premium-input::placeholder,
        textarea::placeholder,
        .apply-tag-placeholder {
          color: #94a39c;
          font-size: 13.5px;
          font-weight: 400;
          opacity: 1;
        }
        .apply-input:hover,
        .apply-select:hover,
        .premium-input:hover {
          border-color: #a9c9b8;
          box-shadow:
            0 2px 4px rgba(15, 70, 52, 0.04),
            0 7px 18px rgba(15, 70, 52, 0.05);
        }
        .apply-input:focus,
        .apply-select:focus,
        .premium-input:focus,
        .premium-field:focus-within {
          border-color: var(--prana-primary);
          background: #ffffff;
          outline: none;
          box-shadow:
            0 0 0 4px rgba(12, 79, 71, 0.10),
            0 6px 18px rgba(12, 79, 71, 0.06);
        }

        .apply-select {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          padding-right: 40px !important;
          background-image: none !important;
          cursor: pointer;
        }

        textarea.apply-textarea, .apply-textarea {
          width: 100%;
          min-height: 105px;
          padding: 13px 15px 30px;
          border: 1px solid #d7e2dc;
          border-radius: 12px;
          background: linear-gradient(180deg, #ffffff 0%, #fbfdfc 100%);
          color: #1f332c;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.6;
          box-shadow:
            0 1px 2px rgba(15, 70, 52, 0.03),
            0 4px 12px rgba(15, 70, 52, 0.025);
          transition: all 0.2s ease;
          resize: vertical;
          outline: none;
        }
        textarea.apply-textarea:hover, .apply-textarea:hover {
          border-color: #a9c9b8;
          box-shadow:
            0 2px 4px rgba(15, 70, 52, 0.04),
            0 7px 18px rgba(15, 70, 52, 0.05);
        }
        textarea.apply-textarea:focus, .apply-textarea:focus {
          border-color: var(--prana-primary);
          background: #ffffff;
          outline: none;
          box-shadow:
            0 0 0 4px rgba(12, 79, 71, 0.10),
            0 6px 18px rgba(12, 79, 71, 0.06);
        }

        .apply-error-text {
          margin-top: 5px;
          color: #c84444;
          font-size: 10.5px;
          font-weight: 500;
          display: block;
        }

        /* -- CHECKBOX / RADIO -- */
        .apply-checkbox-input,
        .apply-avail-checkbox,
        .premium-checkbox {
          width: 17px;
          height: 17px;
          accent-color: var(--prana-primary);
          flex-shrink: 0;
          cursor: pointer;
          border-radius: 4px;
        }
        .apply-checkbox-label {
          font-size: 13.5px;
          color: #263b33;
          cursor: pointer;
          line-height: 1.4;
        }
        .apply-radio-group {
          display: flex;
          gap: 20px;
          align-items: center;
          flex-wrap: wrap;
        }
        .apply-radio-label {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 13.5px;
          color: #263b33;
          cursor: pointer;
        }
        .apply-radio-label input[type='radio'] {
          accent-color: var(--prana-primary);
          width: 17px;
          height: 17px;
          cursor: pointer;
        }

        /* ============================================================
           TAG INPUT
           ============================================================ */
        .apply-tag-container {
          position: relative;
          border: 1px solid #d7e2dc;
          border-radius: 10px;
          padding: 8px 12px;
          background: linear-gradient(180deg, #ffffff 0%, #fbfdfc 100%);
          min-height: 46px;
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          align-items: center;
          cursor: pointer;
          box-shadow:
            0 1px 2px rgba(15, 70, 52, 0.03),
            0 4px 12px rgba(15, 70, 52, 0.025);
          transition: all 0.2s ease;
          width: 100%;
        }
        .apply-tag-container:hover {
          border-color: #a9c9b8;
          box-shadow:
            0 2px 4px rgba(15, 70, 52, 0.04),
            0 7px 18px rgba(15, 70, 52, 0.05);
        }
        .apply-tag-container:focus-within {
          border-color: var(--prana-primary);
          background: #ffffff;
          box-shadow:
            0 0 0 4px rgba(12, 79, 71, 0.10),
            0 6px 18px rgba(12, 79, 71, 0.06);
        }
        .apply-tag-placeholder { color: #94a39c; font-size: 12px; padding-left: 4px; }
        .apply-tag {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-height: 27px;
          padding: 4px 9px;
          border: 1px solid #bfe0cc;
          border-radius: 999px;
          background: #eef8f2;
          color: #176b3e;
          font-size: 11px;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        .apply-tag-remove {
          border: none;
          background: none;
          color: #8aa198;
          cursor: pointer;
          font-size: 14px;
          font-weight: 700;
          padding: 0;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
        }
        .apply-tag-remove:hover {
          color: #d84b4b;
        }
        .apply-tag-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          width: 100%;
          background: #ffffff;
          border: 1px solid #d7e2dc;
          border-radius: 10px;
          box-shadow: 0 10px 25px rgba(15, 70, 52, 0.08);
          z-index: 300;
          max-height: 180px;
          overflow-y: auto;
        }
        .apply-dropdown-item {
          padding: 7px 11px;
          font-size: 12px;
          color: #172b26;
          cursor: pointer;
        }
        .apply-dropdown-item:hover { background: #f4faf6; }
        .apply-dropdown-item.selected { background: #eef9f1; color: #196638; font-weight: 600; }

        /* ============================================================
           CHECKBOX GRID (Teaching Expertise)
           ============================================================ */
        .apply-checkbox-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 9px 12px;
        }
        .apply-checkbox-item {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
        }
        .apply-checkbox-item label {
          font-size: 13.5px;
          color: #263b33;
          font-weight: 500;
          cursor: pointer;
          line-height: 1.35;
          word-break: break-word;
        }

        /* ============================================================
           AVAILABILITY TABLE
           ============================================================ */
        .apply-avail-table-wrap {
          overflow-x: auto;
        }
        .apply-avail-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13.5px;
          min-width: 360px;
        }
        .apply-avail-table th {
          padding: 10px 10px;
          background: #f6faf8;
          color: #263b33;
          font-size: 13.5px;
          font-weight: 700;
          text-align: left;
          border-bottom: 1px solid #e4e9e6;
          white-space: nowrap;
        }
        .apply-avail-table td {
          padding: 9px 10px;
          border-bottom: 1px solid #edf1ef;
          vertical-align: middle;
          color: #34443e;
          font-size: 13.5px;
        }
        .apply-avail-table td:first-child {
          font-weight: 600;
          font-size: 13.5px;
          min-width: 90px;
        }
        .apply-avail-time-input {
          border: 1px solid #dfe6e2;
          border-radius: 6px;
          padding: 6px 10px;
          font-size: 13.5px;
          color: #24332e;
          background: #fff;
          font-family: inherit;
          outline: none;
          min-width: 96px;
          height: 36px;
        }
        .apply-avail-time-input:focus { border-color: var(--prana-primary); }
        .apply-avail-add-slot {
          font-size: 13px;
          font-weight: 600;
          color: var(--prana-primary);
          border: 1px solid rgba(12, 79, 71, 0.3);
          background: var(--prana-primary-soft);
          border-radius: 6px;
          padding: 5px 10px;
          cursor: pointer;
          font-family: inherit;
          white-space: nowrap;
        }
        .apply-avail-add-slot:hover {
          background: var(--prana-primary);
          color: #fff;
          border-color: var(--prana-primary);
        }
        .apply-avail-remove-slot {
          font-size: 12.5px;
          color: #dc2626;
          border: 1px solid #fca5a5;
          background: #fff;
          border-radius: 6px;
          padding: 4px 8px;
          cursor: pointer;
          font-family: inherit;
        }
        .apply-avail-checkbox {
          width: 15px;
          height: 15px;
          accent-color: var(--prana-primary);
          cursor: pointer;
        }

        /* Section 7 Availability: Desktop baseline & element defaults */
        .apply-avail-mobile-label {
          display: none;
        }
        .apply-avail-check-text {
          display: none;
        }
        .apply-avail-extra-slot-row {
          display: flex;
          gap: 4px;
          align-items: center;
          flex-wrap: wrap;
        }
        .apply-avail-extra-slot-fields {
          display: inline-flex;
          gap: 4px;
          align-items: center;
        }
        .apply-avail-slot-sep {
          font-size: 10px;
          color: #4b6358;
        }

        /* ============================================================
           SECTION 7: AVAILABILITY RESPONSIVE (Tablet & Mobile <= 768px)
           ============================================================ */
        @media (max-width: 768px) {
          #section-availability .apply-section-content {
            padding: 14px 12px !important;
          }

          /* Review Grid in Section 7 */
          #section-availability .apply-review-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
          #section-availability .apply-review-field {
            grid-column: 1 / -1 !important;
          }

          /* Preferred Teaching Formats */
          #section-availability .apply-avail-format-pills {
            gap: 6px !important;
          }
          #section-availability .apply-avail-format-btn {
            padding: 8px 12px !important;
            font-size: 13px !important;
            border-radius: 7px !important;
            min-height: 38px !important;
            line-height: 1.3 !important;
            white-space: normal !important;
            text-align: center !important;
          }

          /* Transform Availability Table to Responsive Cards */
          #section-availability .apply-avail-table-wrap {
            overflow-x: visible !important;
            width: 100% !important;
          }
          #section-availability .apply-avail-table {
            display: block !important;
            width: 100% !important;
            min-width: 0 !important;
            border: none !important;
          }
          #section-availability .apply-avail-table thead {
            display: none !important;
          }
          #section-availability .apply-avail-table tbody {
            display: flex !important;
            flex-direction: column !important;
            gap: 10px !important;
            width: 100% !important;
          }
          #section-availability .apply-avail-table tr.apply-avail-row {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            column-gap: 10px !important;
            row-gap: 8px !important;
            background: #ffffff !important;
            border: 1.5px solid #e1e7e4 !important;
            border-radius: 10px !important;
            padding: 12px 12px !important;
            box-shadow: 0 1px 2px rgba(12, 79, 71, 0.03) !important;
            transition: border-color 0.15s ease, background-color 0.15s ease !important;
          }
          #section-availability .apply-avail-table tr.apply-avail-row.is-available {
            border-color: #b3d1c7 !important;
            background: #fbfdfc !important;
          }
          #section-availability .apply-avail-table tr.apply-avail-row.is-unavailable {
            border-color: #edf2ef !important;
            background: #fafbfb !important;
          }

          /* Row 1, Col 1: Day Name */
          #section-availability .apply-avail-cell-day {
            grid-column: 1 / 2 !important;
            grid-row: 1 !important;
            padding: 0 !important;
            border: none !important;
            display: flex !important;
            align-items: center !important;
            font-size: 15px !important;
            font-weight: 700 !important;
            color: #172b26 !important;
          }
          #section-availability .apply-avail-day-label {
            font-size: 14.5px !important;
            font-weight: 700 !important;
            color: #172b26 !important;
            cursor: pointer !important;
            margin: 0 !important;
          }

          /* Row 1, Col 2: Checkbox & Status Indicator */
          #section-availability .apply-avail-cell-status {
            grid-column: 2 / 3 !important;
            grid-row: 1 !important;
            padding: 0 !important;
            border: none !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
          }
          #section-availability .apply-avail-check-label {
            display: inline-flex !important;
            align-items: center !important;
            gap: 6px !important;
            cursor: pointer !important;
            user-select: none !important;
            padding: 5px 9px !important;
            border-radius: 6px !important;
            background: #f1f5f3 !important;
            border: 1px solid #dbe5e1 !important;
            transition: all 0.15s ease !important;
          }
          #section-availability .apply-avail-row.is-available .apply-avail-check-label {
            background: #e8f4f0 !important;
            border-color: #b7d5cb !important;
          }
          #section-availability .apply-avail-checkbox {
            width: 17px !important;
            height: 17px !important;
            margin: 0 !important;
            accent-color: var(--prana-primary) !important;
            cursor: pointer !important;
          }
          #section-availability .apply-avail-check-text {
            display: inline-block !important;
            font-size: 12px !important;
            font-weight: 600 !important;
          }
          #section-availability .apply-avail-row.is-available .apply-avail-check-text {
            color: #0c4f47 !important;
          }
          #section-availability .apply-avail-row.is-unavailable .apply-avail-check-text {
            color: #72837b !important;
          }

          /* Row 2: Time Slots ("From" & "To") */
          #section-availability .apply-avail-cell-from {
            grid-column: 1 / 2 !important;
            grid-row: 2 !important;
            padding: 0 !important;
            border: none !important;
          }
          #section-availability .apply-avail-cell-to {
            grid-column: 2 / 3 !important;
            grid-row: 2 !important;
            padding: 0 !important;
            border: none !important;
          }
          #section-availability .apply-avail-mobile-label {
            display: block !important;
            font-size: 11.5px !important;
            font-weight: 600 !important;
            color: #556760 !important;
            margin-bottom: 4px !important;
          }
          #section-availability .apply-avail-cell-time .time-field-wrap {
            width: 100% !important;
            max-width: 100% !important;
            min-height: 40px !important;
            height: 40px !important;
          }
          #section-availability .apply-avail-cell-time input.apply-avail-time-input {
            width: 100% !important;
            min-width: 0 !important;
            height: 40px !important;
            min-height: 40px !important;
            font-size: 13px !important;
            padding-left: 30px !important;
            padding-right: 6px !important;
            box-sizing: border-box !important;
          }
          #section-availability .apply-avail-row.is-unavailable .apply-avail-cell-time {
            opacity: 0.55 !important;
          }

          /* Row 3: Slot Actions and Extra Slots */
          #section-availability .apply-avail-cell-slots {
            grid-column: 1 / -1 !important;
            grid-row: 3 !important;
            padding: 0 !important;
            border: none !important;
          }
          #section-availability .apply-avail-add-slot {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 5px !important;
            font-size: 12.5px !important;
            font-weight: 600 !important;
            padding: 6px 12px !important;
            min-height: 36px !important;
            border-radius: 6px !important;
            width: auto !important;
          }
          #section-availability .apply-avail-extra-slots-wrap {
            margin-top: 8px !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 8px !important;
          }
          #section-availability .apply-avail-extra-slot-row {
            display: flex !important;
            align-items: flex-end !important;
            gap: 8px !important;
            width: 100% !important;
            background: #f4f8f6 !important;
            padding: 8px 10px !important;
            border-radius: 8px !important;
            border: 1px dashed #c8dbd4 !important;
            box-sizing: border-box !important;
          }
          #section-availability .apply-avail-extra-slot-fields {
            display: grid !important;
            grid-template-columns: 1fr auto 1fr !important;
            align-items: center !important;
            gap: 6px !important;
            flex: 1 !important;
            min-width: 0 !important;
          }
          #section-availability .apply-avail-extra-slot-time {
            min-width: 0 !important;
          }
          #section-availability .apply-avail-extra-slot-time .time-field-wrap {
            width: 100% !important;
            max-width: 100% !important;
            min-height: 38px !important;
            height: 38px !important;
          }
          #section-availability .apply-avail-extra-slot-time input.apply-avail-time-input {
            width: 100% !important;
            min-width: 0 !important;
            height: 38px !important;
            min-height: 38px !important;
            font-size: 13px !important;
            padding-left: 28px !important;
            padding-right: 6px !important;
            box-sizing: border-box !important;
          }
          #section-availability .apply-avail-slot-sep {
            font-size: 13px !important;
            font-weight: 700 !important;
            color: #72837b !important;
            margin-top: 18px !important;
            text-align: center !important;
          }
          #section-availability .apply-avail-remove-slot {
            width: 38px !important;
            height: 38px !important;
            min-width: 38px !important;
            min-height: 38px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            font-size: 18px !important;
            font-weight: 700 !important;
            border-radius: 8px !important;
            padding: 0 !important;
            cursor: pointer !important;
            flex-shrink: 0 !important;
            color: #dc2626 !important;
            background: #fef2f2 !important;
            border: 1px solid #fecaca !important;
            transition: all 0.15s ease !important;
          }
          #section-availability .apply-avail-remove-slot:hover {
            background: #dc2626 !important;
            color: #ffffff !important;
            border-color: #dc2626 !important;
          }

          /* Demo Classes Radio Group */
          #section-availability .apply-radio-group {
            gap: 16px !important;
          }
          #section-availability .apply-radio-label {
            padding: 6px 10px !important;
            border-radius: 6px !important;
            background: #f7faf9 !important;
            border: 1px solid #e2e8e5 !important;
          }
        }

        @media (max-width: 360px) {
          #section-availability .apply-avail-extra-slot-row {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          #section-availability .apply-avail-remove-slot {
            width: 100% !important;
            height: 32px !important;
            min-height: 32px !important;
            font-size: 13px !important;
          }
          #section-availability .apply-avail-check-label {
            padding: 4px 6px !important;
          }
          #section-availability .apply-avail-check-text {
            font-size: 11px !important;
          }
        }

        /* ============================================================
           INLINE BUILDERS (Add Role, Add Cert, Add Ref)
           ============================================================ */
        .apply-item-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 12px;
        }
        .apply-item-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 10px 12px;
          background: #f8faf9;
          border: 1px solid #e2e8e5;
          border-radius: 6px;
          gap: 8px;
        }
        .apply-item-title {
          font-size: 12px;
          font-weight: 700;
          color: #172b26;
        }
        .apply-item-subtitle {
          font-size: 10.5px;
          color: #7a8580;
          margin-top: 2px;
        }
        .apply-item-actions {
          display: flex;
          gap: 5px;
          flex-shrink: 0;
        }
        .apply-btn-action {
          background: #fff;
          border: 1px solid #e2e8e5;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 10.5px;
          font-weight: 600;
          cursor: pointer;
          color: #172b26;
          display: flex;
          align-items: center;
          gap: 3px;
          font-family: inherit;
        }
        .apply-btn-action:hover { border-color: var(--prana-primary); color: var(--prana-primary); }
        .apply-btn-remove:hover { border-color: #dc2626; color: #dc2626; background: #fef2f2; }
        .apply-inline-builder {
          background: #f8faf9;
          border: 1.5px dashed #c8d8d2;
          border-radius: 7px;
          padding: 14px 14px;
          margin-top: 10px;
        }
        .apply-builder-title {
          font-size: 12px;
          font-weight: 700;
          color: #172b26;
          margin: 0 0 12px 0;
        }

        /* ============================================================
           EXPERIENCE TABLE
           ============================================================ */
        .apply-exp-table-wrap {
          overflow-x: auto;
          margin-bottom: 10px;
        }
        .apply-exp-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10.5px;
          min-width: 360px;
        }
        .apply-exp-table th {
          padding: 8px 7px;
          background: #f6faf8;
          color: #52605b;
          font-size: 9.5px;
          font-weight: 700;
          text-align: left;
          border-bottom: 1px solid #e4e9e6;
          white-space: nowrap;
        }
        .apply-exp-table td {
          padding: 8px 7px;
          color: #34443e;
          border-bottom: 1px solid #edf1ef;
          vertical-align: top;
          font-size: 10.5px;
        }

        /* ============================================================
           DECLARATION PANEL
           ============================================================ */
        .apply-decl-panel {
          background: #f4faf6;
          border: 1px solid #c8dfd0;
          border-radius: 6px;
          padding: 14px 14px;
          margin-top: 14px;
        }
        .apply-decl-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px 20px;
        }
        .apply-decl-item {
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }

        /* ============================================================
           REVIEW SUMMARY
           ============================================================ */
        .apply-review-section {
          background: #f8faf9;
          border: 1px solid #e2e8e5;
          border-radius: 8px;
          padding: 14px 16px;
          margin-bottom: 12px;
        }
        .apply-review-section-title {
          font-size: 14px;
          font-weight: 700;
          color: #172b26;
          margin: 0 0 10px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .apply-review-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 8px 16px;
        }
        .apply-review-field {
          font-size: 13.5px;
          font-weight: 600;
          color: #172b26;
        }
        .apply-review-field span {
          color: #64748b;
          display: block;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 2px;
        }

        /* ============================================================
           ACTION BUTTONS
           ============================================================ */
        .apply-actions-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 0;
          border-top: 1px solid #e2e8e5;
          margin-top: 14px;
          grid-column: span 12;
        }
        .apply-btn {
          height: 40px;
          padding: 0 18px;
          border-radius: 7px;
          font-size: 14px;
          font-weight: 650;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          transition:
            background-color 0.2s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease,
            transform 0.2s ease;
          font-family: inherit;
        }
        .apply-btn-primary {
          background: var(--prana-primary);
          color: #fff;
          border: 1px solid var(--prana-primary);
          min-width: 130px;
          box-shadow: 0 5px 14px rgba(12, 79, 71, 0.16);
        }
        .apply-btn-primary:hover {
          background: var(--prana-primary-dark);
          border-color: var(--prana-primary-dark);
          box-shadow: 0 7px 18px rgba(12, 79, 71, 0.22);
          transform: translateY(-1px);
        }
        .apply-btn-secondary {
          background: #ffffff;
          color: var(--prana-primary);
          border: 1.5px solid var(--prana-primary);
          box-shadow: 0 2px 8px rgba(12, 79, 71, 0.06);
        }
        .apply-btn-secondary:hover {
          background: #f0f8f5;
          border-color: var(--prana-primary-dark);
          color: var(--prana-primary-dark);
          transform: translateY(-1px);
        }
        .apply-btn-secondary {
          background: #fff;
          color: var(--prana-primary);
          border: 1px solid var(--prana-primary);
        }
        .apply-btn-secondary:hover {
          background: var(--prana-primary-soft);
        }
        .apply-btn-draft {
          background: #fff;
          color: var(--prana-text);
          border: 1px solid var(--prana-border);
          min-width: 100px;
        }
        .apply-btn-draft:hover { background: var(--prana-primary-soft); border-color: var(--prana-primary); }
        .apply-btn-sm {
          height: 30px;
          padding: 0 12px;
          font-size: 11px;
        }

        /* ============================================================
           TOP INFO BANNER
           ============================================================ */
        .apply-info-banner {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
          gap: 12px;
          grid-column: span 12;
        }
        .apply-banner-left {
          display: flex;
          gap: 10px;
          align-items: flex-start;
        }
        .apply-banner-icon {
          color: #2563eb;
          flex-shrink: 0;
          margin-top: 2px;
          width: 18px;
          height: 18px;
        }
        .apply-banner-text {
          font-size: 13.5px;
          line-height: 1.5;
          color: #2563eb;
        }
        .apply-banner-bold {
          font-weight: 700;
          display: block;
          margin-bottom: 2px;
          font-size: 13.5px;
        }
        .apply-banner-illustration { flex-shrink: 0; }

        /* Error banner */
        .apply-error-banner {
          background: #fef2f2;
          border: 1px solid #fca5a5;
          border-radius: 7px;
          padding: 10px 14px;
          color: #dc2626;
          font-size: 12px;
          display: flex;
          gap: 7px;
          align-items: center;
          margin-bottom: 14px;
          grid-column: span 12;
        }

        /* ============================================================
           RESPONSIVE BREAKPOINTS
           ============================================================ */
        @media (max-width: 1050px) {
          .apply-app-body { flex-direction: column; }
          .apply-sidebar-col {
            display: none;
          }
          .apply-form-column { padding: 0; }
          /* section-half already full width, no override needed */
          .apply-checkbox-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .apply-grid-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .apply-grid-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .apply-decl-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .apply-sections-grid { gap: 10px; }
          .apply-grid-4, .apply-grid-3, .apply-grid-2, .apply-row-flex, .apply-profile-grid {
            grid-template-columns: 1fr;
          }
          .apply-checkbox-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .apply-decl-grid { grid-template-columns: 1fr; }
          .apply-section-content { padding: 12px 12px; }
          .apply-section-title { font-size: 15px; }
          .apply-label { font-size: 13px; }
          .apply-input, .apply-select, .premium-input, textarea.apply-textarea { font-size: 14px; }
          .apply-nav-item { font-size: 12px; }
          .apply-page-topbar { flex-direction: column; gap: 8px; text-align: center; }
        }

        /* ============================================================
           MOBILE STEPS NAV
           ============================================================ */
        .apply-mobile-steps-nav {
          display: none;
          overflow-x: auto;
          padding: 10px 12px;
          background: #fff;
          border: 1px solid #e2e8e5;
          border-radius: 8px;
          gap: 6px;
          margin-bottom: 14px;
        }
        .apply-mobile-step-pill {
          padding: 5px 10px;
          border-radius: 16px;
          background: #f3f4f6;
          color: #7a8580;
          font-size: 10.5px;
          font-weight: 600;
          white-space: nowrap;
          border: 1px solid transparent;
          cursor: pointer;
          font-family: inherit;
        }
        .apply-mobile-step-pill.active {
          background: var(--prana-primary-soft);
          border-color: rgba(12, 79, 71, 0.3);
          color: var(--prana-primary);
        }
        @media (max-width: 1050px) {
          .apply-mobile-steps-nav { display: flex; }
        }

        /* ============================================================
           LEGACY panel classes (kept for outer wrapper compatibility)
           ============================================================ */
        .apply-panel {
          background: transparent;
          border: none;
          border-radius: 0;
          box-shadow: none;
          display: block;
        }
        .apply-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0;
          border-bottom: none;
          background: transparent;
        }
        .apply-panel-body {
          display: contents;
        }

        /* Premium fields system styles */
        .premium-field {
          position: relative;
          width: 100%;
        }
        .premium-field-icon {
          position: absolute;
          left: 13px;
          top: 50%;
          width: 16px;
          height: 16px;
          color: #8aa198;
          transform: translateY(-50%);
          pointer-events: none;
          transition: color 0.2s ease;
          z-index: 10;
        }
        .premium-field:focus-within .premium-field-icon {
          color: var(--prana-primary);
        }
        .premium-input.has-left-icon {
          padding-left: 40px !important;
        }

        .premium-select-chevron {
          position: absolute;
          right: 14px;
          top: 50%;
          width: 15px;
          height: 15px;
          color: #8aa198;
          transform: translateY(-50%);
          pointer-events: none;
          transition: color 0.2s ease, transform 0.2s ease;
          z-index: 10;
        }
        .premium-field:focus-within .premium-select-chevron {
          color: var(--prana-primary);
        }

        .phone-field {
          display: flex;
          align-items: stretch;
          min-height: 46px;
          width: 100%;
          border: 1px solid #d7e2dc;
          border-radius: 10px;
          background: #ffffff;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(15, 70, 52, 0.025);
          transition: 0.2s ease;
        }
        .phone-field:hover {
          border-color: #a9c9b8;
          box-shadow:
            0 2px 4px rgba(15, 70, 52, 0.04),
            0 7px 18px rgba(15, 70, 52, 0.05);
        }
        .phone-field:focus-within {
          border-color: var(--prana-primary);
          box-shadow:
            0 0 0 4px rgba(12, 79, 71, 0.10),
            0 6px 18px rgba(12, 79, 71, 0.06);
        }
        .phone-country-code {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 0 12px;
          border-right: 1px solid #e2e9e5;
          background: #f8fbf9;
        }
        .phone-field input {
          flex: 1;
          min-width: 0;
          border: 0 !important;
          outline: 0 !important;
          padding: 11px 13px !important;
          background: transparent !important;
          box-shadow: none !important;
        }

        .character-counter {
          position: absolute;
          right: 12px;
          bottom: 9px;
          color: #8a9992;
          font-size: 10px;
          font-weight: 500;
          background: rgba(255, 255, 255, 0.8);
          padding: 1px 4px;
          border-radius: 3px;
        }

        .time-field-wrap {
          width: 110px !important;
          min-height: 36px !important;
          height: 36px !important;
          display: inline-flex !important;
        }
        .time-field-wrap .premium-field-icon {
          left: 9px !important;
          width: 13px !important;
          height: 13px !important;
        }
        .time-field-wrap input.apply-avail-time-input {
          padding-left: 28px !important;
          min-height: 36px !important;
          height: 36px !important;
          font-size: 11.5px !important;
          border-radius: 8px !important;
          padding-top: 6px !important;
          padding-bottom: 6px !important;
          border: 1px solid #d7e2dc !important;
          background: linear-gradient(180deg, #ffffff 0%, #fbfdfc 100%) !important;
          box-shadow: 0 1px 2px rgba(15, 70, 52, 0.03) !important;
        }

        /* Read-only and disabled fields */
        .apply-input:disabled,
        .apply-select:disabled {
          background: #f2f5f3 !important;
          border-color: #e1e7e4 !important;
          color: #8a9690 !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
        }

        /* Validation invalid states */
        .apply-input.is-invalid,
        .apply-select.is-invalid,
        .apply-textarea.is-invalid,
        .apply-tag-container.is-invalid {
          border-color: #d85b5b !important;
          background-color: #fffafa !important;
          box-shadow: 0 0 0 3px rgba(216, 91, 91, 0.08) !important;
        }
      `}</style>

      <PublicHeader />

      {/* Top bar is now inside the main wrapper */}

      <div className="apply-main-wrapper">
        {isSubmitted ? (
          <div className="apply-panel" style={{ padding: '48px 32px', textAlign: 'center', maxWidth: '640px', margin: '40px auto', background: '#ffffff', border: '1px solid #e2e8e5', borderRadius: '12px', boxShadow: '0 8px 30px rgba(12,79,71,0.08)' }}>
            <div style={{ width: '64px', height: '64px', backgroundColor: '#EEF9F1', border: '2px solid #159447', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto', color: '#159447' }}>
              <CheckCircle size={32} />
            </div>
            <h2 className="apply-header-title" style={{ marginBottom: '16px', fontSize: '24px', color: '#0c4f47', fontWeight: 700 }}>
              Teacher Application Submitted Successfully
            </h2>
            {successData?.email_sent === false ? (
              <p style={{ fontSize: '14.5px', color: '#92400e', backgroundColor: '#fef3c7', border: '1px solid #fde68a', padding: '12px 16px', borderRadius: '8px', lineHeight: 1.6, marginBottom: '14px', textAlign: 'center' }}>
                {successData?.custom_message || 'Your application was submitted successfully, but we could not send the confirmation email. Our team will still review your application.'}
              </p>
            ) : (
              <p style={{ fontSize: '14.5px', color: '#374151', lineHeight: 1.6, marginBottom: '14px' }}>
                Your details have been submitted for review. Once our admin team verifies the information, we will contact you through the email address and WhatsApp number provided in this form.
              </p>
            )}
            <p style={{ fontSize: '13.5px', color: '#991b1b', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', padding: '12px 16px', borderRadius: '8px', lineHeight: 1.5, marginBottom: '24px', textAlign: 'center' }}>
              Please do not submit this application again, as duplicate applications may delay the verification process.
            </p>
            <div style={{ backgroundColor: '#F7F9FB', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '14px 18px', marginBottom: '28px', textAlign: 'left', display: 'block' }}>
              <div style={{ fontSize: '13px', color: '#4b5563' }}>Reference Code: <strong style={{ color: '#0c4f47', fontWeight: 700 }}>{successData?.ref_no}</strong></div>
              <div style={{ fontSize: '13px', color: '#4b5563', marginTop: '6px' }}>Date Submitted: <strong style={{ color: '#0c4f47', fontWeight: 700 }}>{successData?.date}</strong></div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
              <a
                href="https://wa.me/919548354084?text=Hello%20PranaFlow%20Academy%2C%20I%20have%20submitted%20my%20teacher%20application%20and%20need%20assistance%20regarding%20my%20application."
                target="_blank"
                rel="noopener noreferrer"
                className="apply-btn"
                style={{
                  background: '#25d366',
                  color: '#ffffff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14.5px',
                  fontWeight: 700,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  maxWidth: '340px'
                }}
              >
                <Phone size={18} />
                Contact Admin on WhatsApp
              </a>
              <button
                type="button"
                className="apply-btn apply-btn-secondary"
                onClick={() => navigate('/')}
                style={{ width: '100%', maxWidth: '340px' }}
              >
                Return to Homepage
              </button>
            </div>
          </div>
        ) : (
          /* Main layout */
          <div>
            
            {/* Page top bar with heading and progress */}
            <div className="apply-page-topbar">
              <div className="apply-page-heading">
                <h1>Teacher Application Form</h1>
                <p>Apply to Become an Instructor at PranaFlow Yoga Academy</p>
              </div>
              <div className="apply-progress-card">
                <div className="apply-progress-label">Application Progress: {calculateCompletion()}%</div>
                <div className="apply-progress-track">
                  <div className="apply-progress-fill" style={{width: `${calculateCompletion()}%`}} />
                </div>
              </div>
            </div>

            {/* Mobile Steps horizontal scroll navigation */}
            <div className="apply-mobile-steps-nav">
              {sections.map(s => (
                <div
                  key={s.id}
                  className={`apply-mobile-step-pill ${activeSection === s.id ? 'active' : ''}`}
                  onClick={() => handleMenuClick(s.id)}
                >
                  {s.num}. {s.label}
                </div>
              ))}
            </div>

            {/* APPLICATION BODY: sidebar + content */}
            <div className="apply-app-body">
              
              {/* Left Sidebar Navigation */}
              <div className="apply-sidebar-col">
                <div className="apply-nav-title">APPLICATION SECTIONS</div>
                <div className="apply-nav-list">
                  {sections.map(s => {
                    const isActive = activeSection === s.id;
                    const sectionHasError = Object.keys(formErrors).some(
                      key => fieldToSectionMap[key] === s.id
                    );
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={`apply-nav-item ${isActive ? 'active' : ''} ${sectionHasError ? 'has-error' : ''}`}
                        onClick={() => handleMenuClick(s.id)}
                      >
                        <span className="apply-nav-icon-circle">{s.num}</span>
                        <span className="apply-nav-label">{s.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Need Help Card */}
                <div className="apply-sidebar-help-card">
                  <h5 className="apply-sidebar-help-title">Need Help?</h5>
                  <p className="apply-sidebar-help-text">If you have any questions while filling the form, our team is here to help.</p>
                  <div className="apply-sidebar-help-row">
                    <Mail size={15} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <a href="mailto:info@pranaflowacademy.com" className="apply-sidebar-help-email">
                      info@pranaflowacademy.com
                    </a>
                  </div>
                  <a
                    href="https://wa.me/919548354084?text=Hello%20PranaFlow%20Academy%2C%20I%20need%20help%20with%20the%20teacher%20application%20form."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="apply-sidebar-whatsapp-btn"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M12.012 2c-5.506 0-9.989 4.478-9.99 9.984 0 1.762.459 3.48 1.333 5.001l-1.416 5.172 5.294-1.388c1.467.8 3.123 1.222 4.777 1.223h.004c5.505 0 9.988-4.478 9.989-9.984 0-2.667-1.037-5.174-2.924-7.062a9.921 9.921 0 0 0-7.067-2.946zm5.824 14.372c-.247.694-1.228 1.272-1.706 1.348-.477.075-1.077.106-1.734-.105-.4-.127-.923-.298-1.597-.588-2.825-1.218-4.667-4.062-4.808-4.25-.141-.188-1.144-1.524-1.144-2.908 0-1.384.724-2.065 1.006-2.347.282-.282.614-.353.818-.353.204 0 .408.002.585.01.19.009.444-.072.694.528.25.6.852 2.08.926 2.23.074.15.124.326.024.525-.1.198-.15.322-.297.495-.147.173-.31.388-.443.52-.147.147-.301.307-.129.602.172.295.766 1.262 1.643 2.043 1.129 1.005 2.08 1.317 2.375 1.464.295.147.468.123.641-.075.173-.198.744-.866.942-1.163.198-.297.396-.247.668-.148.272.099 1.73.816 2.027.964.297.148.495.222.569.346.074.124.074.718-.173 1.412z"/>
                    </svg>
                    Message on WhatsApp
                  </a>
                </div>

                {/* Important Note Card */}
                <div className="apply-sidebar-note-card">
                  <h5 className="apply-sidebar-note-title">- Important Note</h5>
                  <p className="apply-sidebar-note-text">After your application is reviewed and approved, your teacher account will be created. You will be able to add your profile photo, certificates and other details to your teacher profile.</p>
                </div>
              </div>

              {/* Right Form Column */}
              <TeacherApplyErrorBoundary>
                <div className="apply-form-column" ref={formScrollRef}>
                  {/* Form Elements */}
                  <form noValidate onSubmit={handleFinalSubmit}>
                    {/* Sections grid wrapper */}
                    <div className="apply-sections-grid">

                    {/* Top Info Banner */}
                  <div className="apply-info-banner">
                    <div className="apply-banner-left">
                      <Info className="apply-banner-icon" />
                      <div className="apply-banner-text">
                        <span className="apply-banner-bold">Profile photo is NOT required at application stage.</span>
                        After admin approval, teacher account will be created and the teacher can add profile photo and media later.
                      </div>
                    </div>
                    <div className="apply-banner-illustration">
                      <svg width="64" height="42" viewBox="0 0 64 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="2" y="4" width="38" height="32" rx="4" fill="#DBEAFE" />
                        <circle cx="21" cy="15" r="5" fill="#93C5FD" />
                        <path d="M10 31C10 25 14 23 21 23C28 23 32 25 32 31" fill="#93C5FD" />
                        <circle cx="46" cy="24" r="10" fill="#FFFFFF" style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.06))' }} />
                        <path d="M46 18L50 26H42L46 18Z" fill="#34A853" />
                      </svg>
                    </div>
                  </div>

                  {errorMsg && (
                    <div className="apply-error-banner">
                      <AlertTriangle size={15} style={{ flexShrink: 0 }} />
                      <span>{errorMsg}</span>
                    </div>
                  )}

                  {/* Required note */}
                  <div className="apply-required-note">All fields marked <span>*</span> are required</div>
                  
                  {/* Section 1: Basic Information (full width) */}
                  <div className={`apply-card section-full ${expanded['basic-information'] ? '' : 'collapsed'}`} id="section-basic-information">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('basic-information')}
                      aria-expanded={!!expanded['basic-information']}
                      aria-controls="section-content-basic-information"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">1</span>
                        <h3 className="apply-section-title">Basic Information</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['basic-information'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>
                    
                    {expanded['basic-information'] && (
                      completedSections['basic-information'] ? (
                        <div className="apply-section-content" id="section-content-basic-information">
                          <div className="apply-review-grid">
                            <div className="apply-review-field"><span>Full Name</span>{formData.full_name || '-'}</div>
                            <div className="apply-review-field"><span>Email Address</span>{formData.email || '-'}</div>
                            <div className="apply-review-field"><span>WhatsApp Mobile</span>{(mobileCountryCode + ' ' + formData.mobile).trim() || '-'}</div>
                            <div className="apply-review-field"><span>Country</span>{formData.country || '-'}</div>
                            <div className="apply-review-field"><span>State</span>{formData.state || '-'}</div>
                            <div className="apply-review-field"><span>City</span>{formData.city || '-'}</div>
                            <div className="apply-review-field"><span>Current Location</span>{formData.current_location || '-'}</div>
                            <div className="apply-review-field"><span>Languages You Teach</span>{formData.languages || '-'}</div>
                            <div className="apply-review-field"><span>Title / Designation</span>{formData.designation || '-'}</div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('basic-information')}>
                              <Edit2 size={13} /> Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-basic-information">
                          {/* Row 1: Full Name, Email Address, WhatsApp Mobile Number */}
                          <div className="apply-grid-3">
                            <div className="apply-field">
                              <label className="apply-label">Full Name <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <UserRound className="premium-field-icon" />
                                <input type="text" name="full_name" required className={`apply-input premium-input has-left-icon${formErrors.full_name ? ' apply-input-error' : ''}`} placeholder="Enter your full name" value={formData.full_name} onChange={handleInputChange} />
                              </div>
                              {formErrors.full_name && <span className="apply-error-text">{formErrors.full_name}</span>}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">Email Address <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <Mail className="premium-field-icon" />
                                <input type="email" name="email" required className={`apply-input premium-input has-left-icon${formErrors.email ? ' apply-input-error' : ''}`} placeholder="Enter your email" value={formData.email} onChange={handleInputChange} onBlur={handleEmailBlur} />
                              </div>
                              {formErrors.email && <span className="apply-error-text">{formErrors.email}</span>}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">WhatsApp Mobile Number <span className="apply-required-star">*</span></label>
                              <CountryPhoneInput
                                countryCode={mobileCountryCode}
                                onCountryCodeChange={setMobileCountryCode}
                                mobileNumber={formData.mobile}
                                onMobileNumberChange={(val) => setFormData(prev => ({ ...prev, mobile: val, whatsapp: val }))}
                                placeholder="Enter WhatsApp mobile number"
                                required={true}
                              />
                              {formErrors.mobile && <span className="apply-error-text">{formErrors.mobile}</span>}
                            </div>
                          </div>

                          {/* Row 2: Country, State / Region, City, Preferred Timezone */}
                          <div className="apply-grid-4 apply-grid-mt">
                            <div className="apply-field">
                              <label className="apply-label">Country <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <MapPin className="premium-field-icon" />
                                <input type="text" name="country" required className={`apply-input premium-input has-left-icon${formErrors.country ? ' apply-input-error' : ''}`} placeholder="Enter your country" value={formData.country} onChange={handleInputChange} />
                              </div>
                              {formErrors.country && <span className="apply-error-text">{formErrors.country}</span>}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">State / Region <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <MapPin className="premium-field-icon" />
                                <input type="text" name="state" required className={`apply-input premium-input has-left-icon${formErrors.state ? ' apply-input-error' : ''}`} placeholder="Enter your state or region" value={formData.state} onChange={handleInputChange} />
                              </div>
                              {formErrors.state && <span className="apply-error-text">{formErrors.state}</span>}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">City <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <MapPin className="premium-field-icon" />
                                <input type="text" name="city" required className={`apply-input premium-input has-left-icon${formErrors.city ? ' apply-input-error' : ''}`} placeholder="Enter your city" value={formData.city} onChange={handleInputChange} />
                              </div>
                              {formErrors.city && <span className="apply-error-text">{formErrors.city}</span>}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">Preferred Timezone</label>
                              <div className="premium-field">
                                <Clock3 className="premium-field-icon" />
                                <input type="text" name="time_zone" className="apply-input premium-input has-left-icon" placeholder="e.g., IST (UTC+5:30), EST" value={formData.time_zone} onChange={handleInputChange} />
                              </div>
                            </div>
                          </div>

                          {/* Row 3: Current Location, Languages You Can Teach, Professional Title / Designation */}
                          <div className="apply-grid-3 apply-grid-mt">
                            <div className="apply-field">
                              <label className="apply-label">Current Location <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <MapPin className="premium-field-icon" />
                                <input type="text" name="current_location" required className={`apply-input premium-input has-left-icon${formErrors.current_location ? ' apply-input-error' : ''}`} placeholder="Enter your current location" value={formData.current_location} onChange={handleInputChange} />
                              </div>
                              {formErrors.current_location && <span className="apply-error-text">{formErrors.current_location}</span>}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">Languages You Can Teach <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <Globe2 className="premium-field-icon" />
                                <input type="text" name="languages" required className={`apply-input premium-input has-left-icon${formErrors.languages ? ' apply-input-error' : ''}`} placeholder="Example: English, Hindi, Spanish" value={formData.languages} onChange={handleInputChange} />
                              </div>
                              {formErrors.languages && <span className="apply-error-text">{formErrors.languages}</span>}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">Professional Title / Designation <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <BriefcaseBusiness className="premium-field-icon" />
                                <input type="text" name="designation" required className={`apply-input premium-input has-left-icon${formErrors.designation ? ' apply-input-error' : ''}`} placeholder="e.g., Yoga Instructor, Meditation Coach" value={formData.designation} onChange={handleInputChange} />
                              </div>
                              {formErrors.designation && <span className="apply-error-text">{formErrors.designation}</span>}
                            </div>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('basic-information')}>
                              Save &amp; Continue
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 2: Professional Profile (full width) */}
                  <div className={`apply-card section-full ${expanded['professional-profile'] ? '' : 'collapsed'}`} id="section-professional-profile">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('professional-profile')}
                      aria-expanded={!!expanded['professional-profile']}
                      aria-controls="section-content-professional-profile"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">2</span>
                        <h3 className="apply-section-title">Professional Profile</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['professional-profile'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>

                    {expanded['professional-profile'] && (
                      completedSections['professional-profile'] ? (
                        <div className="apply-section-content" id="section-content-professional-profile">
                          <div className="apply-review-grid">
                            <div className="apply-review-field"><span>Years of Experience</span>{formData.total_experience_years || formData.yearsOfTeachingExperience || '-'}</div>
                            <div className="apply-review-field"><span>Total Teaching Hours</span>{formData.total_teaching_hours || '-'}</div>
                            <div className="apply-review-field"><span>Total 1:1 Teaching Hours</span>{formData.one_to_one_teaching_hours || '-'}</div>
                            <div className="apply-review-field"><span>Teaching Style / Approach</span>{formData.teaching_style || '-'}</div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('professional-profile')}>
                              <Edit2 size={13} /> Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-professional-profile">
                          <div className="apply-grid-3">
                            <div className="apply-field">
                              <label className="apply-label">Years of Teaching Experience <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <BriefcaseBusiness className="premium-field-icon" />
                                <input type="text" name="yearsOfTeachingExperience" id="yearsOfTeachingExperience" required className={`apply-input premium-input has-left-icon${(formErrors.yearsOfTeachingExperience || formErrors.total_experience_years) ? ' apply-input-error' : ''}`} placeholder="e.g., 5" value={formData.yearsOfTeachingExperience || formData.total_experience_years} onChange={handleInputChange} />
                              </div>
                              {(formErrors.yearsOfTeachingExperience || formErrors.total_experience_years) && (
                                <span className="apply-error-text">{formErrors.yearsOfTeachingExperience || formErrors.total_experience_years}</span>
                              )}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">Total Teaching Hours (Approx.) <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <Timer className="premium-field-icon" />
                                <input type="text" name="total_teaching_hours" required className={`apply-input premium-input has-left-icon${formErrors.total_teaching_hours ? ' apply-input-error' : ''}`} placeholder="e.g., 800+ hours" value={formData.total_teaching_hours} onChange={handleInputChange} />
                              </div>
                              {formErrors.total_teaching_hours && <span className="apply-error-text">{formErrors.total_teaching_hours}</span>}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">Total 1:1 / Private Teaching Hours (Approx.)</label>
                              <div className="premium-field">
                                <Timer className="premium-field-icon" />
                                <input type="text" name="one_to_one_teaching_hours" className="apply-input premium-input has-left-icon" placeholder="e.g., 200+ hours" value={formData.one_to_one_teaching_hours} onChange={handleInputChange} />
                              </div>
                            </div>
                          </div>

                          <div className="apply-field apply-grid-mt">
                            <label className="apply-label">Teaching Style / Approach (Optional)</label>
                            <div style={{ position: 'relative' }}>
                              <textarea name="teaching_style" className="apply-textarea" placeholder="e.g., Hatha, Vinyasa, Meditation." value={formData.teaching_style} onChange={handleInputChange} maxLength={2000} />
                              <div className="character-counter">
                                {formData.teaching_style.length} / 2000
                              </div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('professional-profile')}>
                              Save &amp; Continue
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 3: Qualifications & Certificates */}
                  <div className={`apply-card section-full ${expanded['qualifications-certificates'] ? '' : 'collapsed'}`} id="section-qualifications-certificates">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('qualifications-certificates')}
                      aria-expanded={!!expanded['qualifications-certificates']}
                      aria-controls="section-content-qualifications-certificates"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">3</span>
                        <h3 className="apply-section-title">Qualifications &amp; Certificates</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['qualifications-certificates'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>

                    {expanded['qualifications-certificates'] && (
                      completedSections['qualifications-certificates'] ? (
                        <div className="apply-section-content" id="section-content-qualifications-certificates">
                          <div className="apply-review-grid">
                            <div className="apply-review-field" style={{ gridColumn: 'span 12' }}>
                              <span>Qualifications &amp; Certificates</span>
                              {Array.isArray(formData.certifications) && formData.certifications.length > 0 ? (
                                <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px', fontSize: '13.5px', color: '#172b26' }}>
                                  {formData.certifications.map((c, i) => {
                                    const name = typeof c === 'string' ? c : (c.name || c.title || 'Certificate');
                                    const issuer = typeof c === 'object' ? (c.issuer || c.organization || '') : '';
                                    const year = typeof c === 'object' ? (c.year || '') : '';
                                    const link = typeof c === 'object' ? (c.drive_link || c.link || '') : '';
                                    return (
                                      <li key={i} style={{ marginBottom: '4px' }}>
                                        <strong>{name}</strong> {issuer ? `- ${issuer}` : ''} {year ? `(${year})` : ''} {link ? ` | Link: ${link}` : ''}
                                      </li>
                                    );
                                  })}
                                </ul>
                              ) : 'No credentials added'}
                            </div>
                            <div className="apply-review-field"><span>Verification Confirmed</span>{formData.certs_confirmed ? 'Yes' : 'No'}</div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('qualifications-certificates')}>
                              <Edit2 size={13} /> Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-qualifications-certificates">
                          {/* Google Drive info box */}
                          <div className="apply-drive-info-box">
                            <Info size={13} />
                            <span>Please upload your certificates in Google Drive and paste the shareable link (Anyone with the link can view).</span>
                          </div>
                          <div className="apply-item-list">
                            {(!Array.isArray(formData.certifications) || formData.certifications.length === 0) ? (
                              <p style={{ fontStyle: 'italic', fontSize: '12px', color: '#6B7280', margin: '4px 0' }}>No credentials added yet.</p>
                            ) : (
                              formData.certifications.map((item, idx) => {
                                if (!item) return null;
                                const cName = typeof item === 'string' ? item : (item.name || item.title || 'Certificate');
                                const cIssuer = typeof item === 'object' ? (item.issuer || item.organization || '') : '';
                                const cYear = typeof item === 'object' ? (item.year || item.year_of_completion || '') : '';
                                const cLink = typeof item === 'object' ? (item.drive_link || item.certificate_drive_link || item.link || '') : '';
                                return (
                                  <div key={idx} className="apply-item-row">
                                    <div>
                                      <span className="apply-item-title">{cName}</span>
                                      {(cIssuer || cYear) && <div className="apply-item-subtitle">{cIssuer} {cYear ? `- ${cYear}` : ''}</div>}
                                      {cLink && <div className="apply-item-subtitle" style={{ color: '#159447', wordBreak: 'break-all' }}>Link: {cLink}</div>}
                                    </div>
                                    <div className="apply-item-actions">
                                      <button type="button" className="apply-btn-action" onClick={() => handleEditCert(idx)}>
                                        <Edit2 size={12} /> Edit
                                      </button>
                                      <button type="button" className="apply-btn-action apply-btn-remove" onClick={() => handleRemoveCert(idx)}>
                                        <Trash2 size={12} /> Remove
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>

                          <div className="apply-inline-builder">
                            <p className="apply-builder-title">{editingCertIdx !== null ? '-- Edit Certificate' : '- Add Certificate'}</p>
                            <div className="apply-grid-3">
                              <div className="apply-field">
                                <label className="apply-label">Certificate Name *</label>
                                <div className="premium-field">
                                  <BadgeCheck className="premium-field-icon" />
                                  <input type="text" id="certName" name="certName" className={`apply-input premium-input has-left-icon${formErrors.certName ? ' apply-input-error' : ''}`} placeholder="e.g. RYT 200" value={certName} onChange={e => { setCertName(e.target.value); if (formErrors.certName) setFormErrors(prev => ({ ...prev, certName: '' })); }} />
                                </div>
                                {formErrors.certName && <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>{formErrors.certName}</span>}
                              </div>
                              <div className="apply-field">
                                <label className="apply-label">Issuing Organization</label>
                                <div className="premium-field">
                                  <BadgeCheck className="premium-field-icon" />
                                  <input type="text" id="certIssuer" name="certIssuer" className={`apply-input premium-input has-left-icon${formErrors.certIssuer ? ' apply-input-error' : ''}`} placeholder="e.g. Yoga Alliance" value={certIssuer} onChange={e => { setCertIssuer(e.target.value); if (formErrors.certIssuer) setFormErrors(prev => ({ ...prev, certIssuer: '' })); }} />
                                </div>
                                {formErrors.certIssuer && <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>{formErrors.certIssuer}</span>}
                              </div>
                              <div className="apply-field">
                                <label className="apply-label">Year of Completion</label>
                                <div className="premium-field">
                                  <CalendarDays className="premium-field-icon" />
                                  <input type="text" id="certYear" name="certYear" className={`apply-input premium-input has-left-icon${formErrors.certYear ? ' apply-input-error' : ''}`} placeholder="e.g. 2021" value={certYear} onChange={e => { setCertYear(e.target.value); if (formErrors.certYear) setFormErrors(prev => ({ ...prev, certYear: '' })); }} />
                                </div>
                                {formErrors.certYear && <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>{formErrors.certYear}</span>}
                              </div>
                            </div>

                            <div className="apply-grid-2" style={{ marginTop: '10px' }}>
                              <div className="apply-field">
                                <label className="apply-label">Certificate / Reg ID No. (Optional)</label>
                                <div className="premium-field">
                                  <BadgeCheck className="premium-field-icon" />
                                  <input type="text" id="certId" name="certId" className="apply-input premium-input has-left-icon" placeholder="e.g. YA-88392" value={certId} onChange={e => setCertId(e.target.value)} />
                                </div>
                              </div>
                              <div className="apply-field">
                                <label className="apply-label">Institute / School Link (Optional)</label>
                                <div className="premium-field">
                                  <Link2 className="premium-field-icon" />
                                  <input type="text" inputMode="url" id="certSchoolLink" name="certSchoolLink" className={`apply-input premium-input has-left-icon${formErrors.certSchoolLink ? ' apply-input-error' : ''}`} placeholder="e.g. www.example.com or https://www.example.com" value={certSchoolLink} onChange={e => { setCertSchoolLink(e.target.value); if (formErrors.certSchoolLink) setFormErrors(prev => ({ ...prev, certSchoolLink: '' })); }} />
                      {formErrors.certSchoolLink && <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>{formErrors.certSchoolLink}</span>}
                                </div>
                              </div>
                            </div>

                            <div className="apply-field" style={{ marginTop: '10px' }}>
                              <label className="apply-label">Certificate Google Drive Link (Optional)</label>
                              <div className="premium-field">
                                <Link2 className="premium-field-icon" />
                                <input type="text" inputMode="url" id="certDriveLink" name="certDriveLink" className={`apply-input premium-input has-left-icon${formErrors.certDriveLink ? ' apply-input-error' : ''}`} placeholder="Paste shareable document URL" value={certDriveLink} onChange={e => { setCertDriveLink(e.target.value); if (formErrors.certDriveLink) setFormErrors(prev => ({ ...prev, certDriveLink: '' })); }} />
                              </div>
                              {formErrors.certDriveLink && <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>{formErrors.certDriveLink}</span>}
                              <span style={{ fontSize: '11px', color: '#6B7280', marginTop: '3px' }}>
                                Upload certificates to Google Drive and paste a shareable "Anyone with the link can view" link.
                              </span>
                            </div>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleAddCert()} style={{ marginTop: '14px', height: '34px' }}>
                              {editingCertIdx !== null ? 'Save Changes' : '+ Add Certificate'}
                            </button>
                          </div>

                          <div className="apply-field" style={{ marginTop: '20px', flexDirection: 'row', alignItems: 'flex-start', gap: '8px' }}>
                            <input type="checkbox" name="certs_confirmed" id="certsConfirmCheck" className={`apply-checkbox-input${formErrors.certs_confirmed ? ' apply-input-error' : ''}`} checked={formData.certs_confirmed} onChange={e => { handleInputChange(e); if (formErrors.certs_confirmed) setFormErrors(prev => ({ ...prev, certs_confirmed: '' })); }} style={{ marginTop: '2px' }} />
                            <label htmlFor="certsConfirmCheck" className="apply-checkbox-label" style={{ fontWeight: 500, lineHeight: 1.45, cursor: 'pointer' }}>
                              I confirm that all certificates and qualifications submitted are genuine and may be verified by PranaFlow Yoga Academy. *
                            </label>
                          </div>
                          {formErrors.certs_confirmed && <span className="apply-error-text" style={{ marginLeft: '24px', display: 'block', marginTop: '4px' }}>{formErrors.certs_confirmed}</span>}

                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('qualifications-certificates')}>
                              Save &amp; Continue
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 4: Teaching Expertise */}
                  <div className={`apply-card section-full ${expanded['teaching-expertise'] ? '' : 'collapsed'}`} id="section-teaching-expertise">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('teaching-expertise')}
                      aria-expanded={!!expanded['teaching-expertise']}
                      aria-controls="section-content-teaching-expertise"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">4</span>
                        <h3 className="apply-section-title">Teaching Subjects &amp; Specializations</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['teaching-expertise'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>

                    {expanded['teaching-expertise'] && (
                      completedSections['teaching-expertise'] ? (
                        <div className="apply-section-content" id="section-content-teaching-expertise">
                          <div className="apply-review-grid">
                            <div className="apply-review-field">
                              <span>Teaching Subjects &amp; Specializations</span>
                              {Array.isArray(formData.expertise) && formData.expertise.length > 0 ? formData.expertise.join(', ') : '-'}
                            </div>
                            <div className="apply-review-field"><span>Student Groups You Have Taught</span>{formData.types_students_taught || '-'}</div>
                            <div className="apply-review-field"><span>Other Subject or Specialization</span>{formData.custom_specialization || '-'}</div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('teaching-expertise')}>
                              <Edit2 size={13} /> Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-teaching-expertise">
                          <p style={{ fontSize: '13.5px', color: '#4d7060', margin: '0 0 14px 0', lineHeight: 1.4 }}>
                            <Info size={14} style={{ verticalAlign: '-2px', marginRight: '5px' }} />
                            Select the individual subjects, practices, and areas in which you have teaching expertise. These subjects may be taught as part of one or more academy programs.
                          </p>
                          <label className="apply-label" style={{ marginBottom: '10px', display: 'block' }}>Select individual subjects &amp; practices:</label>
                          <div className="apply-checkbox-grid">
                            {[
                              'Hatha Yoga', 'Ashtanga Yoga', 'Vinyasa Flow', 'Pranayama', 
                              'Meditation', 'Yoga Philosophy', 'Yoga Anatomy', 'Yoga Nidra', 
                              'Alignment and Adjustment', 'Ayurveda', 'Shatkarma', 'Prenatal Yoga', 
                              'Kids Yoga', 'Yoga Therapy', 'Teaching Methodology', 'Restorative Yoga', 
                              'Yoga for Seniors'
                            ].map((item, idx) => {
                              const itemId = `exp-${idx}-${item.replace(/[^a-zA-Z0-9]/g, '-')}`;
                              const isChecked = Array.isArray(formData.expertise) && formData.expertise.includes(item);
                              return (
                                <div key={item} className="apply-checkbox-item">
                                  <input
                                    type="checkbox"
                                    id={itemId}
                                    name="expertise"
                                    className="apply-checkbox-input"
                                    checked={isChecked}
                                    onChange={() => handleExpertiseCheckboxChange(item)}
                                  />
                                  <label htmlFor={itemId} style={{ cursor: 'pointer' }}>{item}</label>
                                </div>
                              );
                            })}
                          </div>
                          {formErrors.expertise && <span className="apply-error-text" style={{ marginTop: '8px', display: 'block' }}>{formErrors.expertise}</span>}

                          <div className="apply-grid-2" style={{ marginTop: '20px' }}>
                            <div className="apply-field">
                              <label className="apply-label">Student Groups You Have Taught</label>
                              <div className="premium-field">
                                <Users className="premium-field-icon" />
                                <input type="text" name="types_students_taught" className="apply-input premium-input has-left-icon" placeholder="e.g. Beginners, children, seniors, prenatal students" value={formData.types_students_taught} onChange={handleInputChange} />
                              </div>
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">Other Subject or Specialization</label>
                              <div className="premium-field">
                                <BadgeCheck className="premium-field-icon" />
                                <input type="text" name="custom_specialization" className="apply-input premium-input has-left-icon" placeholder="e.g. Advanced anatomy, mantra, sound healing" value={formData.custom_specialization} onChange={handleInputChange} />
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('teaching-expertise')}>
                              Save &amp; Continue
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 5: Academy Programs You Are Qualified to Teach (full width) */}
                  <div className={`apply-card section-full course-section ${expanded['courses-programs'] ? '' : 'collapsed'}`} id="section-courses-programs">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('courses-programs')}
                      aria-expanded={!!expanded['courses-programs']}
                      aria-controls="section-content-courses-programs"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">5</span>
                        <h3 className="apply-section-title">Academy Programs You Are Qualified to Teach</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['courses-programs'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>

                    {expanded['courses-programs'] && (
                      completedSections['courses-programs'] ? (
                        <div className="apply-section-content" id="section-content-courses-programs">
                          <div className="apply-review-grid">
                            <div className="apply-review-field" style={{ gridColumn: 'span 12' }}>
                              <span>Academy Programs You Are Qualified to Teach</span>
                              {Array.isArray(formData.selected_course_ids) && formData.selected_course_ids.length > 0 ? (
                                <div style={{ marginTop: '4px', fontSize: '13.5px', color: '#172b26' }}>
                                  {formData.selected_course_ids.map(id => {
                                    if (id === 'OTHER') return 'Other - Custom Unlisted Course';
                                    const match = adminCourses.find(c => String(c.id) === String(id));
                                    return match ? (match.title || match.name) : `Course #${id}`;
                                  }).join(', ')}
                                </div>
                              ) : 'None selected'}
                            </div>

                            {Array.isArray(formData.custom_courses) && formData.custom_courses.length > 0 && (
                              <div className="apply-review-field" style={{ gridColumn: 'span 12', marginTop: '6px' }}>
                                <span style={{ fontWeight: 700, color: '#0c4f47', fontSize: '14px', marginBottom: '8px', display: 'block' }}>Unlisted Custom Courses Added by Applicant</span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '6px' }}>
                                  {formData.custom_courses.map((c, idx) => {
                                    const rawCat = c.course_category || c.courseCategory || '';
                                    const isOtherCat = rawCat.endsWith('-(Other)') || rawCat === 'Other' || c.custom_category || c.manualCategory;
                                    const cleanManualCat = isOtherCat ? (c.custom_category || c.manualCategory || rawCat).replace(/-\(Other\)$/i, '').trim() : rawCat;
                                    const displayCat = isOtherCat ? `${cleanManualCat} (Other)` : rawCat;

                                    const rawSubj = c.subjects_covered || c.mainSubjectsCovered || c.main_subjects || c.subjects;
                                    const parsedSubj = Array.isArray(rawSubj)
                                      ? rawSubj
                                      : typeof rawSubj === 'string'
                                      ? rawSubj.split(',').map(s => s.trim()).filter(Boolean)
                                      : [];

                                    const qual = c.supporting_qualification || c.supportingQualification || c.qualification;
                                    const details = c.course_details || c.briefCourseDetails || c.details || c.description;

                                    return (
                                      <div key={idx} style={{ background: '#f8fcf9', border: '1px solid #d7e2dc', borderRadius: '10px', padding: '14px 16px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                          <strong style={{ fontSize: '14.5px', color: '#0c4f47' }}>{c.course_name || c.courseName}</strong>
                                          <span style={{ fontSize: '12px', background: '#e2e8f0', color: '#334155', padding: '3px 10px', borderRadius: '12px', fontWeight: '600' }}>
                                            Category: {displayCat}
                                          </span>
                                        </div>
                                        <div style={{ fontSize: '12.5px', color: '#4d7060', marginBottom: '6px' }}>
                                          Level: <strong>{c.course_level || c.level || 'Not provided'}</strong> • Duration: <strong>{c.course_duration || c.duration || 'Not provided'}</strong>
                                        </div>
                                        {parsedSubj.length > 0 && (
                                          <div style={{ marginBottom: '6px' }}>
                                            <span style={{ fontSize: '12px', fontWeight: 650, color: '#0c4f47', display: 'block', marginBottom: '4px' }}>Main Subjects Covered:</span>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                              {parsedSubj.map((s, sIdx) => (
                                                <span key={sIdx} style={{ background: '#e6f4ea', color: '#137333', border: '1px solid #ceead6', padding: '2px 8px', borderRadius: '12px', fontSize: '11.5px', fontWeight: '600' }}>
                                                  ✓ {s}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {qual && (
                                          <div style={{ fontSize: '12.5px', color: '#172b26', marginBottom: '4px' }}>
                                            <strong>Supporting Qualification:</strong> {qual}
                                          </div>
                                        )}
                                        {details && (
                                          <div style={{ fontSize: '12.5px', color: '#172b26', whiteSpace: 'pre-wrap', lineHeight: '1.5', background: '#ffffff', border: '1px solid #e2e8e5', padding: '8px 12px', borderRadius: '6px', marginTop: '6px' }}>
                                            <strong>Brief Course Details:</strong> {details}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('courses-programs')}>
                              <Edit2 size={13} /> Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-courses-programs">
                          {/* Course Selectable Pill Buttons */}
                          <div className="apply-field" style={{ marginBottom: '24px' }}>
                            <label className="apply-label" style={{ fontSize: '15px', fontWeight: 650, color: '#172b26', marginBottom: '6px', display: 'block' }}>
                              Select the Complete Programs You Can Independently Teach <span className="apply-required-star">*</span>
                            </label>
                            <p style={{ fontSize: '13.5px', color: '#6b7280', margin: '0 0 4px 0', lineHeight: 1.4 }}>
                              Based on your qualifications and subject expertise, select the complete PranaFlow Academy programs that you are qualified to teach or lead.
                            </p>
                            <p style={{ fontSize: '12px', color: '#0c4f47', fontWeight: 500, margin: '0 0 14px 0', fontStyle: 'italic' }}>
                              Subjects are individual areas of expertise, while programs are complete courses that you are qualified to lead.
                            </p>

                            {coursesLoading ? (
                              <div style={{ padding: '16px 0', color: '#7a8580', fontSize: '14px' }}>
                                Loading available courses...
                              </div>
                            ) : coursesError ? (
                              <div style={{ marginBottom: '14px' }}>
                                <p style={{ color: '#c0392b', fontSize: '13.5px', margin: '0 0 8px 0' }}>{coursesError}</p>
                                <button type="button" className="apply-btn apply-btn-secondary apply-btn-sm" onClick={loadAdminCourses} style={{ marginBottom: '14px' }}>
                                  Retry
                                </button>
                              </div>
                            ) : null}

                            <div className="course-pill-list">
                              {/* Admin-created course pills */}
                              {adminCourses.map(c => {
                                const isSelected = formData.selected_course_ids.some(id => String(id) === String(c.id));
                                const courseName = c.title || c.name;
                                return (
                                  <button
                                    key={c.id}
                                    type="button"
                                    className={`course-pill ${isSelected ? 'is-selected' : ''}`}
                                    onClick={() => toggleCourseSelection(c.id)}
                                    aria-pressed={isSelected}
                                  >
                                    {isSelected ? <Check size={16} /> : <Plus size={16} />}
                                    <span>{courseName}</span>
                                  </button>
                                );
                              })}

                              {adminCourses.length === 0 && !coursesLoading && !coursesError && (
                                <div style={{ fontSize: '13.5px', color: '#6b7280', fontStyle: 'italic', marginBottom: '8px', width: '100%' }}>
                                  No academy courses are currently available. Select "Other - Course Not Listed" to enter your course.
                                </div>
                              )}

                              {/* Always append "Other - Course Not Listed" pill at the end */}
                              <button
                                type="button"
                                className={`course-pill course-pill-other ${formData.selected_course_ids.includes('OTHER') ? 'is-selected' : ''}`}
                                onClick={() => toggleCourseSelection('OTHER')}
                                aria-pressed={formData.selected_course_ids.includes('OTHER')}
                              >
                                {formData.selected_course_ids.includes('OTHER') ? <Check size={16} /> : <Plus size={16} />}
                                <span>Other - Course Not Listed</span>
                              </button>
                            </div>

                            {formErrors.selected_course_ids && (
                              <span className="apply-error-text" style={{ marginTop: '10px', display: 'block' }}>
                                {formErrors.selected_course_ids}
                              </span>
                            )}
                          </div>

                          {/* Conditional Custom Course Subsection */}
                          {formData.selected_course_ids.includes('OTHER') && (
                            <div style={{ background: '#f0f8f5', border: '1.5px solid #b5d4c8', borderRadius: '14px', padding: '20px', marginBottom: '24px' }}>
                              <h4 style={{ margin: '0 0 14px 0', fontSize: '15px', fontWeight: 700, color: '#0c4f47', display: 'flex', alignItems: 'center', gap: '7px' }}>
                                <Plus size={16} /> Add a Course or Program Not Listed
                              </h4>

                              {/* Existing Custom Courses List */}
                              {formData.custom_courses.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '18px' }}>
                                  {formData.custom_courses.map((item, idx) => {
                                    const rawCat = item.course_category || item.courseCategory || '';
                                    const isOtherCat = rawCat.endsWith('-(Other)') || rawCat === 'Other' || item.custom_category || item.manualCategory;
                                    const cleanManualCat = isOtherCat ? (item.custom_category || item.manualCategory || rawCat).replace(/-\(Other\)$/i, '').trim() : rawCat;
                                    const displayCat = isOtherCat ? `${cleanManualCat} (Other)` : rawCat;

                                    const rawSubj = item.subjects_covered || item.mainSubjectsCovered || item.main_subjects || item.subjects;
                                    const parsedSubj = Array.isArray(rawSubj)
                                      ? rawSubj
                                      : typeof rawSubj === 'string'
                                      ? rawSubj.split(',').map(s => s.trim()).filter(Boolean)
                                      : [];

                                    const qual = item.supporting_qualification || item.supportingQualification || item.qualification;
                                    const details = item.course_details || item.briefCourseDetails || item.details || item.description;

                                    return (
                                      <div key={idx} className="apply-item-row" style={{ background: '#ffffff', border: '1px solid #d7e2dc', borderRadius: '10px', padding: '14px 16px' }}>
                                        <div style={{ flex: 1 }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <span className="apply-item-title" style={{ fontSize: '14.5px', fontWeight: 700, color: '#0c4f47' }}>{item.course_name || item.courseName}</span>
                                            <span style={{ fontSize: '11.5px', background: '#e2e8f0', color: '#334155', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                                              Category: {displayCat}
                                            </span>
                                          </div>
                                          <div className="apply-item-subtitle" style={{ fontSize: '12px', color: '#4d7060', marginBottom: '6px' }}>
                                            Level: <strong>{item.course_level || item.level || 'Not provided'}</strong> • Duration: <strong>{item.course_duration || item.duration || 'Not provided'}</strong>
                                          </div>
                                          {parsedSubj.length > 0 && (
                                            <div style={{ marginBottom: '6px' }}>
                                              <span style={{ fontSize: '11.5px', fontWeight: 650, color: '#0c4f47', display: 'block', marginBottom: '3px' }}>Main Subjects Covered:</span>
                                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                {parsedSubj.map((s, sIdx) => (
                                                  <span key={sIdx} style={{ background: '#e6f4ea', color: '#137333', border: '1px solid #ceead6', padding: '1px 7px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
                                                    ✓ {s}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {qual && (
                                            <div style={{ fontSize: '12px', color: '#172b26', marginBottom: '4px' }}>
                                              <strong>Supporting Qualification:</strong> {qual}
                                            </div>
                                          )}
                                          {details && (
                                            <div style={{ fontSize: '12px', color: '#172b26', whiteSpace: 'pre-wrap', lineHeight: '1.45', background: '#f8fcf9', border: '1px solid #e2e8e5', padding: '6px 10px', borderRadius: '6px', marginTop: '4px' }}>
                                              <strong>Brief Course Details:</strong> {details}
                                            </div>
                                          )}
                                        </div>
                                        <div className="apply-item-actions" style={{ marginLeft: '12px', alignSelf: 'flex-start' }}>
                                          <button type="button" className="apply-btn-action" onClick={() => handleEditCustomCourse(idx)}>
                                            <Edit2 size={12} /> Edit
                                          </button>
                                          <button type="button" className="apply-btn-action apply-btn-remove" onClick={() => handleRemoveCustomCourse(idx)}>
                                            <Trash2 size={12} /> Remove
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Custom Course Form Builder */}
                              <div className="apply-inline-builder" style={{ background: '#ffffff', padding: '16px', borderRadius: '12px', border: '1px solid #c8dbd4' }}>
                                <p className="apply-builder-title" style={{ fontWeight: 700, color: '#0c4f47', marginBottom: '12px' }}>
                                  {editingCustomIdx !== null ? 'Edit Custom Course' : '+ Enter Custom Course Details'}
                                </p>

                                <div className="apply-grid-2" style={{ marginBottom: '12px' }}>
                                  <div className="apply-field">
                                    <label className="apply-label">Course or Program Name <span className="apply-required-star">*</span></label>
                                    <input type="text" className="apply-input" placeholder="e.g., Aerial Yoga Teacher Training" value={customName} onChange={e => setCustomName(e.target.value)} />
                                  </div>
                                  <div className="apply-field">
                                    <label className="apply-label">Course Category</label>
                                    <div className="premium-field">
                                      <select 
                                        className="apply-select premium-input" 
                                        value={customCategory} 
                                        onChange={e => {
                                          const val = e.target.value;
                                          setCustomCategory(val);
                                          if (val !== 'Other') {
                                            setCustomCategoryOther('');
                                            setCustomCategoryOtherError('');
                                          }
                                        }}
                                      >
                                        {['Yoga Teacher Training', 'Yoga Specialization', 'Meditation', 'Pranayama', 'Yoga Therapy', 'Ayurveda', 'Wellness', 'Nutrition', 'Workshop', 'Retreat', 'Other'].map(cat => (
                                          <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                      </select>
                                      <ChevronDown className="premium-select-chevron" />
                                    </div>
                                  </div>
                                </div>

                                {customCategory === 'Other' && (
                                  <div className="apply-field" style={{ marginBottom: '12px' }}>
                                    <label className="apply-label">Enter Course Category <span className="apply-required-star">*</span></label>
                                    <input 
                                      type="text" 
                                      className="apply-input" 
                                      placeholder="Enter the course category manually" 
                                      value={customCategoryOther} 
                                      onChange={e => {
                                        setCustomCategoryOther(e.target.value);
                                        if (e.target.value.trim()) {
                                          setCustomCategoryOtherError('');
                                        }
                                      }} 
                                    />
                                    {customCategoryOtherError && (
                                      <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>
                                        {customCategoryOtherError}
                                      </span>
                                    )}
                                  </div>
                                )}

                                <div className="apply-grid-2" style={{ marginBottom: '12px' }}>
                                  <div className="apply-field">
                                    <label className="apply-label">Course Level</label>
                                    <div className="premium-field">
                                      <select 
                                        className="apply-select premium-input" 
                                        value={customLevel} 
                                        onChange={e => setCustomLevel(e.target.value)}
                                      >
                                        {['All Levels', 'Beginner', 'Intermediate', 'Advanced', 'Specialized'].map(lvl => (
                                          <option key={lvl} value={lvl}>{lvl}</option>
                                        ))}
                                      </select>
                                      <ChevronDown className="premium-select-chevron" />
                                    </div>
                                  </div>
                                  <div className="apply-field">
                                    <label className="apply-label">Course Duration</label>
                                    <input 
                                      type="text" 
                                      className="apply-input" 
                                      placeholder="e.g. 30 Hours, 4 Weeks" 
                                      value={customDuration} 
                                      onChange={e => setCustomDuration(e.target.value)} 
                                    />
                                  </div>
                                </div>

                                <div className="apply-field" style={{ marginBottom: '12px' }}>
                                  <label className="apply-label">Main Subjects Covered</label>
                                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                    {customSubjects.map(s => (
                                      <span key={s} className="apply-tag">
                                        {s}
                                        <button type="button" className="apply-tag-remove" onClick={() => setCustomSubjects(prev => prev.filter(x => x !== s))}>×</button>
                                      </span>
                                    ))}
                                  </div>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    <input type="text" className="apply-input" placeholder="Type a subject and press Enter" value={customSubjectInput} onChange={e => setCustomSubjectInput(e.target.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (customSubjectInput.trim() && !customSubjects.includes(customSubjectInput.trim())) { setCustomSubjects(prev => [...prev, customSubjectInput.trim()]); setCustomSubjectInput(''); } } }} style={{ flex: 1 }} />
                                    <button type="button" className="apply-btn apply-btn-primary" style={{ height: '46px', padding: '0 14px', flexShrink: 0 }}
                                      onClick={() => { if (customSubjectInput.trim() && !customSubjects.includes(customSubjectInput.trim())) { setCustomSubjects(prev => [...prev, customSubjectInput.trim()]); setCustomSubjectInput(''); } }}>
                                      + Add
                                    </button>
                                  </div>
                                </div>

                                <div className="apply-field" style={{ marginBottom: '12px' }}>
                                  <label className="apply-label">Qualification Supporting This Course</label>
                                  <input type="text" className="apply-input" placeholder="Mention the certificate, diploma or experience that qualifies you to teach this course" value={customQualification} onChange={e => setCustomQualification(e.target.value)} />
                                </div>

                                <div className="apply-field">
                                  <label className="apply-label">Brief Course Details</label>
                                  <div style={{ position: 'relative' }}>
                                    <textarea className="apply-textarea" maxLength={750} rows={3} placeholder="Briefly explain the course, its learning outcomes and your experience teaching it." value={customDetails} onChange={e => setCustomDetails(e.target.value)} style={{ paddingBottom: '28px' }} />
                                    <span style={{ position: 'absolute', bottom: '8px', right: '12px', fontSize: '11px', color: customDetails.length >= 700 ? '#c0392b' : '#94a39c', fontWeight: 500 }}>
                                      {customDetails.length}/750
                                    </span>
                                  </div>
                                </div>

                                <button type="button" className="apply-btn apply-btn-primary" onClick={handleAddCustomCourse} style={{ marginTop: '14px', height: '38px' }}>
                                  {editingCustomIdx !== null ? 'Save Changes' : '+ Add Custom Course'}
                                </button>
                              </div>
                              {formErrors.custom_courses && <span className="apply-error-text" style={{ marginTop: '8px', display: 'block' }}>{formErrors.custom_courses}</span>}
                            </div>
                          )}

                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('courses-programs')}>
                              Save &amp; Continue
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 6: Experience & Background */}
                  <div className={`apply-card section-full ${expanded['experience-background'] ? '' : 'collapsed'}`} id="section-experience-background">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('experience-background')}
                      aria-expanded={!!expanded['experience-background']}
                      aria-controls="section-content-experience-background"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">6</span>
                        <h3 className="apply-section-title">Experience &amp; Background (Domestic &amp; International)</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['experience-background'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>
                    {expanded['experience-background'] && (
                      completedSections['experience-background'] ? (
                        <div className="apply-section-content" id="section-content-experience-background">
                          <div className="apply-review-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                            <div className="apply-review-field" style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                              <span style={{ fontWeight: 700, color: '#079447', fontSize: '13px', display: 'block', marginBottom: '4px' }}>🇮🇳 Domestic Teaching Experience – India</span>
                              <strong>{formData.domestic_has_experience || 'No'}</strong>
                              {formData.domestic_has_experience === 'Yes' && (
                                <div style={{ fontSize: '12px', color: '#475569', marginTop: '6px' }}>
                                  <div>Mode: <strong>{formData.domestic_teaching_mode || '-'}</strong></div>
                                  <div>Total Students: <strong>{formData.domestic_total_student_count || '0'}</strong> (Online: {formData.domestic_online_student_count || '0'}, In-person: {formData.domestic_in_person_student_count || '0'})</div>
                                  <div>Teaching Hours: <strong>{formData.domestic_teaching_hours || '-'} Hours</strong></div>
                                </div>
                              )}
                            </div>
                            <div className="apply-review-field" style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                              <span style={{ fontWeight: 700, color: '#079447', fontSize: '13px', display: 'block', marginBottom: '4px' }}>🌐 International Teaching Experience</span>
                              <strong>{formData.intl_has_experience || 'No'}</strong>
                              {formData.intl_has_experience === 'Yes' && (
                                <div style={{ fontSize: '12px', color: '#475569', marginTop: '6px' }}>
                                  <div>Mode: <strong>{formData.intl_teaching_mode || '-'}</strong></div>
                                  <div>Total Students: <strong>{formData.intl_student_count || '0'}</strong></div>
                                  <div>Teaching Hours: <strong>{formData.intl_teaching_hours || '-'} Hours</strong></div>
                                </div>
                              )}
                            </div>

                            {formData.previously_taught_choice === 'Yes' && Array.isArray(formData.previous_course_experiences) && formData.previous_course_experiences.length > 0 && (
                              <div className="apply-review-field" style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', gridColumn: '1 / -1' }}>
                                <span style={{ fontWeight: 700, color: '#079447', fontSize: '13px', display: 'block', marginBottom: '6px' }}>🎓 Previous Program Teaching Experience</span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {formData.previous_course_experiences.map((exp, idx) => (
                                    <div key={idx} style={{ background: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}>
                                      <div style={{ fontWeight: 700, color: '#0c4f47' }}>Program Experience {idx + 1}: {exp.courseName || 'N/A'}</div>
                                      <div style={{ color: '#475569', fontSize: '12px', marginTop: '2px' }}>
                                        Where Taught: <strong>{exp.teachingLocation || 'N/A'}</strong> | Duration: <strong>{formatDuration(exp.experienceYears, exp.experienceMonths)}</strong> | Students Taught: <strong>{exp.studentsTaught || 'N/A'}</strong>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {Array.isArray(formData.teaching_journey) && formData.teaching_journey.length > 0 && (
                              <div className="apply-review-field" style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', gridColumn: '1 / -1' }}>
                                <span style={{ fontWeight: 700, color: '#079447', fontSize: '13px', display: 'block', marginBottom: '6px' }}>💼 My Teaching Journey</span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {formData.teaching_journey.map((jEntry, idx) => {
                                    const locStr = [jEntry.city, jEntry.state, jEntry.country].filter(Boolean).join(', ');
                                    const startStr = `${jEntry.start_month || ''} ${jEntry.start_year || ''}`.trim();
                                    const endStr = jEntry.is_current ? 'Present' : (`${jEntry.end_month || ''} ${jEntry.end_year || ''}`.trim() || 'N/A');
                                    return (
                                      <div key={idx} style={{ background: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}>
                                        <div style={{ fontWeight: 700, color: '#0c4f47' }}>Journey Entry {idx + 1}: {jEntry.role_title} at {jEntry.organisation}</div>
                                        <div style={{ color: '#475569', fontSize: '12px', marginTop: '2px' }}>
                                          Location: <strong>{locStr || 'N/A'}</strong> | Period: <strong>{startStr} - {endStr}</strong>
                                        </div>
                                        {jEntry.description && (
                                          <div style={{ color: '#12213A', fontSize: '12.5px', marginTop: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {jEntry.description}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('experience-background')}>
                              <Edit2 size={13} /> Edit Experience Details
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-experience-background">
                          
                          {/* ============================================================
                             SUBSECTION 1: INTERNATIONAL TEACHING EXPERIENCE
                             ============================================================ */}
                          <div style={{ marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                              <Globe2 size={18} color="#079447" />
                              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#12213A' }}>
                                International Teaching Experience
                              </h4>
                            </div>

                            <div className="apply-field" style={{ maxWidth: '440px', marginBottom: '16px' }}>
                              <label className="apply-label">Do you have international teaching experience? <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <Globe2 className="premium-field-icon" />
                                <select
                                  className={`apply-select premium-input has-left-icon${formErrors.intl_has_experience ? ' apply-input-error' : ''}`}
                                  value={formData.intl_has_experience}
                                  onChange={e => {
                                    const val = e.target.value;
                                    setFormData(prev => ({
                                      ...prev,
                                      intl_has_experience: val,
                                      ...(val === 'No' ? {
                                        intl_taught_outside_home: '', intl_taught_online_intl: '',
                                        intl_teaching_mode: '', intl_student_count: '',
                                        intl_teaching_hours: '', intl_description: '',
                                        intl_evidence_url: '', intl_countries_taught: [],
                                        intl_student_countries: [], intl_courses: [], intl_organisations: []
                                      } : {})
                                    }));
                                    if (formErrors.intl_has_experience) setFormErrors(prev => ({ ...prev, intl_has_experience: '' }));
                                  }}
                                >
                                  <option value="">Select an option</option>
                                  <option value="Yes">Yes</option>
                                  <option value="No">No</option>
                                </select>
                                <ChevronDown className="premium-select-chevron" />
                              </div>
                              {formErrors.intl_has_experience && <span className="apply-error-text">{formErrors.intl_has_experience}</span>}
                            </div>

                            {/* Conditional details for International */}
                            {formData.intl_has_experience === 'Yes' && (
                              <div style={{
                                background: '#f0f8f5',
                                border: '1.5px solid #b5d4c8',
                                borderRadius: '14px',
                                padding: '20px 20px 16px',
                                marginTop: '4px'
                              }}>
                                <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#4d7060', fontStyle: 'italic' }}>
                                  Please separate international experience from domestic to provide accurate information.
                                </p>

                                <div className="apply-grid-2" style={{ marginBottom: '16px' }}>
                                  <div className="apply-field">
                                    <label className="apply-label">Have you physically taught outside your home country? <span className="apply-required-star">*</span></label>
                                    <div className="premium-field">
                                      <Globe2 className="premium-field-icon" />
                                      <select className={`apply-select premium-input has-left-icon${formErrors.intl_taught_outside_home ? ' apply-input-error' : ''}`} value={formData.intl_taught_outside_home}
                                        onChange={e => {
                                          const val = e.target.value;
                                          setFormData(prev => ({ ...prev, intl_taught_outside_home: val, ...(val === 'No' ? { intl_countries_taught: [] } : {}) }));
                                          if (formErrors.intl_taught_outside_home) setFormErrors(p => ({ ...p, intl_taught_outside_home: '' }));
                                        }}>
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                      </select>
                                      <ChevronDown className="premium-select-chevron" />
                                    </div>
                                    {formErrors.intl_taught_outside_home && <span className="apply-error-text">{formErrors.intl_taught_outside_home}</span>}
                                  </div>
                                  <div className="apply-field">
                                    <label className="apply-label">Have you taught international students online? <span className="apply-required-star">*</span></label>
                                    <div className="premium-field">
                                      <Globe2 className="premium-field-icon" />
                                      <select className={`apply-select premium-input has-left-icon${formErrors.intl_taught_online_intl ? ' apply-input-error' : ''}`} value={formData.intl_taught_online_intl}
                                        onChange={e => {
                                          const val = e.target.value;
                                          setFormData(prev => ({ ...prev, intl_taught_online_intl: val, ...(val === 'No' ? { intl_student_countries: [] } : {}) }));
                                          if (formErrors.intl_taught_online_intl) setFormErrors(p => ({ ...p, intl_taught_online_intl: '' }));
                                        }}>
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                      </select>
                                      <ChevronDown className="premium-select-chevron" />
                                    </div>
                                    {formErrors.intl_taught_online_intl && <span className="apply-error-text">{formErrors.intl_taught_online_intl}</span>}
                                  </div>
                                </div>

                                <div className="apply-grid-2" style={{ marginBottom: '16px' }}>
                                  {formData.intl_taught_outside_home === 'Yes' && (
                                    <div className="apply-field" ref={intlCountriesRef}>
                                      <label className="apply-label">Countries Where You Have Taught <span className="apply-required-star">*</span></label>
                                      <div style={{ position: 'relative' }}>
                                        <div
                                          className={`apply-tag-container${formErrors.intl_countries_taught ? ' apply-input-error' : ''}`}
                                          style={{ minHeight: '46px', cursor: 'text' }}
                                          onClick={() => {
                                            setShowIntlCountriesDropdown(prev => !prev);
                                            setShowIntlStudentCountriesDropdown(false);
                                            setShowDomesticCoursesDropdown(false);
                                            setShowIntlCoursesDropdown(false);
                                          }}
                                        >
                                          {formData.intl_countries_taught.length === 0 ? (
                                            <span className="apply-tag-placeholder">Search and select countries…</span>
                                          ) : (
                                            formData.intl_countries_taught.map(c => (
                                              <span key={c} className="apply-tag">
                                                {c}
                                                <button type="button" className="apply-tag-remove" aria-label={`Remove ${c}`} onClick={ev => { ev.stopPropagation(); setFormData(prev => ({ ...prev, intl_countries_taught: prev.intl_countries_taught.filter(x => x !== c) })); }}>×</button>
                                              </span>
                                            ))
                                          )}
                                        </div>
                                        {showIntlCountriesDropdown && (
                                          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999, width: '100%', maxHeight: '250px', backgroundColor: '#fff', border: '1px solid #d7e2dc', borderRadius: '10px', boxShadow: '0 8px 24px rgba(12,79,71,0.1)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                            <div style={{ padding: '8px', borderBottom: '1px solid #f0f4f2', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <input type="text" placeholder="Search country…" value={intlCountriesSearch} onChange={e => setIntlCountriesSearch(e.target.value)} autoFocus style={{ flex: 1, padding: '7px 10px', border: '1px solid #d7e2dc', borderRadius: '6px', fontSize: '13px', outline: 'none' }} />
                                              <button
                                                type="button"
                                                aria-label="Close Countries Where You Have Taught dropdown"
                                                title="Close dropdown"
                                                onClick={ev => {
                                                  ev.stopPropagation();
                                                  ev.preventDefault();
                                                  setShowIntlCountriesDropdown(false);
                                                }}
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  width: '36px',
                                                  height: '36px',
                                                  minWidth: '36px',
                                                  minHeight: '36px',
                                                  border: 'none',
                                                  background: '#f0f4f2',
                                                  borderRadius: '6px',
                                                  color: '#0c4f47',
                                                  cursor: 'pointer',
                                                  padding: 0
                                                }}
                                              >
                                                <X size={18} />
                                              </button>
                                            </div>
                                            <div style={{ overflowY: 'auto', flex: 1 }}>
                                              {countries.filter(c => c.name.toLowerCase().includes(intlCountriesSearch.toLowerCase()) && !formData.intl_countries_taught.includes(c.name)).map(c => (
                                                <div key={c.code} onClick={() => { setFormData(prev => ({ ...prev, intl_countries_taught: [...prev.intl_countries_taught, c.name] })); setIntlCountriesSearch(''); if (formErrors.intl_countries_taught) setFormErrors(p => ({ ...p, intl_countries_taught: '' })); }}
                                                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                                  onMouseEnter={e => e.currentTarget.style.background = '#f0f8f5'}
                                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                  <span style={{ fontSize: '1.1rem' }}>{c.flag}</span><span>{c.name}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      {formErrors.intl_countries_taught && <span className="apply-error-text">{formErrors.intl_countries_taught}</span>}
                                    </div>
                                  )}
                                  <div className="apply-field">
                                    <label className="apply-label">International Teaching Mode <span className="apply-required-star">*</span></label>
                                    <div className="premium-field">
                                      <Globe2 className="premium-field-icon" />
                                      <select className={`apply-select premium-input has-left-icon${formErrors.intl_teaching_mode ? ' apply-input-error' : ''}`} value={formData.intl_teaching_mode}
                                        onChange={e => { setFormData(prev => ({ ...prev, intl_teaching_mode: e.target.value })); if (formErrors.intl_teaching_mode) setFormErrors(p => ({ ...p, intl_teaching_mode: '' })); }}>
                                        <option value="">Select mode</option>
                                        <option value="Online">Online</option>
                                        <option value="In-person">In-person</option>
                                        <option value="Hybrid">Hybrid</option>
                                        <option value="Both online and in-person">Both online and in-person</option>
                                      </select>
                                      <ChevronDown className="premium-select-chevron" />
                                    </div>
                                    {formErrors.intl_teaching_mode && <span className="apply-error-text">{formErrors.intl_teaching_mode}</span>}
                                  </div>
                                </div>

                                <div className="apply-grid-2" style={{ marginBottom: '16px' }}>
                                  <div className="apply-field" ref={intlCoursesRef}>
                                    <label className="apply-label">Courses / Subjects Taught Internationally <span className="apply-required-star">*</span></label>
                                    <div style={{ position: 'relative' }}>
                                      <div
                                        className={`apply-tag-container${formErrors.intl_courses ? ' apply-input-error' : ''}`}
                                        style={{ minHeight: '46px', cursor: 'text' }}
                                        onClick={() => {
                                          setShowIntlCountriesDropdown(false);
                                          setShowIntlStudentCountriesDropdown(false);
                                          setShowIntlCoursesDropdown(prev => !prev);
                                        }}
                                      >
                                        {formData.intl_courses.length === 0 ? (
                                          <span className="apply-tag-placeholder">Search or add courses…</span>
                                        ) : (
                                          formData.intl_courses.map(c => (
                                            <span key={c} className="apply-tag">
                                              {c}
                                              <button type="button" className="apply-tag-remove" aria-label={`Remove ${c}`} onClick={ev => { ev.stopPropagation(); setFormData(prev => ({ ...prev, intl_courses: prev.intl_courses.filter(x => x !== c) })); }}>×</button>
                                            </span>
                                          ))
                                        )}
                                      </div>
                                      {showIntlCoursesDropdown && (
                                        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999, width: '100%', maxHeight: '250px', backgroundColor: '#fff', border: '1px solid #d7e2dc', borderRadius: '10px', boxShadow: '0 8px 24px rgba(12,79,71,0.1)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                          <div style={{ padding: '8px', borderBottom: '1px solid #f0f4f2', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input type="text" placeholder="Search or type a course…" value={intlCoursesSearch} onChange={e => setIntlCoursesSearch(e.target.value)} autoFocus style={{ flex: 1, padding: '7px 10px', border: '1px solid #d7e2dc', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                                              onKeyDown={e => {
                                                if (e.key === 'Enter' && intlCoursesSearch.trim() && !formData.intl_courses.includes(intlCoursesSearch.trim())) {
                                                  setFormData(prev => ({ ...prev, intl_courses: [...prev.intl_courses, intlCoursesSearch.trim()] }));
                                                  setIntlCoursesSearch('');
                                                  e.preventDefault();
                                                }
                                              }}
                                            />
                                            <button
                                              type="button"
                                              aria-label="Close Courses / Subjects Taught Internationally dropdown"
                                              title="Close dropdown"
                                              onClick={ev => {
                                                ev.stopPropagation();
                                                ev.preventDefault();
                                                setShowIntlCoursesDropdown(false);
                                              }}
                                              style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '36px',
                                                height: '36px',
                                                minWidth: '36px',
                                                minHeight: '36px',
                                                border: 'none',
                                                background: '#f0f4f2',
                                                borderRadius: '6px',
                                                color: '#0c4f47',
                                                cursor: 'pointer',
                                                padding: 0
                                              }}
                                            >
                                              <X size={18} />
                                            </button>
                                          </div>
                                          <div style={{ overflowY: 'auto', flex: 1 }}>
                                            {intlCourseOptions.filter(c => c.toLowerCase().includes(intlCoursesSearch.toLowerCase()) && !formData.intl_courses.includes(c)).map(c => (
                                              <div key={c} onClick={() => { setFormData(prev => ({ ...prev, intl_courses: [...prev.intl_courses, c] })); setIntlCoursesSearch(''); if (formErrors.intl_courses) setFormErrors(p => ({ ...p, intl_courses: '' })); }}
                                                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f0f8f5'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                {c}
                                              </div>
                                            ))}
                                            {intlCoursesSearch.trim() && !intlCourseOptions.includes(intlCoursesSearch.trim()) && !formData.intl_courses.includes(intlCoursesSearch.trim()) && (
                                              <div onClick={() => { setFormData(prev => ({ ...prev, intl_courses: [...prev.intl_courses, intlCoursesSearch.trim()] })); setIntlCoursesSearch(''); }}
                                                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: '#0c4f47', fontWeight: 600 }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f0f8f5'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                + Add "{intlCoursesSearch.trim()}"
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    {formErrors.intl_courses && <span className="apply-error-text">{formErrors.intl_courses}</span>}
                                  </div>
                                  <div className="apply-field">
                                    <label className="apply-label">Approximate Number of International Students Taught <span className="apply-required-star">*</span></label>
                                    <div className="premium-field">
                                      <Users className="premium-field-icon" />
                                      <input type="number" min="0" className={`apply-input premium-input has-left-icon${formErrors.intl_student_count ? ' apply-input-error' : ''}`} placeholder="e.g. 150"
                                        value={formData.intl_student_count}
                                        onChange={e => { setFormData(prev => ({ ...prev, intl_student_count: e.target.value })); if (formErrors.intl_student_count) setFormErrors(p => ({ ...p, intl_student_count: '' })); }} />
                                    </div>
                                    {formErrors.intl_student_count && <span className="apply-error-text">{formErrors.intl_student_count}</span>}
                                  </div>
                                </div>

                                <div className="apply-grid-2" style={{ marginBottom: '16px' }}>
                                  <div className="apply-field">
                                    <label className="apply-label">Approximate International Teaching Hours</label>
                                    <div className="premium-field">
                                      <Timer className="premium-field-icon" />
                                      <input type="number" min="0" className="apply-input premium-input has-left-icon" placeholder="e.g. 500"
                                        value={formData.intl_teaching_hours}
                                        onChange={e => setFormData(prev => ({ ...prev, intl_teaching_hours: e.target.value }))} />
                                    </div>
                                  </div>
                                  <div className="apply-field">
                                    <label className="apply-label">International Schools / Organisations</label>
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                      {formData.intl_organisations.map(o => (
                                        <span key={o} className="apply-tag">
                                          {o}
                                          <button type="button" className="apply-tag-remove" onClick={() => setFormData(prev => ({ ...prev, intl_organisations: prev.intl_organisations.filter(x => x !== o) }))}>×</button>
                                        </span>
                                      ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                      <input type="text" className="apply-input" placeholder="e.g. Berlin Yoga Centre" value={intlOrgInput} onChange={e => setIntlOrgInput(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (intlOrgInput.trim() && !formData.intl_organisations.includes(intlOrgInput.trim())) { setFormData(prev => ({ ...prev, intl_organisations: [...prev.intl_organisations, intlOrgInput.trim()] })); setIntlOrgInput(''); } } }}
                                        style={{ flex: 1 }} />
                                      <button type="button" className="apply-btn apply-btn-primary" style={{ height: '46px', padding: '0 14px', flexShrink: 0 }}
                                        onClick={() => { if (intlOrgInput.trim() && !formData.intl_organisations.includes(intlOrgInput.trim())) { setFormData(prev => ({ ...prev, intl_organisations: [...prev.intl_organisations, intlOrgInput.trim()] })); setIntlOrgInput(''); } }}>
                                        + Add
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {formData.intl_taught_online_intl === 'Yes' && (
                                  <div className="apply-field" ref={intlStudentCountriesRef} style={{ marginBottom: '16px' }}>
                                    <label className="apply-label">Countries of International Students Taught Online <span className="apply-required-star">*</span></label>
                                    <p className="apply-field-hint" style={{ marginBottom: '6px' }}>
                                      <Info size={11} /> Select countries your online students came from (even if you were based in India).
                                    </p>
                                    <div style={{ position: 'relative' }}>
                                      <div
                                        className={`apply-tag-container${formErrors.intl_student_countries ? ' apply-input-error' : ''}`}
                                        style={{ minHeight: '46px', cursor: 'text' }}
                                        onClick={() => {
                                          setShowIntlCountriesDropdown(false);
                                          setShowIntlCoursesDropdown(false);
                                          setShowIntlStudentCountriesDropdown(prev => !prev);
                                        }}
                                      >
                                        {formData.intl_student_countries.length === 0 ? (
                                          <span className="apply-tag-placeholder">Search and select countries…</span>
                                        ) : (
                                          formData.intl_student_countries.map(c => (
                                            <span key={c} className="apply-tag">
                                              {c}
                                              <button type="button" className="apply-tag-remove" aria-label={`Remove ${c}`} onClick={ev => { ev.stopPropagation(); setFormData(prev => ({ ...prev, intl_student_countries: prev.intl_student_countries.filter(x => x !== c) })); }}>×</button>
                                            </span>
                                          ))
                                        )}
                                      </div>
                                      {showIntlStudentCountriesDropdown && (
                                        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999, width: '100%', maxHeight: '250px', backgroundColor: '#fff', border: '1px solid #d7e2dc', borderRadius: '10px', boxShadow: '0 8px 24px rgba(12,79,71,0.1)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                          <div style={{ padding: '8px', borderBottom: '1px solid #f0f4f2', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input type="text" placeholder="Search country…" value={intlStudentCountriesSearch} onChange={e => setIntlStudentCountriesSearch(e.target.value)} autoFocus style={{ flex: 1, padding: '7px 10px', border: '1px solid #d7e2dc', borderRadius: '6px', fontSize: '13px', outline: 'none' }} />
                                            <button
                                              type="button"
                                              aria-label="Close Countries of International Students Taught Online dropdown"
                                              title="Close dropdown"
                                              onClick={ev => {
                                                ev.stopPropagation();
                                                ev.preventDefault();
                                                setShowIntlStudentCountriesDropdown(false);
                                              }}
                                              style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '36px',
                                                height: '36px',
                                                minWidth: '36px',
                                                minHeight: '36px',
                                                border: 'none',
                                                background: '#f0f4f2',
                                                borderRadius: '6px',
                                                color: '#0c4f47',
                                                cursor: 'pointer',
                                                padding: 0
                                              }}
                                            >
                                              <X size={18} />
                                            </button>
                                          </div>
                                          <div style={{ overflowY: 'auto', flex: 1 }}>
                                            {countries.filter(c => c.name.toLowerCase().includes(intlStudentCountriesSearch.toLowerCase()) && !formData.intl_student_countries.includes(c.name)).map(c => (
                                              <div key={c.code} onClick={() => { setFormData(prev => ({ ...prev, intl_student_countries: [...prev.intl_student_countries, c.name] })); setIntlStudentCountriesSearch(''); if (formErrors.intl_student_countries) setFormErrors(p => ({ ...p, intl_student_countries: '' })); }}
                                                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f0f8f5'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                <span style={{ fontSize: '1.1rem' }}>{c.flag}</span><span>{c.name}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    {formErrors.intl_student_countries && <span className="apply-error-text">{formErrors.intl_student_countries}</span>}
                                  </div>
                                )}

                                {/* Row 6: Description textarea (full width) */}
                                <div className="apply-field" style={{ marginBottom: '16px' }}>
                                  <label className="apply-label">Briefly Describe Your International Teaching Experience <span className="apply-required-star">*</span></label>
                                  <div style={{ position: 'relative' }}>
                                    <textarea
                                      className={`apply-textarea${formErrors.intl_description ? ' apply-input-error' : ''}`}
                                      maxLength={1000}
                                      rows={4}
                                      placeholder="Mention the countries, courses, teaching mode, organisations and the type of international students you taught."
                                      value={formData.intl_description}
                                      onChange={e => { setFormData(prev => ({ ...prev, intl_description: e.target.value })); if (formErrors.intl_description) setFormErrors(p => ({ ...p, intl_description: '' })); }}
                                      style={{ paddingBottom: '28px' }}
                                    />
                                    <span style={{ position: 'absolute', bottom: '8px', right: '12px', fontSize: '11px', color: formData.intl_description.length >= 950 ? '#c0392b' : '#94a39c', fontWeight: 500 }}>
                                      {formData.intl_description.length}/1000
                                    </span>
                                  </div>
                                  {formErrors.intl_description && <span className="apply-error-text">{formErrors.intl_description}</span>}
                                </div>

                                {/* Row 7: Evidence URL (full width, optional) */}
                                <div className="apply-field">
                                  <label className="apply-label">Supporting Document or Evidence Link</label>
                                  <div className="premium-field">
                                    <Link2 className="premium-field-icon" />
                                    <input type="text" inputMode="url" className="apply-input premium-input has-left-icon" placeholder="https://drive.google.com/…"
                                      value={formData.intl_evidence_url}
                                      onChange={e => setFormData(prev => ({ ...prev, intl_evidence_url: e.target.value }))} />
                                  </div>
                                  <p className="apply-field-hint">
                                    <Info size={11} /> Upload supporting documents (certificates, event pages, school profiles) to Google Drive and paste an "Anyone with the link can view" URL.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* ============================================================
                             SUBSECTION 2: DOMESTIC TEACHING EXPERIENCE – INDIA
                             ============================================================ */}
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <MapPin size={18} color="#079447" />
                              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#12213A' }}>
                                Domestic Teaching Experience – India
                              </h4>
                            </div>
                            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>
                              Please provide details of your teaching experience with students within India, including both online and in-person instruction.
                            </p>

                            <div className="apply-field" style={{ maxWidth: '440px', marginBottom: '16px' }}>
                              <label className="apply-label">Have you taught students within India? <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <MapPin className="premium-field-icon" />
                                <select
                                  className={`apply-select premium-input has-left-icon${formErrors.domestic_has_experience ? ' apply-input-error' : ''}`}
                                  value={formData.domestic_has_experience}
                                  onChange={e => {
                                    const val = e.target.value;
                                    setFormData(prev => ({
                                      ...prev,
                                      domestic_has_experience: val,
                                      ...(val === 'No' ? {
                                        domestic_teaching_mode: '', domestic_courses_taught: [],
                                        domestic_taught_online: '', domestic_online_student_count: '',
                                        domestic_taught_in_person: '', domestic_in_person_student_count: '',
                                        domestic_total_student_count: '', domestic_teaching_hours: '',
                                        domestic_organisations: [], domestic_description: ''
                                      } : {})
                                    }));
                                    if (formErrors.domestic_has_experience) setFormErrors(prev => ({ ...prev, domestic_has_experience: '' }));
                                  }}
                                >
                                  <option value="">Select an option</option>
                                  <option value="Yes">Yes</option>
                                  <option value="No">No</option>
                                </select>
                                <ChevronDown className="premium-select-chevron" />
                              </div>
                              {formErrors.domestic_has_experience && <span className="apply-error-text">{formErrors.domestic_has_experience}</span>}
                            </div>

                            {/* Conditional details for Domestic */}
                            {formData.domestic_has_experience === 'Yes' && (
                              <div style={{
                                background: '#f8fafc',
                                border: '1.5px solid #cbd5e1',
                                borderRadius: '14px',
                                padding: '20px 20px 16px',
                                marginTop: '4px'
                              }}>
                                {/* Row 1: Mode + Courses Taught in India */}
                                <div className="apply-grid-2" style={{ marginBottom: '16px' }}>
                                  <div className="apply-field">
                                    <label className="apply-label">Domestic Teaching Mode <span className="apply-required-star">*</span></label>
                                    <div className="premium-field">
                                      <MapPin className="premium-field-icon" />
                                      <select className={`apply-select premium-input has-left-icon${formErrors.domestic_teaching_mode ? ' apply-input-error' : ''}`} value={formData.domestic_teaching_mode}
                                        onChange={e => { setFormData(prev => ({ ...prev, domestic_teaching_mode: e.target.value })); if (formErrors.domestic_teaching_mode) setFormErrors(p => ({ ...p, domestic_teaching_mode: '' })); }}>
                                        <option value="">Select mode</option>
                                        <option value="Online">Online</option>
                                        <option value="In-person">In-person</option>
                                        <option value="Hybrid">Hybrid</option>
                                      </select>
                                      <ChevronDown className="premium-select-chevron" />
                                    </div>
                                    {formErrors.domestic_teaching_mode && <span className="apply-error-text">{formErrors.domestic_teaching_mode}</span>}
                                  </div>

                                  <div className="apply-field" ref={domesticCoursesRef}>
                                    <label className="apply-label">Courses / Subjects Taught in India <span className="apply-required-star">*</span></label>
                                    <div style={{ position: 'relative' }}>
                                      <div className={`apply-tag-container${formErrors.domestic_courses_taught ? ' apply-input-error' : ''}`} style={{ minHeight: '46px', cursor: 'text' }}
                                        onClick={() => {
                                          setShowIntlCountriesDropdown(false);
                                          setShowIntlCoursesDropdown(false);
                                          setShowIntlStudentCountriesDropdown(false);
                                          setShowDomesticCoursesDropdown(prev => !prev);
                                        }}>
                                        {formData.domestic_courses_taught.length === 0 ? (
                                          <span className="apply-tag-placeholder">Search or add courses…</span>
                                        ) : (
                                          formData.domestic_courses_taught.map(c => (
                                            <span key={c} className="apply-tag">
                                              {c}
                                              <button type="button" className="apply-tag-remove" aria-label={`Remove ${c}`} onClick={ev => { ev.stopPropagation(); setFormData(prev => ({ ...prev, domestic_courses_taught: prev.domestic_courses_taught.filter(x => x !== c) })); }}>×</button>
                                            </span>
                                          ))
                                        )}
                                      </div>
                                      {showDomesticCoursesDropdown && (
                                        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 9999, width: '100%', maxHeight: '250px', backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: '10px', boxShadow: '0 8px 24px rgba(18,33,58,0.1)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                          <div style={{ padding: '8px', borderBottom: '1px solid #f0f4f2', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input type="text" placeholder="Search or type a course…" value={domesticCoursesSearch} onChange={e => setDomesticCoursesSearch(e.target.value)} autoFocus style={{ flex: 1, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                                              onKeyDown={e => {
                                                if (e.key === 'Enter' && domesticCoursesSearch.trim() && !formData.domestic_courses_taught.includes(domesticCoursesSearch.trim())) {
                                                  setFormData(prev => ({ ...prev, domestic_courses_taught: [...prev.domestic_courses_taught, domesticCoursesSearch.trim()] }));
                                                  setDomesticCoursesSearch('');
                                                  e.preventDefault();
                                                }
                                              }}
                                            />
                                            <button
                                              type="button"
                                              aria-label="Close Courses / Subjects Taught in India dropdown"
                                              title="Close dropdown"
                                              onClick={ev => {
                                                ev.stopPropagation();
                                                ev.preventDefault();
                                                setShowDomesticCoursesDropdown(false);
                                              }}
                                              style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '36px',
                                                height: '36px',
                                                minWidth: '36px',
                                                minHeight: '36px',
                                                border: 'none',
                                                background: '#f0f4f2',
                                                borderRadius: '6px',
                                                color: '#0c4f47',
                                                cursor: 'pointer',
                                                padding: 0
                                              }}
                                            >
                                              <X size={18} />
                                            </button>
                                          </div>
                                          <div style={{ overflowY: 'auto', flex: 1 }}>
                                            {domesticCourseOptions.filter(c => c.toLowerCase().includes(domesticCoursesSearch.toLowerCase()) && !formData.domestic_courses_taught.includes(c)).map(c => (
                                              <div key={c} onClick={() => { setFormData(prev => ({ ...prev, domestic_courses_taught: [...prev.domestic_courses_taught, c] })); setDomesticCoursesSearch(''); if (formErrors.domestic_courses_taught) setFormErrors(p => ({ ...p, domestic_courses_taught: '' })); }}
                                                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f0f8f5'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                {c}
                                              </div>
                                            ))}
                                            {domesticCoursesSearch.trim() && !domesticCourseOptions.includes(domesticCoursesSearch.trim()) && !formData.domestic_courses_taught.includes(domesticCoursesSearch.trim()) && (
                                              <div onClick={() => { setFormData(prev => ({ ...prev, domestic_courses_taught: [...prev.domestic_courses_taught, domesticCoursesSearch.trim()] })); setDomesticCoursesSearch(''); }}
                                                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: '#079447', fontWeight: 600 }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f0f8f5'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                + Add "{domesticCoursesSearch.trim()}"
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    {formErrors.domestic_courses_taught && <span className="apply-error-text">{formErrors.domestic_courses_taught}</span>}
                                  </div>
                                </div>

                                {/* Row 2: Online questions & In-person questions */}
                                <div className="apply-grid-2" style={{ marginBottom: '16px' }}>
                                  <div className="apply-field">
                                    <label className="apply-label">Have you taught students online within India? <span className="apply-required-star">*</span></label>
                                    <div className="premium-field">
                                      <Globe2 className="premium-field-icon" />
                                      <select className={`apply-select premium-input has-left-icon${formErrors.domestic_taught_online ? ' apply-input-error' : ''}`} value={formData.domestic_taught_online}
                                        onChange={e => {
                                          const val = e.target.value;
                                          setFormData(prev => ({ ...prev, domestic_taught_online: val, ...(val === 'No' ? { domestic_online_student_count: '0' } : {}) }));
                                          if (formErrors.domestic_taught_online) setFormErrors(p => ({ ...p, domestic_taught_online: '' }));
                                        }}>
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                      </select>
                                      <ChevronDown className="premium-select-chevron" />
                                    </div>
                                    {formErrors.domestic_taught_online && <span className="apply-error-text">{formErrors.domestic_taught_online}</span>}
                                  </div>

                                  <div className="apply-field">
                                    <label className="apply-label">Have you taught students in person within India? <span className="apply-required-star">*</span></label>
                                    <div className="premium-field">
                                      <MapPin className="premium-field-icon" />
                                      <select className={`apply-select premium-input has-left-icon${formErrors.domestic_taught_in_person ? ' apply-input-error' : ''}`} value={formData.domestic_taught_in_person}
                                        onChange={e => {
                                          const val = e.target.value;
                                          setFormData(prev => ({ ...prev, domestic_taught_in_person: val, ...(val === 'No' ? { domestic_in_person_student_count: '0' } : {}) }));
                                          if (formErrors.domestic_taught_in_person) setFormErrors(p => ({ ...p, domestic_taught_in_person: '' }));
                                        }}>
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                      </select>
                                      <ChevronDown className="premium-select-chevron" />
                                    </div>
                                    {formErrors.domestic_taught_in_person && <span className="apply-error-text">{formErrors.domestic_taught_in_person}</span>}
                                  </div>
                                </div>

                                {/* Row 3: Conditional Student Counts */}
                                <div className="apply-grid-2" style={{ marginBottom: '16px' }}>
                                  {formData.domestic_taught_online === 'Yes' && (
                                    <div className="apply-field">
                                      <label className="apply-label">Approximate Students Taught Online in India <span className="apply-required-star">*</span></label>
                                      <div className="premium-field">
                                        <Users className="premium-field-icon" />
                                        <input type="number" min="0" className={`apply-input premium-input has-left-icon${formErrors.domestic_online_student_count ? ' apply-input-error' : ''}`} placeholder="e.g. 200"
                                          value={formData.domestic_online_student_count}
                                          onChange={e => { setFormData(prev => ({ ...prev, domestic_online_student_count: e.target.value })); if (formErrors.domestic_online_student_count) setFormErrors(p => ({ ...p, domestic_online_student_count: '' })); }} />
                                      </div>
                                      {formErrors.domestic_online_student_count && <span className="apply-error-text">{formErrors.domestic_online_student_count}</span>}
                                    </div>
                                  )}

                                  {formData.domestic_taught_in_person === 'Yes' && (
                                    <div className="apply-field">
                                      <label className="apply-label">Approximate Students Taught In Person in India <span className="apply-required-star">*</span></label>
                                      <div className="premium-field">
                                        <Users className="premium-field-icon" />
                                        <input type="number" min="0" className={`apply-input premium-input has-left-icon${formErrors.domestic_in_person_student_count ? ' apply-input-error' : ''}`} placeholder="e.g. 350"
                                          value={formData.domestic_in_person_student_count}
                                          onChange={e => { setFormData(prev => ({ ...prev, domestic_in_person_student_count: e.target.value })); if (formErrors.domestic_in_person_student_count) setFormErrors(p => ({ ...p, domestic_in_person_student_count: '' })); }} />
                                      </div>
                                      {formErrors.domestic_in_person_student_count && <span className="apply-error-text">{formErrors.domestic_in_person_student_count}</span>}
                                    </div>
                                  )}
                                </div>

                                {/* Row 4: Total Domestic Students Card (Auto-calculated metric display) */}
                                <div style={{
                                  background: 'linear-gradient(135deg, #eef8f2 0%, #ffffff 100%)',
                                  border: '1.5px solid #b5d4c8',
                                  borderRadius: '12px',
                                  padding: '14px 18px',
                                  marginBottom: '16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}>
                                  <div>
                                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#056d34' }}>Approximate Total Number of Students Taught in India</div>
                                    <div style={{ fontSize: '11.5px', color: '#475569' }}>Automatically calculated sum (Online + In-person)</div>
                                  </div>
                                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#079447' }}>
                                    {formData.domestic_total_student_count || '0'} <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Students</span>
                                  </div>
                                </div>

                                {/* Row 5: Hours + Organisations */}
                                <div className="apply-grid-2" style={{ marginBottom: '16px' }}>
                                  <div className="apply-field">
                                    <label className="apply-label">Approximate Domestic Teaching Hours</label>
                                    <div className="premium-field">
                                      <Timer className="premium-field-icon" />
                                      <input type="number" min="0" className="apply-input premium-input has-left-icon" placeholder="e.g. 800"
                                        value={formData.domestic_teaching_hours}
                                        onChange={e => setFormData(prev => ({ ...prev, domestic_teaching_hours: e.target.value }))} />
                                    </div>
                                  </div>

                                  <div className="apply-field">
                                    <label className="apply-label">Schools / Studios / Organisations Worked With in India</label>
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                      {formData.domestic_organisations.map(o => (
                                        <span key={o} className="apply-tag">
                                          {o}
                                          <button type="button" className="apply-tag-remove" onClick={() => setFormData(prev => ({ ...prev, domestic_organisations: prev.domestic_organisations.filter(x => x !== o) }))}>×</button>
                                        </span>
                                      ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                      <input type="text" className="apply-input" placeholder="e.g. Rishikesh Yoga Ashram" value={domesticOrgInput} onChange={e => setDomesticOrgInput(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (domesticOrgInput.trim() && !formData.domestic_organisations.includes(domesticOrgInput.trim())) { setFormData(prev => ({ ...prev, domestic_organisations: [...prev.domestic_organisations, domesticOrgInput.trim()] })); setDomesticOrgInput(''); } } }}
                                        style={{ flex: 1 }} />
                                      <button type="button" className="apply-btn apply-btn-primary" style={{ height: '46px', padding: '0 14px', flexShrink: 0 }}
                                        onClick={() => { if (domesticOrgInput.trim() && !formData.domestic_organisations.includes(domesticOrgInput.trim())) { setFormData(prev => ({ ...prev, domestic_organisations: [...prev.domestic_organisations, domesticOrgInput.trim()] })); setDomesticOrgInput(''); } }}>
                                        + Add
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* Row 6: Domestic Experience Description */}
                                <div className="apply-field" style={{ marginBottom: '12px' }}>
                                  <label className="apply-label">Briefly Describe Your Domestic Teaching Experience <span className="apply-required-star">*</span></label>
                                  <p className="apply-field-hint" style={{ marginBottom: '6px' }}>
                                    <Info size={11} /> Describe the courses you taught, the types of students you worked with, your teaching methods, the organisations you were associated with, and any significant outcomes or achievements.
                                  </p>
                                  <div style={{ position: 'relative' }}>
                                    <textarea
                                      className={`apply-textarea${formErrors.domestic_description ? ' apply-input-error' : ''}`}
                                      maxLength={1000}
                                      rows={4}
                                      placeholder="Describe your domestic teaching journey in India, student types, methods and achievements..."
                                      value={formData.domestic_description}
                                      onChange={e => { setFormData(prev => ({ ...prev, domestic_description: e.target.value })); if (formErrors.domestic_description) setFormErrors(p => ({ ...p, domestic_description: '' })); }}
                                      style={{ paddingBottom: '28px' }}
                                    />
                                    <span style={{ position: 'absolute', bottom: '8px', right: '12px', fontSize: '11px', color: formData.domestic_description.length >= 950 ? '#c0392b' : '#94a39c', fontWeight: 500 }}>
                                      {formData.domestic_description.length}/1000
                                    </span>
                                  </div>
                                  {formErrors.domestic_description && <span className="apply-error-text">{formErrors.domestic_description}</span>}
                                </div>
                              </div>
                            )}

                            {/* Previous Program Teaching Experience Question */}
                            <div className="apply-field" style={{ marginTop: '24px', marginBottom: '20px', borderTop: '1px dashed #c8dbd4', paddingTop: '20px' }}>
                              <h4 style={{ fontSize: '15.5px', fontWeight: 700, color: '#0c4f47', margin: '0 0 6px 0' }}>
                                Previous Program Teaching Experience
                              </h4>
                              <p style={{ fontSize: '13.5px', color: '#4d7060', margin: '0 0 14px 0', lineHeight: 1.4 }}>
                                Tell us whether you have previously taught any of the complete programs selected in this application.
                              </p>
                              <label className="apply-label">Have You Previously Taught the Selected Programs? <span className="apply-required-star">*</span></label>
                              <div className="premium-field" style={{ maxWidth: '320px' }}>
                                <select
                                  className={`apply-select premium-input${formErrors.previously_taught_choice ? ' apply-input-error' : ''}`}
                                  value={formData.previously_taught_choice}
                                  onChange={e => {
                                    const val = e.target.value;
                                    setFormData(prev => ({
                                      ...prev,
                                      previously_taught_choice: val,
                                      ...(val === 'No' ? {
                                        prev_teaching_desc: '',
                                        prev_batches_taught: '',
                                        prev_recent_year: '',
                                        prev_students_taught: '',
                                        previously_taught_course_ids: []
                                      } : {})
                                    }));
                                    if (formErrors.previously_taught_choice) {
                                      setFormErrors(p => ({ ...p, previously_taught_choice: '' }));
                                    }
                                  }}
                                >
                                  <option value="">Select option</option>
                                  <option value="Yes">Yes</option>
                                  <option value="No">No</option>
                                </select>
                                <ChevronDown className="premium-select-chevron" />
                              </div>
                              {formErrors.previously_taught_choice && <span className="apply-error-text">{formErrors.previously_taught_choice}</span>}
                            </div>

                            {/* Conditional sub-fields when Yes: Repeatable Program Experience Cards */}
                            {formData.previously_taught_choice === 'Yes' && (
                              <div style={{ background: '#f4faf6', border: '1.5px solid #b6dfc6', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                                <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 700, color: '#0c4f47' }}>
                                  Tell us more about your previous teaching experience with these programs
                                </h4>
                                <p style={{ fontSize: '13px', color: '#4d7060', margin: '0 0 16px 0' }}>
                                  Add separate experience details for each program you have taught previously.
                                </p>

                                {formErrors.previous_course_experiences && (
                                  <span className="apply-error-text" style={{ marginBottom: '12px', display: 'block' }}>
                                    {formErrors.previous_course_experiences}
                                  </span>
                                )}

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '18px' }}>
                                  {(Array.isArray(formData.previous_course_experiences) ? formData.previous_course_experiences : []).map((exp, idx) => {
                                    const selectableOptions = getSelectableCoursesOptions();
                                    const otherSelected = (formData.previous_course_experiences || [])
                                      .filter((_, i) => i !== idx)
                                      .map(e => e.courseName)
                                      .filter(Boolean);
                                    const isDuplicate = exp.courseName && exp.courseName !== 'Other' && otherSelected.includes(exp.courseName);

                                    // READ-ONLY SUMMARY CARD
                                    if (!exp.isEditing) {
                                      return (
                                        <div
                                          key={exp.localId || `exp-${idx}`}
                                          style={{
                                            background: '#ffffff',
                                            border: '1.5px solid #c8dbd4',
                                            borderRadius: '12px',
                                            padding: '18px 20px',
                                            boxShadow: '0 2px 8px rgba(12,79,71,0.04)'
                                          }}
                                        >
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #eef4f1', paddingBottom: '10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <span style={{ fontSize: '14.5px', fontWeight: 700, color: '#0c4f47' }}>
                                                Program Experience {idx + 1}
                                              </span>
                                              <span style={{ fontSize: '11px', fontWeight: 650, color: '#079447', background: '#eef8f2', border: '1px solid #c3e6cb', padding: '2px 8px', borderRadius: '10px' }}>
                                                Completed
                                              </span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                              <button
                                                type="button"
                                                className="apply-btn-action"
                                                onClick={() => handleEditCourseExperience(idx)}
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: '5px',
                                                  padding: '6px 14px',
                                                  borderRadius: '7px',
                                                  border: '1.5px solid #0c4f47',
                                                  background: '#ffffff',
                                                  color: '#0c4f47',
                                                  fontWeight: 650,
                                                  fontSize: '12.5px',
                                                  cursor: 'pointer'
                                                }}
                                              >
                                                <Edit2 size={13} /> Edit
                                              </button>
                                              <button
                                                type="button"
                                                className="apply-btn-action apply-btn-remove"
                                                onClick={() => handleRemoveCourseExperience(idx)}
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: '5px',
                                                  padding: '6px 14px',
                                                  borderRadius: '7px',
                                                  border: '1px solid #fecaca',
                                                  background: '#fff5f5',
                                                  color: '#e53e3e',
                                                  fontWeight: 600,
                                                  fontSize: '12.5px',
                                                  cursor: 'pointer'
                                                }}
                                              >
                                                <Trash2 size={13} /> Remove
                                              </button>
                                            </div>
                                          </div>

                                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px 24px' }}>
                                            <div>
                                              <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '3px' }}>
                                                Course or Program
                                              </span>
                                              <strong style={{ fontSize: '14px', color: '#12213A', fontWeight: 700 }}>
                                                {exp.courseName || 'N/A'}
                                              </strong>
                                            </div>

                                            <div>
                                              <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '3px' }}>
                                                Where Taught
                                              </span>
                                              <span style={{ fontSize: '14px', color: '#12213A', fontWeight: 500 }}>
                                                {exp.teachingLocation || 'N/A'}
                                              </span>
                                            </div>

                                            <div>
                                              <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '3px' }}>
                                                Teaching Duration
                                              </span>
                                              <span style={{ fontSize: '14px', color: '#12213A', fontWeight: 500 }}>
                                                {formatDuration(exp.experienceYears, exp.experienceMonths)}
                                              </span>
                                            </div>

                                            <div>
                                              <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '3px' }}>
                                                Approximate Students Taught
                                              </span>
                                              <strong style={{ fontSize: '14px', color: '#0c4f47', fontWeight: 700 }}>
                                                {exp.studentsTaught || 'N/A'}
                                              </strong>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }

                                    // EDITABLE INPUT FORM
                                    return (
                                      <div
                                        key={exp.localId || `exp-${idx}`}
                                        style={{
                                          background: '#ffffff',
                                          border: '1.5px solid #c8dbd4',
                                          borderRadius: '12px',
                                          padding: '16px 18px',
                                          boxShadow: '0 2px 8px rgba(12,79,71,0.04)'
                                        }}
                                      >
                                        {/* Card Header */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #eef4f1', paddingBottom: '8px' }}>
                                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0c4f47' }}>
                                            Program Experience {idx + 1}
                                          </span>
                                          <button
                                            type="button"
                                            className="apply-btn-action apply-btn-remove"
                                            aria-label={`Remove Program Experience ${idx + 1}`}
                                            onClick={() => handleRemoveCourseExperience(idx)}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12.5px' }}
                                          >
                                            <Trash2 size={13} /> Remove Course Experience
                                          </button>
                                        </div>

                                        {/* Field 1: Which selected course or program have you taught previously? */}
                                        <div className="apply-field" style={{ marginBottom: '12px' }}>
                                          <label className="apply-label">Which selected course or program have you taught previously? <span className="apply-required-star">*</span></label>
                                          <div className="premium-field">
                                            <select
                                              className={`apply-select premium-input${formErrors[`prev_exp_${idx}_courseName`] ? ' apply-input-error' : ''}`}
                                              value={exp.courseName}
                                              onChange={e => handleCourseExperienceChange(idx, 'courseName', e.target.value)}
                                            >
                                              <option value="">Select a previously taught program</option>
                                              {selectableOptions.map(opt => {
                                                const isDisabled = otherSelected.includes(opt);
                                                return (
                                                  <option key={opt} value={opt} disabled={isDisabled}>
                                                    {opt} {isDisabled ? '(Already added)' : ''}
                                                  </option>
                                                );
                                              })}
                                              <option value="Other">Other — Program Not Listed Above</option>
                                            </select>
                                            <ChevronDown className="premium-select-chevron" />
                                          </div>
                                          {isDuplicate && (
                                            <span className="apply-error-text" style={{ marginTop: '4px', display: 'block', color: '#d97706' }}>
                                              This program has already been added.
                                            </span>
                                          )}
                                          {formErrors[`prev_exp_${idx}_courseName`] && (
                                            <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>
                                              {formErrors[`prev_exp_${idx}_courseName`]}
                                            </span>
                                          )}
                                        </div>

                                        {/* Field 2: Where did you teach this course? */}
                                        <div className="apply-field" style={{ marginBottom: '12px' }}>
                                          <label className="apply-label">Where did you teach this course? <span className="apply-required-star">*</span></label>
                                          <input
                                            type="text"
                                            className={`apply-input${formErrors[`prev_exp_${idx}_teachingLocation`] ? ' apply-input-error' : ''}`}
                                            placeholder="Studio name, online platform or school name"
                                            value={exp.teachingLocation}
                                            onChange={e => handleCourseExperienceChange(idx, 'teachingLocation', e.target.value)}
                                          />
                                          {formErrors[`prev_exp_${idx}_teachingLocation`] && (
                                            <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>
                                              {formErrors[`prev_exp_${idx}_teachingLocation`]}
                                            </span>
                                          )}
                                        </div>

                                        {/* Field 3 & Field 4 Grid */}
                                        <div className="apply-grid-2">
                                          <div className="apply-field">
                                            <label className="apply-label">How long did you teach this course? <span className="apply-required-star">*</span></label>
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                              <div style={{ flex: 1 }}>
                                                <div className="premium-field">
                                                  <input
                                                    type="number"
                                                    min="0"
                                                    className={`apply-input premium-input${formErrors[`prev_exp_${idx}_experienceYears`] ? ' apply-input-error' : ''}`}
                                                    placeholder="Years (e.g. 3)"
                                                    value={exp.experienceYears}
                                                    onChange={e => handleCourseExperienceChange(idx, 'experienceYears', e.target.value)}
                                                  />
                                                </div>
                                                <span style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', display: 'block' }}>Years</span>
                                              </div>
                                              <div style={{ flex: 1 }}>
                                                <div className="premium-field">
                                                  <input
                                                    type="number"
                                                    min="0"
                                                    max="11"
                                                    className={`apply-input premium-input${formErrors[`prev_exp_${idx}_experienceMonths`] ? ' apply-input-error' : ''}`}
                                                    placeholder="Months (0-11)"
                                                    value={exp.experienceMonths}
                                                    onChange={e => handleCourseExperienceChange(idx, 'experienceMonths', e.target.value)}
                                                  />
                                                </div>
                                                <span style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', display: 'block' }}>Months</span>
                                              </div>
                                            </div>
                                            {formErrors[`prev_exp_${idx}_experienceYears`] && (
                                              <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>
                                                {formErrors[`prev_exp_${idx}_experienceYears`]}
                                              </span>
                                            )}
                                            {formErrors[`prev_exp_${idx}_experienceMonths`] && (
                                              <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>
                                                {formErrors[`prev_exp_${idx}_experienceMonths`]}
                                              </span>
                                            )}
                                            {formErrors[`prev_exp_${idx}_duration`] && (
                                              <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>
                                                {formErrors[`prev_exp_${idx}_duration`]}
                                              </span>
                                            )}
                                          </div>

                                          <div className="apply-field">
                                            <label className="apply-label">Approximately how many students did you teach? <span className="apply-required-star">*</span></label>
                                            <div className="premium-field">
                                              <input
                                                type="number"
                                                min="0"
                                                className={`apply-input premium-input${formErrors[`prev_exp_${idx}_studentsTaught`] ? ' apply-input-error' : ''}`}
                                                placeholder="Enter approximate number of students"
                                                value={exp.studentsTaught}
                                                onChange={e => handleCourseExperienceChange(idx, 'studentsTaught', e.target.value)}
                                              />
                                            </div>
                                            {formErrors[`prev_exp_${idx}_studentsTaught`] && (
                                              <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>
                                                {formErrors[`prev_exp_${idx}_studentsTaught`]}
                                              </span>
                                            )}
                                          </div>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                                          <button
                                            type="button"
                                            className="apply-btn apply-btn-secondary"
                                            onClick={() => handleSaveCourseExperience(idx)}
                                            style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '6px',
                                              fontWeight: 650,
                                              fontSize: '13px',
                                              padding: '7px 16px',
                                              borderRadius: '7px'
                                            }}
                                          >
                                            <Check size={14} /> Save Entry
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                <button
                                  type="button"
                                  className="apply-btn apply-btn-secondary"
                                  onClick={handleAddCourseExperience}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontWeight: 650,
                                    color: '#0c4f47',
                                    borderColor: '#0c4f47',
                                    background: '#ffffff',
                                    padding: '9px 16px',
                                    borderRadius: '8px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Plus size={15} /> Add Another Course
                                </button>
                              </div>
                            )}
                          </div>

                          {/* ============================================================
                             SUBSECTION 4: MY TEACHING JOURNEY
                             ============================================================ */}
                          <div style={{ marginTop: '24px', borderTop: '1px dashed #c8dbd4', paddingTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <Briefcase size={18} color="#0c4f47" />
                              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#12213A' }}>
                                My Teaching Journey
                              </h4>
                            </div>
                            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>
                              Add the important teaching roles, organisations and experiences that have shaped your journey as a yoga teacher.
                            </p>

                            {formErrors.teaching_journey && (
                              <span className="apply-error-text" style={{ marginBottom: '12px', display: 'block' }}>
                                {formErrors.teaching_journey}
                              </span>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '18px' }}>
                              {(Array.isArray(formData.teaching_journey) ? formData.teaching_journey : []).map((jEntry, idx) => {
                                // READ-ONLY SUMMARY CARD
                                if (!jEntry.isEditing) {
                                  const locStr = [jEntry.city, jEntry.state, jEntry.country].filter(Boolean).join(', ');
                                  const startStr = `${jEntry.start_month || ''} ${jEntry.start_year || ''}`.trim();
                                  const endStr = jEntry.is_current ? 'Present' : (`${jEntry.end_month || ''} ${jEntry.end_year || ''}`.trim() || 'N/A');

                                  return (
                                    <div
                                      key={jEntry.localId || `journey-${idx}`}
                                      style={{
                                        background: '#ffffff',
                                        border: '1.5px solid #c8dbd4',
                                        borderRadius: '12px',
                                        padding: '18px 20px',
                                        boxShadow: '0 2px 8px rgba(12,79,71,0.04)'
                                      }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #eef4f1', paddingBottom: '10px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <span style={{ fontSize: '14.5px', fontWeight: 700, color: '#0c4f47' }}>
                                            Journey Entry {idx + 1}
                                          </span>
                                          <span style={{ fontSize: '11px', fontWeight: 650, color: '#079447', background: '#eef8f2', border: '1px solid #c3e6cb', padding: '2px 8px', borderRadius: '10px' }}>
                                            Completed
                                          </span>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                          <button
                                            type="button"
                                            className="apply-btn-action"
                                            onClick={() => handleEditTeachingJourney(idx)}
                                            style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '5px',
                                              padding: '6px 14px',
                                              borderRadius: '7px',
                                              border: '1.5px solid #0c4f47',
                                              background: '#ffffff',
                                              color: '#0c4f47',
                                              fontWeight: 650,
                                              fontSize: '12.5px',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            <Edit2 size={13} /> Edit
                                          </button>
                                          <button
                                            type="button"
                                            className="apply-btn-action apply-btn-remove"
                                            onClick={() => handleRemoveTeachingJourney(idx)}
                                            style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '5px',
                                              padding: '6px 14px',
                                              borderRadius: '7px',
                                              border: '1px solid #fecaca',
                                              background: '#fff5f5',
                                              color: '#e53e3e',
                                              fontWeight: 600,
                                              fontSize: '12.5px',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            <Trash2 size={13} /> Remove Journey
                                          </button>
                                        </div>
                                      </div>

                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px 24px', marginBottom: '12px' }}>
                                        <div>
                                          <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '3px' }}>
                                            Role / Position
                                          </span>
                                          <strong style={{ fontSize: '14.5px', color: '#12213A', fontWeight: 700 }}>
                                            {jEntry.role_title || 'N/A'}
                                          </strong>
                                        </div>

                                        <div>
                                          <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '3px' }}>
                                            Organisation / Studio / Academy
                                          </span>
                                          <strong style={{ fontSize: '14px', color: '#0c4f47', fontWeight: 700 }}>
                                            {jEntry.organisation || 'N/A'}
                                          </strong>
                                        </div>

                                        <div>
                                          <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '3px' }}>
                                            Location
                                          </span>
                                          <span style={{ fontSize: '14px', color: '#12213A', fontWeight: 500 }}>
                                            {locStr || 'N/A'}
                                          </span>
                                        </div>

                                        <div>
                                          <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '3px' }}>
                                            Employment Status
                                          </span>
                                          <span style={{ fontSize: '13.5px', fontWeight: 600, color: jEntry.is_current ? '#079447' : '#475569' }}>
                                            {jEntry.is_current ? 'Currently working here' : 'Past Position'}
                                          </span>
                                        </div>

                                        <div>
                                          <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '3px' }}>
                                            Start Period
                                          </span>
                                          <span style={{ fontSize: '14px', color: '#12213A', fontWeight: 500 }}>
                                            {startStr || 'N/A'}
                                          </span>
                                        </div>

                                        <div>
                                          <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '3px' }}>
                                            End Period
                                          </span>
                                          <span style={{ fontSize: '14px', color: '#12213A', fontWeight: jEntry.is_current ? 700 : 500 }}>
                                            {endStr}
                                          </span>
                                        </div>
                                      </div>

                                      {jEntry.description && (
                                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #eef4f1' }}>
                                          <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#52605b', marginBottom: '4px' }}>
                                            Role Description
                                          </span>
                                          <div style={{ color: '#12213A', fontSize: '13.5px', lineHeight: 1.6, background: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                            {jEntry.description}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }

                                // EDITABLE INPUT FORM
                                return (
                                  <div
                                    key={jEntry.localId || `journey-${idx}`}
                                    style={{
                                      background: '#ffffff',
                                      border: '1.5px solid #c8dbd4',
                                      borderRadius: '12px',
                                      padding: '18px 20px',
                                      boxShadow: '0 2px 8px rgba(12,79,71,0.04)'
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #eef4f1', paddingBottom: '8px' }}>
                                      <span style={{ fontSize: '14px', fontWeight: 700, color: '#0c4f47' }}>
                                        Journey Entry {idx + 1}
                                      </span>
                                      <button
                                        type="button"
                                        className="apply-btn-action apply-btn-remove"
                                        aria-label={`Remove Journey Entry ${idx + 1}`}
                                        onClick={() => handleRemoveTeachingJourney(idx)}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12.5px' }}
                                      >
                                        <Trash2 size={13} /> Remove Journey
                                      </button>
                                    </div>

                                    {/* Grid Row 1: Role Title & Organisation */}
                                    <div className="apply-grid-2" style={{ marginBottom: '12px' }}>
                                      <div className="apply-field">
                                        <label className="apply-label">Role / Position Title <span className="apply-required-star">*</span></label>
                                        <input
                                          type="text"
                                          className={`apply-input${formErrors[`journey_${idx}_role_title`] ? ' apply-input-error' : ''}`}
                                          placeholder="e.g. Senior Yoga Teacher & Mentor"
                                          value={jEntry.role_title || ''}
                                          onChange={e => handleTeachingJourneyChange(idx, 'role_title', e.target.value)}
                                        />
                                        {formErrors[`journey_${idx}_role_title`] && <span className="apply-error-text">{formErrors[`journey_${idx}_role_title`]}</span>}
                                      </div>

                                      <div className="apply-field">
                                        <label className="apply-label">Organisation / Studio / Academy <span className="apply-required-star">*</span></label>
                                        <input
                                          type="text"
                                          className={`apply-input${formErrors[`journey_${idx}_organisation`] ? ' apply-input-error' : ''}`}
                                          placeholder="e.g. PranaFlow Academy"
                                          value={jEntry.organisation || ''}
                                          onChange={e => handleTeachingJourneyChange(idx, 'organisation', e.target.value)}
                                        />
                                        {formErrors[`journey_${idx}_organisation`] && <span className="apply-error-text">{formErrors[`journey_${idx}_organisation`]}</span>}
                                      </div>
                                    </div>

                                    {/* Grid Row 2: City, State, Country */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                                      <div className="apply-field">
                                        <label className="apply-label">City</label>
                                        <input
                                          type="text"
                                          className="apply-input"
                                          placeholder="e.g. Rishikesh"
                                          value={jEntry.city || ''}
                                          onChange={e => handleTeachingJourneyChange(idx, 'city', e.target.value)}
                                        />
                                      </div>

                                      <div className="apply-field">
                                        <label className="apply-label">State / Region</label>
                                        <input
                                          type="text"
                                          className="apply-input"
                                          placeholder="e.g. Uttarakhand"
                                          value={jEntry.state || ''}
                                          onChange={e => handleTeachingJourneyChange(idx, 'state', e.target.value)}
                                        />
                                      </div>

                                      <div className="apply-field">
                                        <label className="apply-label">Country <span className="apply-required-star">*</span></label>
                                        <div className="premium-field">
                                          <select
                                            className={`apply-select premium-input${formErrors[`journey_${idx}_country`] ? ' apply-input-error' : ''}`}
                                            value={jEntry.country || 'India'}
                                            onChange={e => handleTeachingJourneyChange(idx, 'country', e.target.value)}
                                          >
                                            {countries.map(c => (
                                              <option key={c.code || c.name} value={c.name}>{c.name}</option>
                                            ))}
                                          </select>
                                          <ChevronDown className="premium-select-chevron" />
                                        </div>
                                        {formErrors[`journey_${idx}_country`] && <span className="apply-error-text">{formErrors[`journey_${idx}_country`]}</span>}
                                      </div>
                                    </div>

                                    {/* Grid Row 3: Currently Working Checkbox */}
                                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <input
                                        type="checkbox"
                                        id={`is_current_${idx}`}
                                        checked={!!jEntry.is_current}
                                        onChange={e => handleTeachingJourneyChange(idx, 'is_current', e.target.checked)}
                                        style={{ width: '17px', height: '17px', accentColor: '#0c4f47', cursor: 'pointer' }}
                                      />
                                      <label htmlFor={`is_current_${idx}`} style={{ fontSize: '13.5px', fontWeight: 600, color: '#12213A', cursor: 'pointer' }}>
                                        Currently working here
                                      </label>
                                    </div>

                                    {/* Grid Row 4: Start Month & Year, End Month & Year */}
                                    <div className="apply-grid-2" style={{ marginBottom: '12px' }}>
                                      <div className="apply-field">
                                        <label className="apply-label">Start Period <span className="apply-required-star">*</span></label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                          <select
                                            className={`apply-select${formErrors[`journey_${idx}_start_month`] ? ' apply-input-error' : ''}`}
                                            style={{ flex: 1 }}
                                            value={jEntry.start_month || ''}
                                            onChange={e => handleTeachingJourneyChange(idx, 'start_month', e.target.value)}
                                          >
                                            <option value="">Start Month</option>
                                            {monthsList.map(m => <option key={m} value={m}>{m}</option>)}
                                          </select>

                                          <select
                                            className={`apply-select${formErrors[`journey_${idx}_start_year`] ? ' apply-input-error' : ''}`}
                                            style={{ flex: 1 }}
                                            value={jEntry.start_year || ''}
                                            onChange={e => handleTeachingJourneyChange(idx, 'start_year', e.target.value)}
                                          >
                                            <option value="">Start Year</option>
                                            {yearsList.map(y => <option key={y} value={y}>{y}</option>)}
                                          </select>
                                        </div>
                                        {(formErrors[`journey_${idx}_start_month`] || formErrors[`journey_${idx}_start_year`]) && (
                                          <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>
                                            {formErrors[`journey_${idx}_start_month`] || formErrors[`journey_${idx}_start_year`]}
                                          </span>
                                        )}
                                      </div>

                                      <div className="apply-field">
                                        <label className="apply-label">End Period {!jEntry.is_current && <span className="apply-required-star">*</span>}</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                          <select
                                            className={`apply-select${formErrors[`journey_${idx}_end_month`] ? ' apply-input-error' : ''}`}
                                            style={{ flex: 1, opacity: jEntry.is_current ? 0.6 : 1 }}
                                            disabled={!!jEntry.is_current}
                                            value={jEntry.is_current ? '' : (jEntry.end_month || '')}
                                            onChange={e => handleTeachingJourneyChange(idx, 'end_month', e.target.value)}
                                          >
                                            <option value="">{jEntry.is_current ? 'Present' : 'End Month'}</option>
                                            {monthsList.map(m => <option key={m} value={m}>{m}</option>)}
                                          </select>

                                          <select
                                            className={`apply-select${formErrors[`journey_${idx}_end_year`] ? ' apply-input-error' : ''}`}
                                            style={{ flex: 1, opacity: jEntry.is_current ? 0.6 : 1 }}
                                            disabled={!!jEntry.is_current}
                                            value={jEntry.is_current ? '' : (jEntry.end_year || '')}
                                            onChange={e => handleTeachingJourneyChange(idx, 'end_year', e.target.value)}
                                          >
                                            <option value="">{jEntry.is_current ? 'Present' : 'End Year'}</option>
                                            {yearsList.map(y => <option key={y} value={y}>{y}</option>)}
                                          </select>
                                        </div>
                                        {!jEntry.is_current && (formErrors[`journey_${idx}_end_month`] || formErrors[`journey_${idx}_end_year`]) && (
                                          <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>
                                            {formErrors[`journey_${idx}_end_month`] || formErrors[`journey_${idx}_end_year`]}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Grid Row 5: Role Description */}
                                    <div className="apply-field">
                                      <label className="apply-label">Role Description <span className="apply-required-star">*</span></label>
                                      <div style={{ position: 'relative' }}>
                                        <textarea
                                          className={`apply-textarea${formErrors[`journey_${idx}_description`] ? ' apply-input-error' : ''}`}
                                          rows={3}
                                          maxLength={1000}
                                          placeholder="e.g. Leading YTTC batches, workshops and retreats with a focus on holistic transformation."
                                          value={jEntry.description || ''}
                                          onChange={e => handleTeachingJourneyChange(idx, 'description', e.target.value)}
                                          style={{ paddingBottom: '24px' }}
                                        />
                                        <span style={{ position: 'absolute', bottom: '6px', right: '12px', fontSize: '11px', color: '#94a39c' }}>
                                          {(jEntry.description || '').length}/1000
                                        </span>
                                      </div>
                                      {formErrors[`journey_${idx}_description`] && (
                                        <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>
                                          {formErrors[`journey_${idx}_description`]}
                                        </span>
                                      )}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                                      <button
                                        type="button"
                                        className="apply-btn apply-btn-secondary"
                                        onClick={() => handleSaveTeachingJourney(idx)}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          fontWeight: 650,
                                          fontSize: '13px',
                                          padding: '7px 16px',
                                          borderRadius: '7px'
                                        }}
                                      >
                                        <Check size={14} /> Save Entry
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            <button
                              type="button"
                              className="apply-btn apply-btn-secondary"
                              onClick={handleAddTeachingJourney}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontWeight: 650,
                                color: '#0c4f47',
                                borderColor: '#0c4f47',
                                background: '#ffffff',
                                padding: '9px 16px',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                            >
                              <Plus size={15} /> Add Another Journey
                            </button>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('experience-background')}>
                              Save &amp; Continue
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 7: Availability */}
                  <div className={`apply-card section-full ${expanded['availability'] ? '' : 'collapsed'}`} id="section-availability">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('availability')}
                      aria-expanded={!!expanded['availability']}
                      aria-controls="section-content-availability"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">7</span>
                        <h3 className="apply-section-title">Availability (Weekly Schedule)</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['availability'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>

                    {expanded['availability'] && (
                      completedSections['availability'] ? (
                        <div className="apply-section-content" id="section-content-availability">
                          <div className="apply-review-grid">
                            <div className="apply-review-field" style={{ gridColumn: '1 / -1' }}>
                              <span>Preferred Teaching Formats</span>
                              <div style={{ marginTop: '4px', fontSize: '13.5px', color: '#172b26' }}>
                                {Array.isArray(formData.teaching_formats) && formData.teaching_formats.length > 0 ? formData.teaching_formats.join(', ') : 'None selected'}
                              </div>
                            </div>
                            <div className="apply-review-field" style={{ gridColumn: '1 / -1' }}>
                              <span>Available Days &amp; Slots</span>
                              <div style={{ marginTop: '4px', fontSize: '13px', color: '#172b26' }}>
                                {Object.keys(formData.weekly_schedule).filter(day => formData.weekly_schedule[day]?.available).map(day => {
                                  const slots = formData.weekly_schedule[day]?.slots || [];
                                  const slotStr = slots.map(s => `${s.from} - ${s.to}`).join(', ');
                                  return <div key={day}><strong>{day}:</strong> {slotStr || 'Available'}</div>;
                                })}
                                {Object.keys(formData.weekly_schedule).filter(day => formData.weekly_schedule[day]?.available).length === 0 && 'No available days specified'}
                              </div>
                            </div>
                            <div className="apply-review-field"><span>Available for Free Demo</span>{formData.demo_available || 'No'}</div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('availability')}>
                              <Edit2 size={13} /> Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-availability">
                          {/* Preferred Teaching Formats */}
                          <div className="apply-field" style={{ marginBottom: '24px', borderBottom: '1px dashed #c8dbd4', paddingBottom: '20px' }}>
                            <label className="apply-label" style={{ fontSize: '15px', fontWeight: 650, color: '#172b26', marginBottom: '6px', display: 'block' }}>
                              Preferred Teaching Formats <span className="apply-required-star">*</span>
                            </label>
                            <p style={{ fontSize: '13.5px', color: '#6b7280', margin: '0 0 12px 0', lineHeight: 1.4 }}>
                              Select all teaching formats in which you are available and comfortable delivering classes.
                            </p>
                            <div className="apply-avail-format-pills" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {['Live Online', 'In-person', 'Hybrid', 'Pre-recorded Lessons', 'Workshops', 'Retreats', 'One-to-One', 'Group Classes'].map(fmt => {
                                const isSel = formData.teaching_formats.includes(fmt);
                                return (
                                  <button
                                    key={fmt} type="button" onClick={() => toggleTeachingFormat(fmt)}
                                    className="apply-avail-format-btn"
                                    style={{
                                      padding: '8px 16px', borderRadius: '8px', fontSize: '13.5px', fontWeight: 600,
                                      cursor: 'pointer', transition: 'all 0.15s ease',
                                      backgroundColor: isSel ? '#0c4f47' : '#ffffff', color: isSel ? '#ffffff' : '#263b33',
                                      border: isSel ? '1.5px solid #0c4f47' : '1.5px solid #d7e2dc'
                                    }}
                                  >
                                    {isSel ? '✓ ' : '+ '}{fmt}
                                  </button>
                                );
                              })}
                            </div>
                            {formErrors.teaching_formats && <span className="apply-error-text" style={{ marginTop: '8px', display: 'block' }}>{formErrors.teaching_formats}</span>}
                          </div>

                          {/* Compact availability table */}
                          <div className="apply-avail-table-wrap">
                            <table className="apply-avail-table">
                              <thead>
                                <tr>
                                  <th>Day</th>
                                  <th>Available</th>
                                  <th>From</th>
                                  <th>To</th>
                                  <th>Add More Slot</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.keys(formData.weekly_schedule).map(day => {
                                  const dayData = formData.weekly_schedule[day];
                                  const firstSlot = (dayData.slots && dayData.slots[0]) || { from: '09:00', to: '17:00' };
                                  return (
                                    <tr key={day} className={`apply-avail-row ${dayData.available ? 'is-available' : 'is-unavailable'}`}>
                                      <td className="apply-avail-cell apply-avail-cell-day">
                                        <label htmlFor={`avail-day-${day}`} className="apply-avail-day-label" style={{ cursor: 'pointer', fontWeight: 600 }}>{day}</label>
                                      </td>
                                      <td className="apply-avail-cell apply-avail-cell-status">
                                        <label htmlFor={`avail-day-${day}`} className="apply-avail-check-label">
                                          <input
                                            type="checkbox"
                                            id={`avail-day-${day}`}
                                            className="apply-avail-checkbox"
                                            checked={!!dayData.available}
                                            onChange={e => handleDayToggle(day, e.target.checked)}
                                          />
                                          <span className="apply-avail-check-text">{dayData.available ? 'Available' : 'Unavailable'}</span>
                                        </label>
                                      </td>
                                      <td className="apply-avail-cell apply-avail-cell-time apply-avail-cell-from">
                                        <span className="apply-avail-mobile-label">From:</span>
                                        <div className="premium-field time-field-wrap">
                                          <Clock className="premium-field-icon" />
                                          <input
                                            type="time"
                                            className="apply-avail-time-input premium-input has-left-icon"
                                            value={firstSlot.from}
                                            disabled={!dayData.available}
                                            onChange={e => handleSlotChange(day, 0, 'from', e.target.value)}
                                          />
                                        </div>
                                      </td>
                                      <td className="apply-avail-cell apply-avail-cell-time apply-avail-cell-to">
                                        <span className="apply-avail-mobile-label">To:</span>
                                        <div className="premium-field time-field-wrap">
                                          <Clock className="premium-field-icon" />
                                          <input
                                            type="time"
                                            className="apply-avail-time-input premium-input has-left-icon"
                                            value={firstSlot.to}
                                            disabled={!dayData.available}
                                            onChange={e => handleSlotChange(day, 0, 'to', e.target.value)}
                                          />
                                        </div>
                                      </td>
                                      <td className="apply-avail-cell apply-avail-cell-slots">
                                        {dayData.available && (
                                          <>
                                            <button type="button" className="apply-avail-add-slot" onClick={() => handleAddSlot(day)}>+ Add</button>
                                            {dayData.slots && dayData.slots.length > 1 && (
                                              <div className="apply-avail-extra-slots-wrap" style={{marginTop:'4px',display:'flex',flexDirection:'column',gap:'4px'}}>
                                                {dayData.slots.slice(1).map((slot, si) => (
                                                  <div key={si+1} className="apply-avail-extra-slot-row">
                                                    <div className="apply-avail-extra-slot-fields">
                                                      <div className="apply-avail-extra-slot-time">
                                                        <span className="apply-avail-mobile-label">From:</span>
                                                        <div className="premium-field time-field-wrap">
                                                          <Clock className="premium-field-icon" />
                                                          <input type="time" className="apply-avail-time-input premium-input has-left-icon" value={slot.from} onChange={e => handleSlotChange(day, si+1, 'from', e.target.value)} />
                                                        </div>
                                                      </div>
                                                      <span className="apply-avail-slot-sep">-</span>
                                                      <div className="apply-avail-extra-slot-time">
                                                        <span className="apply-avail-mobile-label">To:</span>
                                                        <div className="premium-field time-field-wrap">
                                                          <Clock className="premium-field-icon" />
                                                          <input type="time" className="apply-avail-time-input premium-input has-left-icon" value={slot.to} onChange={e => handleSlotChange(day, si+1, 'to', e.target.value)} />
                                                        </div>
                                                      </div>
                                                    </div>
                                                    <button type="button" className="apply-avail-remove-slot" onClick={() => handleRemoveSlot(day, si+1)} title="Remove slot" aria-label="Remove slot">-</button>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div style={{marginTop:'12px'}}>
                             <div className="apply-field">
                               <label className="apply-label">Available for Free Demo Classes?</label>
                               <div className="apply-radio-group" style={{marginTop:'4px'}}>
                                 <label className="apply-radio-label">
                                   <input type="radio" name="demo_available" value="Yes" checked={formData.demo_available==='Yes'} onChange={handleInputChange} /> Yes
                                 </label>
                                 <label className="apply-radio-label">
                                   <input type="radio" name="demo_available" value="No" checked={formData.demo_available==='No'} onChange={handleInputChange} /> No
                                 </label>
                               </div>
                             </div>
                           </div>
                           <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                             <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('availability')}>
                               Save &amp; Continue
                             </button>
                           </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 8: Videos & Links */}
                  <div className={`apply-card section-full ${expanded['videos-links'] ? '' : 'collapsed'}`} id="section-videos-links">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('videos-links')}
                      aria-expanded={!!expanded['videos-links']}
                      aria-controls="section-content-videos-links"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">8</span>
                        <h3 className="apply-section-title">Videos &amp; Links (Google Drive Links)</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['videos-links'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>

                    {expanded['videos-links'] && (
                      completedSections['videos-links'] ? (
                        <div className="apply-section-content" id="section-content-videos-links">
                          <div className="apply-review-grid">
                            <div className="apply-review-field"><span>Intro Video</span>{formData.intro_video_link || '-'}</div>
                            <div className="apply-review-field"><span>Demo Video</span>{formData.demo_video_link || '-'}</div>
                            <div className="apply-review-field"><span>Resume / CV</span>{formData.resume_drive_link || '-'}</div>
                            <div className="apply-review-field"><span>Website</span>{formData.website || '-'}</div>
                            <div className="apply-review-field"><span>Instagram</span>{formData.instagram || '-'}</div>
                            <div className="apply-review-field"><span>LinkedIn</span>{formData.linkedin || '-'}</div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('videos-links')}>
                              <Edit2 size={13} /> Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-videos-links">
                          {/* Google Drive info banner */}
                          <div className="apply-drive-info-box">
                            <Info size={13} />
                            <span>Please upload your videos to Google Drive and paste the shareable link (Anyone with the link can view).</span>
                          </div>
                          <div className="apply-grid-3">
                            <div className="apply-field">
                              <label className="apply-label">Introduction Video Link <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <PlayCircle className="premium-field-icon" />
                                <input type="text" inputMode="url" id="intro_video_link" name="intro_video_link" className={`apply-input premium-input has-left-icon${formErrors.intro_video_link ? ' apply-input-error' : ''}`} placeholder="e.g. www.youtube.com/watch?v=... or https://drive.google.com/..." value={formData.intro_video_link} onChange={handleInputChange} />
                              </div>
                              {formErrors.intro_video_link && <span className="apply-error-text">{formErrors.intro_video_link}</span>}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">Demo Teaching Video Link <span className="apply-required-star">*</span></label>
                              <div className="premium-field">
                                <PlayCircle className="premium-field-icon" />
                                <input type="text" inputMode="url" id="demo_video_link" name="demo_video_link" className={`apply-input premium-input has-left-icon${formErrors.demo_video_link ? ' apply-input-error' : ''}`} placeholder="e.g. www.youtube.com/watch?v=... or https://drive.google.com/..." value={formData.demo_video_link} onChange={handleInputChange} />
                              </div>
                              {formErrors.demo_video_link && <span className="apply-error-text">{formErrors.demo_video_link}</span>}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">Resume / CV Google Drive Link (Optional)</label>
                              <div className="premium-field">
                                <Link2 className="premium-field-icon" />
                                <input type="text" inputMode="url" id="resume_drive_link" name="resume_drive_link" className={`apply-input premium-input has-left-icon${formErrors.resume_drive_link ? ' apply-input-error' : ''}`} placeholder="e.g. www.drive.google.com/file/..." value={formData.resume_drive_link} onChange={handleInputChange} />
                              </div>
                              {formErrors.resume_drive_link && <span className="apply-error-text">{formErrors.resume_drive_link}</span>}
                            </div>
                          </div>

                          <div className="apply-grid-3" style={{ marginTop: '16px' }}>
                            <div className="apply-field">
                              <label className="apply-label" htmlFor="website">Personal Website (Optional)</label>
                              <div className="premium-field">
                                <Globe2 className="premium-field-icon" />
                                <input
                                  type="text"
                                  id="website"
                                  name="website"
                                  className={`apply-input premium-input has-left-icon${formErrors.website ? ' apply-input-error' : ''}`}
                                  placeholder="e.g. www.yourwebsite.com"
                                  value={formData.website}
                                  onChange={handleInputChange}
                                  onBlur={(e) => {
                                    const norm = normalizeWebsiteUrl(e.target.value);
                                    if (norm && norm !== e.target.value) {
                                      setFormData(prev => ({ ...prev, website: norm }));
                                    }
                                  }}
                                  aria-invalid={Boolean(formErrors.website)}
                                  aria-describedby={formErrors.website ? "website-error" : "website-hint"}
                                />
                              </div>
                              <span id="website-hint" className="apply-field-hint" style={{ fontSize: '11.5px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                                Example: www.yourwebsite.com or https://www.yourwebsite.com
                              </span>
                              {formErrors.website && (
                                <span id="website-error" className="apply-error-text" role="alert">
                                  {formErrors.website}
                                </span>
                              )}
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">Instagram Profile</label>
                              <div className="premium-field">
                                <Globe2 className="premium-field-icon" />
                                <input type="text" inputMode="url" id="instagram" name="instagram" className={`apply-input premium-input has-left-icon${formErrors.instagram ? ' apply-input-error' : ''}`} placeholder="e.g. www.instagram.com/yourprofile" value={formData.instagram} onChange={handleInputChange} />
                            {formErrors.instagram && <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>{formErrors.instagram}</span>}
                              </div>
                            </div>
                            <div className="apply-field">
                              <label className="apply-label">LinkedIn Profile</label>
                              <div className="premium-field">
                                <Globe2 className="premium-field-icon" />
                                <input type="text" inputMode="url" id="linkedin" name="linkedin" className={`apply-input premium-input has-left-icon${formErrors.linkedin ? ' apply-input-error' : ''}`} placeholder="e.g. www.linkedin.com/in/yourprofile" value={formData.linkedin} onChange={handleInputChange} />
                            {formErrors.linkedin && <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>{formErrors.linkedin}</span>}
                              </div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('videos-links')}>
                              Save &amp; Continue
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 9: Yoga School Affiliation */}
                  <div className={`apply-card section-full ${expanded['school-affiliation'] ? '' : 'collapsed'}`} id="section-school-affiliation">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('school-affiliation')}
                      aria-expanded={!!expanded['school-affiliation']}
                      aria-controls="section-content-school-affiliation"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">9</span>
                        <h3 className="apply-section-title">Yoga School Affiliation</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['school-affiliation'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>

                    {expanded['school-affiliation'] && (
                      completedSections['school-affiliation'] ? (
                        <div className="apply-section-content" id="section-content-school-affiliation">
                          <div className="apply-review-grid">
                            <div className="apply-review-field"><span>Owns/Represents School</span>{formData.owns_or_represents_school || 'No'}</div>
                            {formData.owns_or_represents_school === 'Yes' && (
                              <>
                                <div className="apply-review-field"><span>School Name</span>{formData.partner_school_name || '-'}</div>
                                <div className="apply-review-field"><span>Yoga Alliance RYS ID</span>{formData.yoga_alliance_school_id || '-'}</div>
                                <div className="apply-review-field"><span>Can Issue Recognized Certificates?</span>{formData.can_issue_certificates === 'Yes' ? 'Yes' : (formData.can_issue_certificates === 'No' ? 'No' : (formData.can_issue_certificates || 'No'))}</div>
                                <div className="apply-review-field"><span>Certificate Types Can Issue</span>{formData.can_issue_certificates === 'Yes' ? (formData.certificate_types_can_issue?.trim() || '-') : 'N/A'}</div>
                              </>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('school-affiliation')}>
                              <Edit2 size={13} /> Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-school-affiliation">
                          <div className="apply-field" style={{ marginBottom: '16px' }}>
                            <label className="apply-label">Do you own or represent a yoga school which can issue recognised certificates to students?</label>
                            <div className="premium-field" style={{ maxWidth: '320px' }}>
                              <Award className="premium-field-icon" />
                              <select name="owns_or_represents_school" className="apply-select premium-input has-left-icon" value={formData.owns_or_represents_school} onChange={handleInputChange}>
                                <option value="No">No</option>
                                <option value="Yes">Yes</option>
                              </select>
                              <ChevronDown className="premium-select-chevron" />
                            </div>
                          </div>

                          {formData.owns_or_represents_school === 'Yes' && (
                            <div style={{ background: '#f8fcf9', border: '1px solid #d7e2dc', borderRadius: '12px', padding: '18px' }}>
                              <h4 style={{ margin: '0 0 14px 0', fontSize: '14.5px', fontWeight: 700, color: '#0c4f47' }}>
                                School Affiliation Details
                              </h4>
                              <div className="apply-grid-2" style={{ marginBottom: '12px' }}>
                                <div className="apply-field">
                                  <label className="apply-label">Partner School Name</label>
                                  <input type="text" name="partner_school_name" className="apply-input" placeholder="e.g. Himalayan Yoga Shala" value={formData.partner_school_name} onChange={handleInputChange} />
                                </div>
                                <div className="apply-field">
                                  <label className="apply-label">Yoga Alliance School ID (RYS ID)</label>
                                  <input type="text" name="yoga_alliance_school_id" className="apply-input" placeholder="e.g. RYS-200-987" value={formData.yoga_alliance_school_id} onChange={handleInputChange} />
                                </div>
                              </div>
                              <div className="apply-grid-2" style={{ marginBottom: '12px' }}>
                                <div className="apply-field">
                                  <label className="apply-label">Yoga Alliance School Link</label>
                                  <input type="text" inputMode="url" id="yoga_alliance_school_link" name="yoga_alliance_school_link" className={`apply-input${formErrors.yoga_alliance_school_link ? ' apply-input-error' : ''}`} placeholder="e.g. www.yogaalliance.org/..." value={formData.yoga_alliance_school_link} onChange={handleInputChange} />
                                  {formErrors.yoga_alliance_school_link && <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>{formErrors.yoga_alliance_school_link}</span>}
                                </div>
                                <div className="apply-field">
                                   <label className="apply-label" htmlFor="school_website_link">School Website Link (Optional)</label>
                                   <input
                                     type="text"
                                     id="school_website_link"
                                     name="school_website_link"
                                     className={`apply-input${formErrors.school_website_link ? ' apply-input-error' : ''}`}
                                     placeholder="e.g. www.yourwebsite.com"
                                     value={formData.school_website_link}
                                     onChange={handleInputChange}
                                     onBlur={(e) => {
                                       const norm = normalizeWebsiteUrl(e.target.value);
                                       if (norm && norm !== e.target.value) {
                                         setFormData(prev => ({ ...prev, school_website_link: norm }));
                                       }
                                     }}
                                     aria-invalid={Boolean(formErrors.school_website_link)}
                                     aria-describedby={formErrors.school_website_link ? "school_website_link-error" : "school_website_link-hint"}
                                   />
                                   <span id="school_website_link-hint" className="apply-field-hint" style={{ fontSize: '11.5px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                                     Example: www.yourwebsite.com or https://www.yourwebsite.com
                                   </span>
                                   {formErrors.school_website_link && (
                                     <span id="school_website_link-error" className="apply-error-text" role="alert">
                                       {formErrors.school_website_link}
                                     </span>
                                   )}
                                </div>
                              </div>
                              <div className="apply-field" style={{ marginBottom: '12px' }}>
                                <label className="apply-label">School Address</label>
                                <input type="text" name="school_address" className="apply-input" placeholder="Full address of the school" value={formData.school_address} onChange={handleInputChange} />
                              </div>
                              <div className="apply-grid-2" style={{ marginBottom: '12px' }}>
                                <div className="apply-field">
                                  <label className="apply-label">Can Issue Recognized Certificates?</label>
                                  <select
                                    name="can_issue_certificates"
                                    className={`apply-select premium-input${formErrors.can_issue_certificates ? ' apply-input-error' : ''}`}
                                    value={formData.can_issue_certificates || ''}
                                    onChange={handleInputChange}
                                  >
                                    <option value="">Select Yes or No</option>
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                  </select>
                                  {formErrors.can_issue_certificates && <span className="apply-error-text">{formErrors.can_issue_certificates}</span>}
                                </div>
                                {formData.can_issue_certificates === 'Yes' && (
                                  <div className="apply-field">
                                    <label className="apply-label">Certificate Types Can Issue <span style={{ color: '#e11d48' }}>*</span></label>
                                    <input
                                      type="text"
                                      name="certificate_types_can_issue"
                                      className={`apply-input${formErrors.certificate_types_can_issue ? ' apply-input-error' : ''}`}
                                      placeholder="Example: 500-Hour YTTC Yoga, 200-Hour YTTC Yoga, 300-Hour YTTC Yoga"
                                      value={formData.certificate_types_can_issue || ''}
                                      onChange={handleInputChange}
                                    />
                                    {formErrors.certificate_types_can_issue && <span className="apply-error-text">{formErrors.certificate_types_can_issue}</span>}
                                  </div>
                                )}
                              </div>
                              <div className="apply-grid-2">
                                <div className="apply-field">
                                  <label className="apply-label">Authorized Person Name</label>
                                  <input type="text" name="authorized_person_name" className="apply-input" placeholder="Name of representative" value={formData.authorized_person_name} onChange={handleInputChange} />
                                </div>
                                <div className="apply-field">
                                  <label className="apply-label">Authorized Person Contact</label>
                                  <input type="text" name="authorized_person_contact" className="apply-input" placeholder="Email or Phone" value={formData.authorized_person_contact} onChange={handleInputChange} />
                                </div>
                              </div>
                            </div>
                          )}

                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('school-affiliation')}>
                              Save &amp; Continue
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 10: Reference / How Did You Hear About Us? */}
                  <div className={`apply-card section-full ${expanded['references'] ? '' : 'collapsed'}`} id="section-references">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('references')}
                      aria-expanded={!!expanded['references']}
                      aria-controls="section-content-references"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">10</span>
                        <h3 className="apply-section-title">Reference / How Did You Hear About Us?</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['references'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>

                    {expanded['references'] && (
                      completedSections['references'] ? (
                        <div className="apply-section-content" id="section-content-references">
                          <div className="apply-review-grid">
                            <div className="apply-review-field"><span>How They Heard About Us</span>{formData.hear_about_us || 'Not specified'}</div>
                            {formData.hear_about_us === 'Other' && formData.hear_about_us_other && (
                              <div className="apply-review-field"><span>Details</span>{formData.hear_about_us_other}</div>
                            )}
                            {formData.ref_name && (
                              <div className="apply-review-field"><span>Reference Name</span>{formData.ref_name}</div>
                            )}
                            {formData.ref_org && (
                              <div className="apply-review-field"><span>Organisation</span>{formData.ref_org}</div>
                            )}
                            {formData.ref_email && (
                              <div className="apply-review-field"><span>Reference Email</span>{formData.ref_email}</div>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('references')}>
                              <Edit2 size={13} /> Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-references">
                          <p style={{ fontSize: '13px', color: '#52605b', margin: '0 0 18px 0', lineHeight: 1.5 }}>
                            This section is completely optional. You may leave it blank and still submit your application.
                          </p>

                          {/* How did you hear about us */}
                          <div className="apply-field" style={{ marginBottom: '20px', maxWidth: '420px' }}>
                            <label className="apply-label">How did you hear about PranaFlow Academy? <span style={{ color: '#52605b', fontWeight: 400 }}>(Optional)</span></label>
                            <div className="premium-field">
                              <MessageSquare className="premium-field-icon" />
                              <select className="apply-select premium-input has-left-icon" name="hear_about_us" value={formData.hear_about_us}
                                onChange={handleInputChange}>
                                <option value="">Select an option</option>
                                <option value="Friend or colleague">Friend or colleague</option>
                                <option value="Existing teacher">Existing teacher</option>
                                <option value="Former student">Former student</option>
                                <option value="Social media">Social media</option>
                                <option value="Google or another search engine">Google or another search engine</option>
                                <option value="Advertisement">Advertisement</option>
                                <option value="Yoga school or training centre">Yoga school or training centre</option>
                                <option value="Other">Other</option>
                              </select>
                              <ChevronDown className="premium-select-chevron" />
                            </div>
                          </div>

                          {/* Other explanation */}
                          {formData.hear_about_us === 'Other' && (
                            <div className="apply-field" style={{ marginBottom: '20px' }}>
                              <label className="apply-label">Please explain how you heard about us (Optional)</label>
                              <textarea name="hear_about_us_other" className="apply-textarea" placeholder="Describe how you found PranaFlow Academy..." value={formData.hear_about_us_other} onChange={handleInputChange} style={{ minHeight: '80px' }} />
                            </div>
                          )}

                          {/* Reference fields - shown only when referred by a person */}
                          {(formData.hear_about_us === 'Friend or colleague' || formData.hear_about_us === 'Existing teacher' || formData.hear_about_us === 'Former student') && (
                            <div style={{ background: '#f4faf6', border: '1px solid #b6dfc6', borderRadius: '10px', padding: '16px 18px', marginBottom: '20px' }}>
                              <p style={{ fontSize: '13px', fontWeight: 600, color: '#196638', margin: '0 0 14px 0' }}>
                                Reference Details <span style={{ fontWeight: 400, color: '#52605b' }}>(Optional - all fields below are optional)</span>
                              </p>
                              <div className="apply-grid-2" style={{ marginBottom: '12px' }}>
                                <div className="apply-field">
                                  <label className="apply-label">Reference Name</label>
                                  <div className="premium-field">
                                    <UserRound className="premium-field-icon" />
                                    <input type="text" name="ref_name" className="apply-input premium-input has-left-icon" placeholder="Full name of the person" value={formData.ref_name} onChange={handleInputChange} />
                                  </div>
                                </div>
                                <div className="apply-field">
                                  <label className="apply-label">Organisation</label>
                                  <div className="premium-field">
                                    <BriefcaseBusiness className="premium-field-icon" />
                                    <input type="text" name="ref_org" className="apply-input premium-input has-left-icon" placeholder="Their school, studio, or company" value={formData.ref_org} onChange={handleInputChange} />
                                  </div>
                                </div>
                              </div>
                              <div className="apply-grid-2">
                                <div className="apply-field">
                                  <label className="apply-label">Reference Email</label>
                                  <div className="premium-field">
                                    <Mail className="premium-field-icon" />
                                    <input type="email" name="ref_email" className="apply-input premium-input has-left-icon" placeholder="their@email.com" value={formData.ref_email} onChange={handleInputChange} />
                                  </div>
                                </div>
                                <div className="apply-field">
                                  <label className="apply-label">Designation</label>
                                  <div className="premium-field">
                                    <BriefcaseBusiness className="premium-field-icon" />
                                    <input type="text" name="ref_designation" className="apply-input premium-input has-left-icon" placeholder="e.g., Senior Yoga Teacher" value={formData.ref_designation} onChange={handleInputChange} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('references')}>
                              Save &amp; Continue
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 11: About the Teacher */}
                  <div className={`apply-card section-full ${expanded['motivation-strengths'] ? '' : 'collapsed'}`} id="section-motivation-strengths">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('motivation-strengths')}
                      aria-expanded={!!expanded['motivation-strengths']}
                      aria-controls="section-content-motivation-strengths"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">11</span>
                        <h3 className="apply-section-title">About the Teacher</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['motivation-strengths'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>

                    {expanded['motivation-strengths'] && (
                      completedSections['motivation-strengths'] ? (
                        <div className="apply-section-content" id="section-content-motivation-strengths">
                          <div className="apply-review-grid">
                            <div className="apply-review-field" style={{ gridColumn: 'span 12' }}>
                              <span>Teaching Style / Approach</span>
                              <div style={{ marginTop: '4px', fontSize: '13px', color: '#172b26', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{formData.teaching_style_approach || 'Not provided'}</div>
                            </div>
                            <div className="apply-review-field" style={{ gridColumn: 'span 12' }}>
                              <span>Professional Summary</span>
                              <div style={{ marginTop: '4px', fontSize: '13px', color: '#172b26', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{formData.professional_summary || 'Not provided'}</div>
                            </div>
                            <div className="apply-review-field" style={{ gridColumn: 'span 12' }}>
                              <span>Why I Want to Join PranaFlow Academy</span>
                              <div style={{ marginTop: '4px', fontSize: '13px', color: '#172b26', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{formData.why_join_pranaflow || 'Not provided'}</div>
                            </div>
                            <div className="apply-review-field" style={{ gridColumn: 'span 12' }}>
                              <span>What Sets Me Apart as a Teacher</span>
                              <div style={{ marginTop: '4px', fontSize: '13px', color: '#172b26', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{formData.what_makes_me_exceptional || 'Not provided'}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('motivation-strengths')}>
                              <Edit2 size={13} /> Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-motivation-strengths">

                          {/* Question 1: Teaching Style / Approach */}
                          <div className="apply-field" style={{ marginBottom: '28px' }}>
                            <label className="apply-label" htmlFor="teaching_style_approach">
                              How would you describe your teaching style and overall approach to guiding students? <span className="apply-required-star">*</span>
                            </label>
                            <p style={{ fontSize: '12.5px', color: '#52605b', margin: '4px 0 8px 0', lineHeight: 1.5 }}>
                              Explain the principles, techniques, and teaching methods you use to create safe, effective, and meaningful learning experiences.
                            </p>
                            <div style={{ position: 'relative' }}>
                              <textarea
                                id="teaching_style_approach"
                                name="teaching_style_approach"
                                className={`apply-textarea${formErrors.teaching_style_approach ? ' apply-input-error' : ''}`}
                                placeholder="Describe your teaching philosophy, class structure, use of alignment, anatomy, traditional teachings, mindfulness, or other methods."
                                value={formData.teaching_style_approach}
                                onChange={handleInputChange}
                                maxLength={1000}
                                style={{ minHeight: '140px' }}
                              />
                              <div className="character-counter" style={{ color: (formData.teaching_style_approach || '').length < 100 ? '#c84444' : '#52605b' }}>
                                {(formData.teaching_style_approach || '').length} / 1000
                              </div>
                            </div>
                            {formErrors.teaching_style_approach && <span className="apply-error-text">{formErrors.teaching_style_approach}</span>}
                          </div>

                          {/* Question 2: Professional Summary */}
                          <div className="apply-field" style={{ marginBottom: '28px' }}>
                            <label className="apply-label" htmlFor="professional_summary">
                              Please provide a professional summary of your teaching experience, expertise, and areas of specialization. <span className="apply-required-star">*</span>
                            </label>
                            <p style={{ fontSize: '12.5px', color: '#52605b', margin: '4px 0 8px 0', lineHeight: 1.5 }}>
                              Include your years of experience, the types of students you have taught, your core teaching skills, and the practices or subjects you commonly integrate into your sessions.
                            </p>
                            <div style={{ position: 'relative' }}>
                              <textarea
                                id="professional_summary"
                                name="professional_summary"
                                className={`apply-textarea${formErrors.professional_summary ? ' apply-input-error' : ''}`}
                                placeholder="Summarize your experience, qualifications, teaching strengths, student levels, and areas of expertise."
                                value={formData.professional_summary}
                                onChange={handleInputChange}
                                maxLength={1000}
                                style={{ minHeight: '140px' }}
                              />
                              <div className="character-counter" style={{ color: (formData.professional_summary || '').length < 100 ? '#c84444' : '#52605b' }}>
                                {(formData.professional_summary || '').length} / 1000
                              </div>
                            </div>
                            {formErrors.professional_summary && <span className="apply-error-text">{formErrors.professional_summary}</span>}
                          </div>

                          {/* Question 3: Why I Want to Join PranaFlow Academy */}
                          <div className="apply-field" style={{ marginBottom: '28px' }}>
                            <label className="apply-label" htmlFor="why_join_pranaflow">
                              Why would you like to join PranaFlow Academy as a teacher? <span className="apply-required-star">*</span>
                            </label>
                            <p style={{ fontSize: '12.5px', color: '#52605b', margin: '4px 0 8px 0', lineHeight: 1.5 }}>
                              Explain how PranaFlow Academy's values, teaching philosophy, community, or global vision align with your professional goals and personal purpose.
                            </p>
                            <div style={{ position: 'relative' }}>
                              <textarea
                                id="why_join_pranaflow"
                                name="why_join_pranaflow"
                                className={`apply-textarea${formErrors.why_join_pranaflow ? ' apply-input-error' : ''}`}
                                placeholder="Share what attracts you to PranaFlow Academy and how you hope to contribute to its students and community."
                                value={formData.why_join_pranaflow}
                                onChange={handleInputChange}
                                maxLength={1000}
                                style={{ minHeight: '140px' }}
                              />
                              <div className="character-counter" style={{ color: (formData.why_join_pranaflow || '').length < 100 ? '#c84444' : '#52605b' }}>
                                {(formData.why_join_pranaflow || '').length} / 1000
                              </div>
                            </div>
                            {formErrors.why_join_pranaflow && <span className="apply-error-text">{formErrors.why_join_pranaflow}</span>}
                          </div>

                          {/* Question 4: What Sets Me Apart as a Teacher */}
                          <div className="apply-field" style={{ marginBottom: '22px' }}>
                            <label className="apply-label" htmlFor="what_makes_me_exceptional">
                              What qualities, skills, or experiences distinguish you as an exceptional teacher? <span className="apply-required-star">*</span>
                            </label>
                            <p style={{ fontSize: '12.5px', color: '#52605b', margin: '4px 0 8px 0', lineHeight: 1.5 }}>
                              Describe how you connect with students, personalize your guidance, overcome teaching challenges, and create meaningful or lasting outcomes.
                            </p>
                            <div style={{ position: 'relative' }}>
                              <textarea
                                id="what_makes_me_exceptional"
                                name="what_makes_me_exceptional"
                                className={`apply-textarea${formErrors.what_makes_me_exceptional ? ' apply-input-error' : ''}`}
                                placeholder="Explain the unique qualities, teaching strengths, or experiences that make your approach valuable to students."
                                value={formData.what_makes_me_exceptional}
                                onChange={handleInputChange}
                                maxLength={1000}
                                style={{ minHeight: '140px' }}
                              />
                              <div className="character-counter" style={{ color: (formData.what_makes_me_exceptional || '').length < 100 ? '#c84444' : '#52605b' }}>
                                {(formData.what_makes_me_exceptional || '').length} / 1000
                              </div>
                            </div>
                            {formErrors.what_makes_me_exceptional && <span className="apply-error-text">{formErrors.what_makes_me_exceptional}</span>}
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue('motivation-strengths')}>
                              Save &amp; Continue
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Section 12: Verification */}
                  <div className={`apply-card section-full ${expanded['verification'] ? '' : 'collapsed'}`} id="section-verification">
                    <button
                      type="button"
                      className="apply-section-title-row"
                      onClick={() => toggleSection('verification')}
                      aria-expanded={!!expanded['verification']}
                      aria-controls="section-content-verification"
                    >
                      <div className="apply-section-header">
                        <span className="apply-section-badge">12</span>
                        <h3 className="apply-section-title">Verification</h3>
                      </div>
                      <span className="apply-section-toggle-btn" aria-hidden="true">
                        {expanded['verification'] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>

                    {expanded['verification'] && (
                      completedSections['verification'] ? (
                        <div className="apply-section-content" id="section-content-verification">
                          <div className="apply-review-grid">
                            <div className="apply-review-field"><span>Government ID Drive Link</span>{formData.government_id_drive_link || '-'}</div>
                            <div className="apply-review-field"><span>All Declarations Agreed</span>{formData.decl_boundaries && formData.decl_privacy && formData.decl_policies && formData.decl_accuracy ? 'Yes' : 'No'}</div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', gap: '10px' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleEditSection('verification')}>
                              <Edit2 size={13} /> Edit
                            </button>
                            <button type="submit" className="apply-btn apply-btn-primary" disabled={isSubmitting}>
                              {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="apply-section-content" id="section-content-verification">
                          <div className="apply-grid-3">
                            <div className="apply-field" style={{ gridColumn: 'span 3' }}>
                              <label className="apply-label">Government ID Google Drive Link (Optional)</label>
                              <div className="premium-field">
                                <Link2 className="premium-field-icon" />
                                <input type="text" inputMode="url" id="government_id_drive_link" name="government_id_drive_link" className={`apply-input premium-input has-left-icon${formErrors.government_id_drive_link ? ' apply-input-error' : ''}`} placeholder="e.g. www.drive.google.com/file/..." value={formData.government_id_drive_link} onChange={handleInputChange} />
                {formErrors.government_id_drive_link && <span className="apply-error-text" style={{ marginTop: '4px', display: 'block' }}>{formErrors.government_id_drive_link}</span>}
                              </div>
                            </div>
                          </div>

                          {/* declarations panel */}
                          <div className="apply-decl-panel">
                            <p style={{fontSize:'14px',fontWeight:700,color:'#196638',margin:'0 0 10px 0'}}>Declarations &amp; Code of Conduct</p>
                            <div className="apply-decl-grid">
                              {[
                                { field: 'decl_boundaries', label: 'I will maintain professional boundaries with all students.' },
                                { field: 'decl_privacy', label: 'I will protect student privacy and confidentiality.' },
                                { field: 'decl_policies', label: "I will follow the academy's teaching and conduct policies." },
                                { field: 'decl_accuracy', label: 'I confirm that all submitted information is accurate.' }
                              ].map(item => (
                                <div key={item.field} className="apply-decl-item">
                                  <input type="checkbox" name={item.field} id={item.field} className="apply-checkbox-input" checked={formData[item.field]} onChange={handleInputChange} style={{marginTop:'2px'}} />
                                  <label htmlFor={item.field} className="apply-checkbox-label" style={{fontWeight:500, fontSize:'13.5px', lineHeight:1.45, cursor:'pointer'}}>
                                    {item.label} <span style={{color:'#dc2626', fontWeight:700}}>*</span>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                          {formErrors.declarations && <span className="apply-error-text" style={{ display: 'block', marginTop: '8px' }}>{formErrors.declarations}</span>}

                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', gap: '10px', flexWrap: 'wrap' }}>
                            <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleSectionContinue('verification')}>
                              Save &amp; Continue
                            </button>
                            <button type="submit" className="apply-btn apply-btn-primary" disabled={isSubmitting}>
                              {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Bottom note */}
                  <div style={{gridColumn:'span 12',background:'#f4f7f5',border:'1px solid #e2e8e5',borderRadius:'8px',padding:'12px 16px',fontSize:'13px',color:'#52605b',textAlign:'center',lineHeight:1.5}}>
                    <strong style={{color:'#172b26'}}>Note:</strong> All file uploads (images, videos, PDFs) must be shared via Google Drive.<br/>
                    After admin approval, you will be able to complete your profile and upload media directly.
                  </div>

                  {/* Actions Row at bottom of form */}
                  <div className="apply-actions-bar">
                    <button type="button" className="apply-btn apply-btn-draft" onClick={handleSaveDraft} disabled={isSavingDraft}>
                      {isSavingDraft ? 'Saving...' : 'Save Draft'}
                    </button>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {getSectionNum(activeSection) > 1 && (
                        <button type="button" className="apply-btn apply-btn-secondary" onClick={() => handleMenuClick(getSectionNum(activeSection) - 1)}>
                          Previous
                        </button>
                      )}
                      {getSectionNum(activeSection) < 12 ? (
                        <button type="button" className="apply-btn apply-btn-primary" onClick={() => handleSectionContinue(activeSection)}>
                          Save &amp; Continue
                        </button>
                      ) : (
                        <button type="submit" className="apply-btn apply-btn-primary" disabled={isSubmitting}>
                          {isSubmitting ? 'Submitting...' : 'Submit Application'}
                        </button>
                      )}
                    </div>
                  </div>

                  </div> {/* end apply-sections-grid */}

                </form>
              </div>{/* end apply-form-column */}
              </TeacherApplyErrorBoundary>

            </div>{/* end apply-app-body */}

          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  );
};

export default TeacherApply;
